import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  createEventOrder,
  deleteEventOrder,
  fetchAdditionalServices,
  fetchEventVenues,
  fetchEventOrders,
  updateEventOrder,
} from '../../store/slices/eventOrderSlice';
import { fetchDishes } from '../../store/slices/dishSlice';
import { EventCommercialProposal, EventCommercialProposalDetail, EventCommercialProposalLine, EventCommercialProposalStatus, EventOrder, EventOrderProfitability, EventProductionPlan, EventReadiness, EventReceivable, PrintSettings } from '../../types';
import { hasWorkerAccess } from '../../constants/management';
import { formatDishName } from '../../utils/dishNameFormat';
import { forecastPortions } from '../../utils/portionForecast';
import { formatProfitabilityMargin, formatProfitabilityMoney, getProfitabilityBasisLabel, getProfitabilityStatusLabel } from '../../utils/eventOrderProfitability';
import { paginate } from '../../utils/paginate';
import EventStaffingPanel from '../../components/EventStaffingPanel';
import SendPdfToTelegramButton from '../../components/SendPdfToTelegramButton';
import { rebuildWriteOffsWithProgress, WriteOffRebuildProgress } from '../../utils/writeOffRebuildProgress';
import ItemQuantityField from '../../components/ItemQuantityField';
import { openAndPrintHtml } from '../../utils/printDocument';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';
import { hasProductionRoutingEditPermission } from '../../utils/productionRouting';
import { eventOrderProtectedEditNotice } from '../../utils/productionRouting';
import { extractApiErrorMessage } from '../../utils/apiError';
import ProductionRoutingEditor, { ProductionRouting } from './ProductionRoutingEditor';
import { useSelectedOperatingGroup } from '../../hooks/useSelectedOperatingGroup';
import { getHistoricalWriteOffUnavailableNotice } from '../../utils/operatingContext';

type NamedOption = { id: number | string; name: string };
type DishOption = NamedOption & { output: number; price: number; category: string; servingsCount?: number | null; rawName: string };
type AdditionalServiceOption = NamedOption & { price: number; costPrice: number };
type EventOrderSort =
  | 'date-asc' | 'date-desc'
  | 'customer-asc' | 'customer-desc'
  | 'venue-asc' | 'venue-desc'
  | 'event-asc' | 'event-desc'
  | 'status-asc' | 'status-desc'
  | 'guests-asc' | 'guests-desc'
  | 'updated-asc' | 'updated-desc';
type DishRow = {
  key: number;
  rowKind: 'dish' | 'separator';
  separatorLabel: string;
  dishId: string;
  dishOutput: string;
  portions: string;
  unitPrice: string;
  servingTiming: string;
  feastTableNumber: string;
  isMixedServe: boolean;
  mixedGroup: string;
  dishNotes: string;
};
type ChildRow = {
  key: number;
  rowKind: 'dish' | 'separator';
  separatorLabel: string;
  dishId: string;
  dishOutput: string;
  portions: string;
  unitPrice: string;
  servingTiming: string;
  feastTableNumber: string;
  isMixedServe: boolean;
  mixedGroup: string;
  dishNotes: string;
};
type ContractorRow = {
  key: number;
  rowKind: 'dish' | 'separator';
  separatorLabel: string;
  dishId: string;
  dishOutput: string;
  portions: string;
  unitPrice: string;
  servingTiming: string;
  feastTableNumber: string;
  isMixedServe: boolean;
  mixedGroup: string;
  dishNotes: string;
};
type AdditionalServiceRow = {
  key: number;
  additionalServiceId: string;
  quantity: string;
  unitPrice: string;
  unitCost: string;
  serviceNotes: string;
};
type CustomSectionRow = {
  key: number;
  dishId: string;
  dishOutput: string;
  portions: string;
  unitPrice: string;
  servingTiming: string;
  feastTableNumber: string;
  isMixedServe: boolean;
  mixedGroup: string;
  dishNotes: string;
};
type CustomSection = {
  key: number;
  name: string;
  participantCount: string;
  serveTiming: string;
  rows: CustomSectionRow[];
};
type PreparationSemiItem = {
  name: string;
  qtyPerPortion: number;
};
type PreparationDishRow = {
  key: string;
  day: string;
  section: string;
  category: string;
  dishId: number;
  dishName: string;
  menuPortions: number;
  adjustedPortions: number;
  semiItems: PreparationSemiItem[];
};
type EventOrderPrintLayoutSettings = {
  indexColWidth: number;
  portionsColWidth: number;
  priceColWidth: number;
  amountColWidth: number;
  outputColWidth: number;
  totalOutputColWidth: number;
  timingColWidth: number;
  feastColWidth: number;
  mixColWidth: number;
  notesColWidth: number;
  cellFontSize: number;
  headerFontSize: number;
  cellPaddingY: number;
  cellPaddingX: number;
};
type EventOrderPrintLayoutDraft = Record<keyof EventOrderPrintLayoutSettings, string>;
type EventOrderStatusOption = {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
};
const ORDERS_PER_PAGE = 10;

type FormState = {
  eventDate: string;
  orderStatusId: string;
  meetingTime: string;
  ceremonyTime: string;
  feastTime: string;
  customerName: string;
  customerContacts: string;
  guestCount: string;
  childCount: string;
  contractorCount: string;
  childServeTiming: string;
  contractorServeTiming: string;
  menuCompiledBy: string;
  servicePercent: string;
  childServicePercent: string;
  contractorServicePercent: string;
  discountPercent: string;
  advanceAmount: string;
  serviceLaborCost: string;
  otherCost: string;
  costDataConfirmed: boolean;
  additionalEventInfo: string;
  notes: string;
  eventVenue: string;
  eventName: string;
  sourceWarehouseId: string;
};

type EventOrderExcelPreviewRow = {
  rowNumber: number;
  sourceDishName: string;
  b52Code: string;
  matchedDishId: number | null;
  matchedDishName: string;
  matchConfidence: number;
  matchMethod: string;
  portions: number;
  unitPrice: number;
  dishOutput: number;
  servingTiming: string;
  feastTableNumber: string;
  dishNotes: string;
  updateB52Price: boolean;
};

type EventOrderExcelPreview = {
  eventDate: string;
  eventName: string;
  customerName: string;
  customerContacts: string;
  eventVenue: string;
  guestCount: number;
  childCount: number;
  contractorCount: number;
  meetingTime: string;
  ceremonyTime: string;
  feastTime: string;
  servicePercent: number;
  discountPercent: number;
  serviceLaborCost: number;
  otherCost: number;
  costDataConfirmed: boolean;
  notes: string;
  rows: EventOrderExcelPreviewRow[];
  children: EventOrderExcelPreviewRow[];
  contractors: EventOrderExcelPreviewRow[];
  additionalServices: EventOrderExcelServicePreviewRow[];
};

type EventOrderExcelServicePreviewRow = {
  rowNumber: number;
  sourceServiceName: string;
  matchedAdditionalServiceId: number | null;
  matchedAdditionalServiceName: string;
  matchConfidence: number;
  matchMethod: string;
  quantity: number;
  unitPrice: number;
  unitCost: number | null;
};

const excelImportThStyle: React.CSSProperties = {
  padding: '8px',
  textAlign: 'left',
  background: 'var(--crm-surface-alt)',
  color: 'var(--crm-text)',
  borderBottom: '2px solid var(--crm-border)',
  whiteSpace: 'nowrap',
};
const excelImportTdStyle: React.CSSProperties = {
  padding: '6px 8px',
  color: 'var(--crm-text)',
  borderBottom: '1px solid var(--crm-table-row-border)',
  verticalAlign: 'top',
};
const modalCloseButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  borderRadius: 'var(--crm-radius-sm)',
  width: 44,
  height: 44,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  lineHeight: 1,
};

const DEFAULT_ORDER_STATUS_ID = 1;
const fallbackEventOrderStatuses: EventOrderStatusOption[] = [
  { id: 1, code: 'preliminary', name: 'Попереднє', sortOrder: 1, isActive: true },
  { id: 2, code: 'confirmed', name: 'Підтверджене', sortOrder: 2, isActive: true },
  { id: 3, code: 'completed', name: 'Завершене', sortOrder: 3, isActive: true },
  { id: 4, code: 'cancelled', name: 'Відмінене', sortOrder: 4, isActive: true },
];
const EVENT_ORDER_PRINT_LAYOUT_STORAGE_KEY = 'crm4chef.eventorders.printLayout.v1';
const EVENT_ORDER_PRINT_SECTIONS_STORAGE_KEY = 'crm4chef.eventorders.printSplitSections.v1';
const defaultEventOrderPrintLayout: EventOrderPrintLayoutSettings = {
  indexColWidth: 4,
  portionsColWidth: 8,
  priceColWidth: 9,
  amountColWidth: 10,
  outputColWidth: 9,
  totalOutputColWidth: 9,
  timingColWidth: 12,
  feastColWidth: 8,
  mixColWidth: 10,
  notesColWidth: 14,
  cellFontSize: 12,
  headerFontSize: 11,
  cellPaddingY: 6,
  cellPaddingX: 8,
};
const toEventOrderPrintLayoutDraft = (layout: EventOrderPrintLayoutSettings): EventOrderPrintLayoutDraft => ({
  indexColWidth: String(layout.indexColWidth),
  portionsColWidth: String(layout.portionsColWidth),
  priceColWidth: String(layout.priceColWidth),
  amountColWidth: String(layout.amountColWidth),
  outputColWidth: String(layout.outputColWidth),
  totalOutputColWidth: String(layout.totalOutputColWidth),
  timingColWidth: String(layout.timingColWidth),
  feastColWidth: String(layout.feastColWidth),
  mixColWidth: String(layout.mixColWidth),
  notesColWidth: String(layout.notesColWidth),
  cellFontSize: String(layout.cellFontSize),
  headerFontSize: String(layout.headerFontSize),
  cellPaddingY: String(layout.cellPaddingY),
  cellPaddingX: String(layout.cellPaddingX),
});
const MENU_SEPARATORS_OPEN = '[[MENU_SEPARATORS::';
const MENU_SEPARATORS_CLOSE = '::MENU_SEPARATORS]]';
const ORDER_FINANCIALS_OPEN = '[[ORDER_FINANCIALS::';
const ORDER_FINANCIALS_CLOSE = '::ORDER_FINANCIALS]]';
const ORDER_SECTION_META_OPEN = '[[ORDER_SECTION_META::';
const ORDER_SECTION_META_CLOSE = '::ORDER_SECTION_META]]';
type MenuSeparatorMeta = { index: number; label: string };
type MenuSeparatorState = {
  main: MenuSeparatorMeta[];
  children: MenuSeparatorMeta[];
  contractors: MenuSeparatorMeta[];
};
type OrderFinancialState = {
  mainServicePercent: number;
  childServicePercent: number;
  contractorServicePercent: number;
  advanceAmount: number;
};
type OrderSectionMetaState = {
  childServeTiming: string;
  contractorServeTiming: string;
};
type OrderFinancialSummary = {
  subtotal: number;
  mainServiceBaseSubtotal: number;
  mainServiceAmount: number;
  childServiceAmount: number;
  contractorServiceAmount: number;
  serviceAmount: number;
  effectiveServicePercent: number;
  discountAmount: number;
  total: number;
  balanceDue: number;
};

type ChildOrContractorRow = {
  rowKind: 'dish' | 'separator';
  separatorLabel: string;
};

const emptyMenuSeparatorState = (): MenuSeparatorState => ({
  main: [],
  children: [],
  contractors: [],
});

const toNonNegativeNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseOrderStatusId = (value: unknown, fallback = DEFAULT_ORDER_STATUS_ID): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const parseDecimalNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) return 0;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeLegacyDishOutput = (unitOutput: number, fallbackOutput: number): number => {
  if (unitOutput <= 0) return fallbackOutput > 0 ? fallbackOutput : 0;
  // Legacy orders might contain grams while dishes now store kilograms.
  if (fallbackOutput > 0 && fallbackOutput < 10 && unitOutput >= 10 && unitOutput / fallbackOutput >= 100) {
    return unitOutput / 1000;
  }
  return unitOutput;
};

const resolveOrderDishOutput = (
  storedDishOutput: unknown,
  storedTotalOutput: unknown,
  portions: unknown,
  fallbackOutput: unknown
): number => {
  const safePortions = parseDecimalNumber(portions);
  const unitFromStored = parseDecimalNumber(storedDishOutput);
  const unitFromTotal = safePortions > 0 ? parseDecimalNumber(storedTotalOutput) / safePortions : 0;
  const nextUnitOutput = unitFromStored > 0 ? unitFromStored : unitFromTotal;
  return normalizeLegacyDishOutput(nextUnitOutput, parseDecimalNumber(fallbackOutput));
};

const emptyOrderFinancialState = (baseServicePercent = 10): OrderFinancialState => ({
  mainServicePercent: toNonNegativeNumber(baseServicePercent, 10),
  childServicePercent: toNonNegativeNumber(baseServicePercent, 10),
  contractorServicePercent: toNonNegativeNumber(baseServicePercent, 10),
  advanceAmount: 0,
});
const emptyOrderSectionMetaState = (): OrderSectionMetaState => ({
  childServeTiming: '',
  contractorServeTiming: '',
});

const normalizeOrderFinancialState = (value: unknown, baseServicePercent = 10): OrderFinancialState => {
  const defaults = emptyOrderFinancialState(baseServicePercent);
  if (!value || typeof value !== 'object') return defaults;
  const financials = value as Partial<Record<keyof OrderFinancialState, unknown>>;
  return {
    mainServicePercent: toNonNegativeNumber(financials.mainServicePercent, defaults.mainServicePercent),
    childServicePercent: toNonNegativeNumber(financials.childServicePercent, defaults.childServicePercent),
    contractorServicePercent: toNonNegativeNumber(financials.contractorServicePercent, defaults.contractorServicePercent),
    advanceAmount: toNonNegativeNumber(financials.advanceAmount, 0),
  };
};

const normalizeOrderSectionMetaState = (value: unknown): OrderSectionMetaState => {
  const defaults = emptyOrderSectionMetaState();
  if (!value || typeof value !== 'object') return defaults;
  const meta = value as Partial<Record<keyof OrderSectionMetaState, unknown>>;
  return {
    childServeTiming: typeof meta.childServeTiming === 'string' ? meta.childServeTiming.trim() : '',
    contractorServeTiming: typeof meta.contractorServeTiming === 'string' ? meta.contractorServeTiming.trim() : '',
  };
};

const extractJsonMarker = <T,>(
  raw: string,
  open: string,
  close: string,
  fallback: T,
  normalize: (parsed: unknown) => T
): { cleanText: string; value: T } => {
  const start = raw.lastIndexOf(open);
  if (start < 0) return { cleanText: raw, value: fallback };
  const end = raw.indexOf(close, start + open.length);
  if (end < 0) return { cleanText: raw, value: fallback };

  const cleanText = `${raw.slice(0, start)}${raw.slice(end + close.length)}`.trim();
  const encoded = raw.slice(start + open.length, end).trim();
  try {
    return { cleanText, value: normalize(JSON.parse(encoded)) };
  } catch {
    return { cleanText, value: fallback };
  }
};

const normalizeMenuSeparatorList = (value: unknown): MenuSeparatorMeta[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => ({
      index: Number((item as { index?: unknown })?.index),
      label: typeof (item as { label?: unknown })?.label === 'string' ? (item as { label?: string }).label ?? '' : '',
    }))
    .filter((item) => Number.isFinite(item.index) && item.index >= 0)
    .map((item) => ({ index: Math.floor(item.index), label: item.label }));
};

const parseOrderNotesMetadata = (
  notes?: string | null,
  baseServicePercent = 10
): { cleanNotes: string; separators: MenuSeparatorState; financials: OrderFinancialState; sectionMeta: OrderSectionMetaState } => {
  const raw = notes ?? '';
  const parsedSeparators = extractJsonMarker(
    raw,
    MENU_SEPARATORS_OPEN,
    MENU_SEPARATORS_CLOSE,
    emptyMenuSeparatorState(),
    (parsed) => {
      const separators = emptyMenuSeparatorState();
      if (Array.isArray(parsed)) {
        separators.main = normalizeMenuSeparatorList(parsed);
      } else if (parsed && typeof parsed === 'object') {
        separators.main = normalizeMenuSeparatorList((parsed as { main?: unknown }).main);
        separators.children = normalizeMenuSeparatorList((parsed as { children?: unknown }).children);
        separators.contractors = normalizeMenuSeparatorList((parsed as { contractors?: unknown }).contractors);
      }
      return separators;
    }
  );
  const parsedFinancials = extractJsonMarker(
    parsedSeparators.cleanText,
    ORDER_FINANCIALS_OPEN,
    ORDER_FINANCIALS_CLOSE,
    emptyOrderFinancialState(baseServicePercent),
    (parsed) => normalizeOrderFinancialState(parsed, baseServicePercent)
  );
  const parsedSectionMeta = extractJsonMarker(
    parsedFinancials.cleanText,
    ORDER_SECTION_META_OPEN,
    ORDER_SECTION_META_CLOSE,
    emptyOrderSectionMetaState(),
    normalizeOrderSectionMetaState
  );
  return {
    cleanNotes: parsedSectionMeta.cleanText,
    separators: parsedSeparators.value,
    financials: parsedFinancials.value,
    sectionMeta: parsedSectionMeta.value,
  };
};

const composeOrderNotesMetadata = (
  notes: string,
  separators: MenuSeparatorState,
  financials: OrderFinancialState,
  sectionMeta: OrderSectionMetaState
): string => {
  const cleanNotes = notes.trim();
  const normalized: MenuSeparatorState = {
    main: normalizeMenuSeparatorList(separators.main),
    children: normalizeMenuSeparatorList(separators.children),
    contractors: normalizeMenuSeparatorList(separators.contractors),
  };
  const markers: string[] = [];
  if (normalized.main.length > 0 || normalized.children.length > 0 || normalized.contractors.length > 0) {
    markers.push(`${MENU_SEPARATORS_OPEN}${JSON.stringify(normalized)}${MENU_SEPARATORS_CLOSE}`);
  }
  markers.push(
    `${ORDER_FINANCIALS_OPEN}${JSON.stringify({
      mainServicePercent: toNonNegativeNumber(financials.mainServicePercent, 10),
      childServicePercent: toNonNegativeNumber(financials.childServicePercent, 10),
      contractorServicePercent: toNonNegativeNumber(financials.contractorServicePercent, 10),
      advanceAmount: toNonNegativeNumber(financials.advanceAmount, 0),
    })}${ORDER_FINANCIALS_CLOSE}`
  );
  const normalizedSectionMeta = normalizeOrderSectionMetaState(sectionMeta);
  if (normalizedSectionMeta.childServeTiming || normalizedSectionMeta.contractorServeTiming) {
    markers.push(`${ORDER_SECTION_META_OPEN}${JSON.stringify(normalizedSectionMeta)}${ORDER_SECTION_META_CLOSE}`);
  }
  return [cleanNotes, ...markers].filter(Boolean).join('\n');
};

const calculateOrderFinancialSummary = ({
  dishesSubtotal,
  childrenSubtotal,
  contractorsSubtotal,
  customSectionsSubtotal,
  additionalServicesSubtotal,
  mainServicePercent,
  childServicePercent,
  contractorServicePercent,
  discountPercent,
  advanceAmount,
}: {
  dishesSubtotal: number;
  childrenSubtotal: number;
  contractorsSubtotal: number;
  customSectionsSubtotal: number;
  additionalServicesSubtotal: number;
  mainServicePercent: number;
  childServicePercent: number;
  contractorServicePercent: number;
  discountPercent: number;
  advanceAmount: number;
}): OrderFinancialSummary => {
  const subtotal = dishesSubtotal + childrenSubtotal + contractorsSubtotal + customSectionsSubtotal + additionalServicesSubtotal;
  const mainServiceBaseSubtotal = dishesSubtotal;
  const mainServiceAmount = mainServiceBaseSubtotal * (mainServicePercent / 100);
  const childServiceAmount = childrenSubtotal * (childServicePercent / 100);
  const contractorServiceAmount = contractorsSubtotal * (contractorServicePercent / 100);
  const serviceAmount = mainServiceAmount + childServiceAmount + contractorServiceAmount;
  const discountAmount = subtotal * (discountPercent / 100);
  const total = subtotal + serviceAmount - discountAmount;
  const effectiveServicePercent = subtotal > 0 ? (serviceAmount / subtotal) * 100 : 0;
  return {
    subtotal,
    mainServiceBaseSubtotal,
    mainServiceAmount,
    childServiceAmount,
    contractorServiceAmount,
    serviceAmount,
    effectiveServicePercent,
    discountAmount,
    total,
    balanceDue: total - advanceAmount,
  };
};

const extractSeparatorsFromRows = <T extends ChildOrContractorRow>(rows: T[]): MenuSeparatorMeta[] => {
  let contentIndex = 0;
  const separators: MenuSeparatorMeta[] = [];
  rows.forEach((row) => {
    if (row.rowKind === 'separator') {
      separators.push({ index: contentIndex, label: row.separatorLabel || '' });
    } else {
      contentIndex += 1;
    }
  });
  return separators;
};

const insertSeparatorsIntoRows = <TRow, TSeparator>(
  baseRows: TRow[],
  separators: MenuSeparatorMeta[],
  createSeparator: (label: string) => TSeparator
): Array<TRow | TSeparator> => {
  const rows: Array<TRow | TSeparator> = [];
  for (let i = 0; i <= baseRows.length; i += 1) {
    separators
      .filter((separator) => separator.index === i)
      .forEach((separator) => rows.push(createSeparator(separator.label || '')));
    if (i < baseRows.length) rows.push(baseRows[i]);
  }
  return rows;
};

const emptyDishRow = (key: number): DishRow => ({
  key,
  rowKind: 'dish',
  separatorLabel: '',
  dishId: '',
  dishOutput: '',
  portions: '',
  unitPrice: '',
  servingTiming: '',
  feastTableNumber: '',
  isMixedServe: false,
  mixedGroup: '',
  dishNotes: '',
});

const emptySeparatorRow = (key: number): DishRow => ({
  key,
  rowKind: 'separator',
  separatorLabel: '',
  dishId: '',
  dishOutput: '',
  portions: '',
  unitPrice: '',
  servingTiming: '',
  feastTableNumber: '',
  isMixedServe: false,
  mixedGroup: '',
  dishNotes: '',
});

const emptyChildRow = (key: number): ChildRow => ({
  key,
  rowKind: 'dish',
  separatorLabel: '',
  dishId: '',
  dishOutput: '',
  portions: '',
  unitPrice: '',
  servingTiming: '',
  feastTableNumber: '',
  isMixedServe: false,
  mixedGroup: '',
  dishNotes: '',
});

const emptyChildSeparatorRow = (key: number): ChildRow => ({
  ...emptyChildRow(key),
  rowKind: 'separator',
});

const emptyContractorRow = (key: number): ContractorRow => ({
  key,
  rowKind: 'dish',
  separatorLabel: '',
  dishId: '',
  dishOutput: '',
  portions: '',
  unitPrice: '',
  servingTiming: '',
  feastTableNumber: '',
  isMixedServe: false,
  mixedGroup: '',
  dishNotes: '',
});

const emptyContractorSeparatorRow = (key: number): ContractorRow => ({
  ...emptyContractorRow(key),
  rowKind: 'separator',
});

const emptyAdditionalServiceRow = (key: number): AdditionalServiceRow => ({
  key,
  additionalServiceId: '',
  quantity: '1',
  unitPrice: '',
  unitCost: '',
  serviceNotes: '',
});

const emptyCustomSectionRow = (key: number): CustomSectionRow => ({
  key,
  dishId: '',
  dishOutput: '',
  portions: '',
  unitPrice: '',
  servingTiming: '',
  feastTableNumber: '',
  isMixedServe: false,
  mixedGroup: '',
  dishNotes: '',
});

const SearchSelect: React.FC<{
  items: NamedOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
}> = ({ items, value, onChange, placeholder }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = items.find((i) => String(i.id) === value);

  const calcPos = () => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, width: r.width });
  };

  const handleOpen = () => {
    calcPos();
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 10);
  };
  const handleClose = () => {
    setOpen(false);
    setQuery('');
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const portal = document.getElementById('eo-search-portal');
      if (wrapRef.current && !wrapRef.current.contains(target) && portal && !portal.contains(target)) {
        handleClose();
      }
    };
    const onScrollOrResize = () => calcPos();
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const filtered = query.trim()
    ? items.filter((i) => i.name.toLowerCase().includes(query.toLowerCase()))
    : items;

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-sm)',
    padding: '5px 8px',
    width: '100%',
    boxSizing: 'border-box',
    fontSize: '13px',
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text)',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {selected && !open ? (
        <div
          style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'text', minHeight: '31px' }}
          onClick={handleOpen}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setQuery('');
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-text-muted)', fontSize: '16px', lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          style={inputStyle}
          value={query}
          placeholder={placeholder ?? '🔍 Пошук страви...'}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) handleOpen();
          }}
          onFocus={() => {
            if (!open) handleOpen();
          }}
        />
      )}
      {open &&
        createPortal(
          <div
            id="eo-search-portal"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 99999,
              background: 'var(--crm-surface)',
              border: '1px solid var(--crm-border)',
              borderRadius: '6px',
              boxShadow: 'var(--crm-shadow-sm)',
              maxHeight: '220px',
              overflowY: 'auto',
            }}
          >
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 12px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>Нічого не знайдено</div>
            ) : (
              filtered.map((i) => (
                <div
                  key={i.id}
                  onMouseDown={() => {
                    onChange(String(i.id));
                    handleClose();
                  }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--crm-table-row-border)', color: 'var(--crm-text)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--crm-surface-alt)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {i.name}
                </div>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

const EventOrdersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const operatingGroup = useSelectedOperatingGroup();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedEventOrderId = searchParams.get('eventOrderId');
  const handledEventOrderIdRef = useRef<string | null>(null);
  const { orders, loading, additionalServices, eventVenues } = useAppSelector((s) => s.eventOrder);
  const dishes = useAppSelector((s) => s.dish.items);
  const username = useAppSelector((s) => s.auth.username);
  const [printSettings, setPrintSettings] = useState<PrintSettings | null>(null);
  const userRoles = useAppSelector((s) => s.auth.roles);
  const isWorker = hasWorkerAccess(userRoles);

  const dishOptions: DishOption[] = dishes.map((d) => ({
    id: d.id,
    name: formatDishName({ name: d.name, output: d.cleanOutput, servingsCount: d.servingsCount }, printSettings),
    rawName: d.name,
    output: d.cleanOutput ?? 0,
    price: Math.round(d.finalPrice ?? 0),
    category: (d.categoryName || '').trim(),
    servingsCount: d.servingsCount ?? null,
  }));
  const itemQuantityDishOptions = dishOptions.map(({ id, name }) => ({ id: String(id), name }));
  const dishOptionMap = useMemo(() => new Map(dishOptions.map((dish) => [dish.id, dish])), [dishOptions]);
  const additionalServiceOptions: AdditionalServiceOption[] = additionalServices.map((s) => ({
    id: s.id,
    name: s.name,
    price: s.price ?? 0,
    costPrice: s.costPrice ?? 0,
  }));
  const itemQuantityServiceOptions = additionalServiceOptions.map(({ id, name }) => ({ id: String(id), name }));
  const eventVenueOptions: NamedOption[] = eventVenues.map((v) => ({
    id: v.id,
    name: v.name,
  }));

  const [modalOpen, setModalOpen] = useState(false);
  const [routingOrder, setRoutingOrder] = useState<EventOrder | null>(null);
  const [productionRouting, setProductionRouting] = useState<ProductionRouting | null>(null);
  const [productionKitchens, setProductionKitchens] = useState<Array<{ id: number; name: string }>>([]);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState('');
  const [canEditProductionRouting, setCanEditProductionRouting] = useState(false);
  const routingRequestSequence = useRef(0);
  useEffect(() => {
    let active = true;
    api.get<{ permissions: string[]; editPermissions: string[] }>('/users/me/access')
      .then(({ data }) => {
        if (active) setCanEditProductionRouting(hasProductionRoutingEditPermission(data.permissions, data.editPermissions));
      })
      .catch(() => { if (active) setCanEditProductionRouting(false); });
    return () => { active = false; };
  }, []);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [excelImportBusy, setExcelImportBusy] = useState(false);
  const [excelImportError, setExcelImportError] = useState('');
  const [excelPreview, setExcelPreview] = useState<EventOrderExcelPreview | null>(null);
  const [excelPreviews, setExcelPreviews] = useState<EventOrderExcelPreview[]>([]);
  const [excelPreviewIndex, setExcelPreviewIndex] = useState(0);
  const [excelImportProgress, setExcelImportProgress] = useState('');
  const excelFileInputRef = useRef<HTMLInputElement>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>({
    eventDate: new Date().toISOString().slice(0, 10),
    orderStatusId: String(DEFAULT_ORDER_STATUS_ID),
    meetingTime: '',
    ceremonyTime: '',
    feastTime: '',
    customerName: '',
    customerContacts: '',
    guestCount: '0',
    childCount: '0',
    contractorCount: '0',
    childServeTiming: '',
    contractorServeTiming: '',
    menuCompiledBy: '',
    servicePercent: '10',
    childServicePercent: '10',
    contractorServicePercent: '10',
    discountPercent: '0',
    advanceAmount: '0',
    serviceLaborCost: '0',
    otherCost: '0',
    costDataConfirmed: false,
    additionalEventInfo: '',
    notes: '',
    eventVenue: '',
    eventName: '',
    sourceWarehouseId: '',
  });
  const [statusOptions, setStatusOptions] = useState<EventOrderStatusOption[]>(fallbackEventOrderStatuses);
  const statusOptionsById = useMemo(
    () => new Map(statusOptions.map((status) => [status.id, status])),
    [statusOptions]
  );
  const getStatusOption = useCallback(
    (statusId?: number | null) => statusOptionsById.get(parseOrderStatusId(statusId)) ?? fallbackEventOrderStatuses[0],
    [statusOptionsById]
  );
  const getStatusLabel = useCallback(
    (statusId?: number | null, fallbackName?: string | null) => (fallbackName || '').trim() || getStatusOption(statusId)?.name || fallbackEventOrderStatuses[0].name,
    [getStatusOption]
  );
  const getStatusCode = useCallback(
    (statusId?: number | null, fallbackCode?: string | null) => (fallbackCode || '').trim() || getStatusOption(statusId)?.code || fallbackEventOrderStatuses[0].code,
    [getStatusOption]
  );
  const eventVenueSearchOptions = useMemo(() => {
    const options = eventVenueOptions.map((venue) => ({ id: venue.name, name: venue.name }));
    if (form.eventVenue && !options.some((option) => option.id === form.eventVenue)) {
      options.unshift({ id: form.eventVenue, name: form.eventVenue });
    }
    return options;
  }, [eventVenueOptions, form.eventVenue]);
  const [rows, setRows] = useState<DishRow[]>([emptyDishRow(0)]);
  const [childRows, setChildRows] = useState<ChildRow[]>([]);
  const [contractorRows, setContractorRows] = useState<ContractorRow[]>([]);
  const [customSections, setCustomSections] = useState<CustomSection[]>([]);
  const [additionalServiceRows, setAdditionalServiceRows] = useState<AdditionalServiceRow[]>([]);
  const [selectedPrintOrderIds, setSelectedPrintOrderIds] = useState<number[]>([]);
  const [showChildren, setShowChildren] = useState(false);
  const [showContractors, setShowContractors] = useState(false);
  const [showAdditionalServices, setShowAdditionalServices] = useState(false);
  const [dishExtras, setDishExtras] = useState<Set<string>>(new Set());
  const [childExtras, setChildExtras] = useState<Set<string>>(new Set());
  const [contractorExtras, setContractorExtras] = useState<Set<string>>(new Set());
  const [serviceExtras, setServiceExtras] = useState<Set<string>>(new Set());
  const [dragMainRowKey, setDragMainRowKey] = useState<number | null>(null);
  const [dragChildRowKey, setDragChildRowKey] = useState<number | null>(null);
  const [dragContractorRowKey, setDragContractorRowKey] = useState<number | null>(null);
  const [dragCustomRow, setDragCustomRow] = useState<{ sectionKey: number; rowKey: number } | null>(null);
  const [printColumns, setPrintColumns] = useState({ output: false, totalOutput: false });
  const [printSplitSections, setPrintSplitSections] = useState(false);
  const [printLayout, setPrintLayout] = useState<EventOrderPrintLayoutSettings>({ ...defaultEventOrderPrintLayout });
  const [printLayoutDraft, setPrintLayoutDraft] = useState<EventOrderPrintLayoutDraft>(toEventOrderPrintLayoutDraft(defaultEventOrderPrintLayout));
  const togglePrintCol = (key: keyof typeof printColumns) => setPrintColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const setPrintLayoutDraftValue = (key: keyof EventOrderPrintLayoutSettings, value: string) => {
    setPrintLayoutDraft((prev) => ({ ...prev, [key]: value }));
  };
  const savePrintLayoutPreset = () => {
    const limits: Record<keyof EventOrderPrintLayoutSettings, [number, number]> = {
      indexColWidth: [2, 12],
      portionsColWidth: [4, 24],
      priceColWidth: [4, 24],
      amountColWidth: [5, 28],
      outputColWidth: [4, 24],
      totalOutputColWidth: [4, 24],
      timingColWidth: [6, 30],
      feastColWidth: [4, 24],
      mixColWidth: [4, 24],
      notesColWidth: [6, 35],
      cellFontSize: [9, 15],
      headerFontSize: [9, 14],
      cellPaddingY: [3, 16],
      cellPaddingX: [3, 18],
    };

    const next: EventOrderPrintLayoutSettings = { ...printLayout };
    (Object.keys(limits) as Array<keyof EventOrderPrintLayoutSettings>).forEach((key) => {
      const [min, max] = limits[key];
      const parsed = parseFloat((printLayoutDraft[key] || '').replace(',', '.'));
      if (!Number.isFinite(parsed)) return;
      next[key] = Math.min(max, Math.max(min, parsed));
    });

    setPrintLayout(next);
    setPrintLayoutDraft(toEventOrderPrintLayoutDraft(next));
    try {
      localStorage.setItem(EVENT_ORDER_PRINT_LAYOUT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };
  const toggleDishExtra = (key: string) =>
    setDishExtras((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleChildExtra = (key: string) =>
    setChildExtras((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleContractorExtra = (key: string) =>
    setContractorExtras((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleServiceExtra = (key: string) =>
    setServiceExtras((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const [rowKey, setRowKey] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listMessage, setListMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingStatusId, setEditingStatusId] = useState<number | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusProcessingIds, setStatusProcessingIds] = useState<Set<number>>(new Set());
  const [recalculatingDishPrices, setRecalculatingDishPrices] = useState(false);
  const [dishPriceRecalcProgress, setDishPriceRecalcProgress] = useState<{
    completed: number;
    total: number;
    updatedOrders: number;
    updatedRows: number;
    failedOrderIds: number[];
  } | null>(null);
  const [rebuildFromDate, setRebuildFromDate] = useState('');
  const [rebuildToDate, setRebuildToDate] = useState('');
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [rebuildMessage, setRebuildMessage] = useState('');
  const [rebuildProgress, setRebuildProgress] = useState<WriteOffRebuildProgress | null>(null);
  const rebuildRunning = useRef(false);
  const rebuildAbort = useRef<AbortController | null>(null);
  const bulkRebuildAbort = useRef<AbortController | null>(null);
  const isOperatingGroupCurrent = operatingGroup.isCurrent;
  const historicalRebuildUnavailableNotice = getHistoricalWriteOffUnavailableNotice(
    operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready);
  const canRunHistoricalRebuild = historicalRebuildUnavailableNotice === null;
  useEffect(() => {
    if (bulkRebuildAbort.current && !isOperatingGroupCurrent(null))
      bulkRebuildAbort.current.abort();
  }, [operatingGroup.groupId, operatingGroup.changedInAnotherTab, isOperatingGroupCurrent]);
  useEffect(() => () => {
    rebuildAbort.current?.abort();
    bulkRebuildAbort.current?.abort();
  }, []);
  const [writeOffIssues, setWriteOffIssues] = useState<Array<{ id: number; eventDate: string; eventName: string; customerName: string; updatedDate: string }>>([]);
  const [prepModalOpen, setPrepModalOpen] = useState(false);
  const [prepOrder] = useState<EventOrder | null>(null);
  const [viewOrder, setViewOrder] = useState<EventOrder | null>(null);
  const [commercialProposals, setCommercialProposals] = useState<EventCommercialProposal[]>([]);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalSaving, setProposalSaving] = useState(false);
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposalValidUntil, setProposalValidUntil] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toISOString().slice(0, 10);
  });
  const [proposalNotes, setProposalNotes] = useState('');
  const [proposalDetail, setProposalDetail] = useState<EventCommercialProposalDetail | null>(null);
  const [proposalDetailLoading, setProposalDetailLoading] = useState(false);
  const [receivable, setReceivable] = useState<EventReceivable | null>(null);
  const [receivableLoading, setReceivableLoading] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Банківський переказ');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [profitabilityByOrderId, setProfitabilityByOrderId] = useState<Record<number, EventOrderProfitability>>({});
  const [profitabilityLoadingId, setProfitabilityLoadingId] = useState<number | null>(null);
  const [profitabilityError, setProfitabilityError] = useState('');
  const [eventReadiness, setEventReadiness] = useState<EventReadiness | null>(null);
  const [eventReadinessLoading, setEventReadinessLoading] = useState(false);
  const [eventReadinessError, setEventReadinessError] = useState('');
  const [eventProductionPlan, setEventProductionPlan] = useState<EventProductionPlan | null>(null);
  const [eventProductionPlanLoading, setEventProductionPlanLoading] = useState(false);
  const [expandedPlanSections, setExpandedPlanSections] = useState<{ plan: boolean; semi: boolean; raw: boolean }>({ plan: false, semi: false, raw: false });

  const closeViewOrder = () => {
    setViewOrder(null);
    if (searchParams.has('eventOrderId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('eventOrderId');
      setSearchParams(next, { replace: true });
    }
  };

  const loadCommercialProposals = useCallback(async (eventOrderId: number) => {
    setProposalLoading(true);
    setProposalMessage('');
    try {
      const response = await api.get<EventCommercialProposal[]>(`/eventorders/${eventOrderId}/commercial-proposals`);
      setCommercialProposals(response.data);
    } catch {
      setCommercialProposals([]);
      setProposalMessage('Не вдалося завантажити комерційні пропозиції.');
    } finally {
      setProposalLoading(false);
    }
  }, []);

  const loadReceivable = useCallback(async (eventOrderId: number) => {
    setReceivableLoading(true);
    setPaymentMessage('');
    try {
      const response = await api.get<EventReceivable>(`/eventorders/${eventOrderId}/receivable`);
      setReceivable(response.data);
    } catch {
      setReceivable(null);
      setPaymentMessage('Не вдалося завантажити розрахунки з клієнтом.');
    } finally {
      setReceivableLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewOrder) {
      loadCommercialProposals(viewOrder.id);
      loadReceivable(viewOrder.id);
    } else {
      setCommercialProposals([]);
      setProposalMessage('');
      setReceivable(null);
      setPaymentMessage('');
    }
  }, [viewOrder, loadCommercialProposals, loadReceivable]);

  const createCommercialProposal = async () => {
    if (!viewOrder || !proposalValidUntil) return;
    setProposalSaving(true);
    setProposalMessage('');
    try {
      await api.post(`/eventorders/${viewOrder.id}/commercial-proposals`, {
        validUntil: proposalValidUntil,
        notes: proposalNotes,
      });
      setProposalNotes('');
      setProposalMessage('Нову версію комерційної пропозиції створено.');
      await loadCommercialProposals(viewOrder.id);
    } catch (requestError: any) {
      setProposalMessage(requestError?.response?.data?.message || 'Не вдалося створити комерційну пропозицію.');
    } finally {
      setProposalSaving(false);
    }
  };

  const transitionCommercialProposal = async (
    proposal: EventCommercialProposal,
    status: EventCommercialProposalStatus,
  ) => {
    if (!viewOrder) return;
    let decisionReason: string | null = null;
    if (status === 'Rejected') {
      decisionReason = window.prompt('Причина відхилення комерційної пропозиції:')?.trim() || null;
      if (!decisionReason) return;
    }
    if (status === 'Accepted' && !window.confirm(`Підтвердити прийняття ${proposal.proposalNumber}? Після цього версія стане незмінною.`)) return;
    setProposalSaving(true);
    setProposalMessage('');
    try {
      await api.post(`/eventorders/${viewOrder.id}/commercial-proposals/${proposal.id}/transition`, {
        status,
        version: proposal.version,
        decisionReason,
      });
      setProposalMessage('Статус комерційної пропозиції оновлено.');
      await loadCommercialProposals(viewOrder.id);
    } catch (requestError: any) {
      setProposalMessage(requestError?.response?.data?.message || 'Не вдалося змінити статус пропозиції.');
    } finally {
      setProposalSaving(false);
    }
  };

  const openCommercialProposal = async (proposal: EventCommercialProposal) => {
    setProposalDetailLoading(true);
    setProposalMessage('');
    try {
      const response = await api.get<EventCommercialProposalDetail>(
        `/eventorders/${proposal.eventOrderId}/commercial-proposals/${proposal.id}`,
      );
      setProposalDetail(response.data);
    } catch (requestError: any) {
      setProposalMessage(requestError?.response?.data?.detail || 'Не вдалося відкрити збережену версію пропозиції.');
    } finally {
      setProposalDetailLoading(false);
    }
  };

  const addCustomerPayment = async () => {
    if (!viewOrder || !receivable) return;
    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentMessage('Вкажіть коректну суму платежу.');
      return;
    }
    setPaymentSaving(true);
    setPaymentMessage('');
    try {
      const idempotencyKey = typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      await api.post(`/eventorders/${viewOrder.id}/receivable/payments`, {
        paymentDate,
        amount,
        method: paymentMethod,
        reference: paymentReference,
        notes: paymentNotes,
        idempotencyKey,
        version: receivable.version,
      });
      setPaymentAmount('');
      setPaymentReference('');
      setPaymentNotes('');
      setPaymentMessage('Платіж зафіксовано.');
      await loadReceivable(viewOrder.id);
    } catch (requestError: any) {
      setPaymentMessage(requestError?.response?.data?.message || 'Не вдалося зафіксувати платіж.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const reverseCustomerPayment = async (paymentId: number) => {
    if (!viewOrder) return;
    const reason = window.prompt('Причина сторнування платежу:')?.trim();
    if (!reason) return;
    setPaymentSaving(true);
    setPaymentMessage('');
    try {
      await api.post(`/eventorders/${viewOrder.id}/receivable/payments/${paymentId}/reverse`, { reason, version: receivable?.version ?? 0 });
      setPaymentMessage('Платіж сторновано; запис залишено в аудиті.');
      await loadReceivable(viewOrder.id);
    } catch (requestError: any) {
      setPaymentMessage(requestError?.response?.data?.message || 'Не вдалося сторнувати платіж.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const escapeProposalHtml = (value: unknown) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const buildProposalLinesHtml = (title: string, lines: EventCommercialProposalLine[]) => {
    if (lines.length === 0) return '';
    return `
      <h3>${escapeProposalHtml(title)}</h3>
      <table>
        <thead><tr><th>Найменування</th><th>Порції</th><th>Ціна</th><th>Сума</th></tr></thead>
        <tbody>${lines.map(line => `
          <tr>
            <td>${escapeProposalHtml(line.name)}</td>
            <td class="number">${line.portions.toFixed(2)}</td>
            <td class="number">${line.unitPrice.toFixed(2)}</td>
            <td class="number">${line.totalPrice.toFixed(2)}</td>
          </tr>`).join('')}</tbody>
      </table>`;
  };

  const buildCommercialProposalPrintHtml = (detail: EventCommercialProposalDetail) => {
    const { proposal, snapshot } = detail;
    const statusLabel = { Draft: 'Чернетка', Sent: 'Надіслано', Accepted: 'Прийнято', Rejected: 'Відхилено', Superseded: 'Замінено' }[proposal.status];
    const customSections = snapshot.customSections.map(section =>
      buildProposalLinesHtml(
        `${section.name} (${section.participantCount} ос.)`,
        section.dishes,
      )).join('');
    const services = snapshot.additionalServices.length === 0 ? '' : `
      <h3>Додаткові послуги</h3>
      <table>
        <thead><tr><th>Послуга</th><th>Кількість</th><th>Ціна</th><th>Сума</th></tr></thead>
        <tbody>${snapshot.additionalServices.map(line => `
          <tr>
            <td>${escapeProposalHtml(line.name)}</td>
            <td class="number">${line.quantity.toFixed(2)}</td>
            <td class="number">${line.unitPrice.toFixed(2)}</td>
            <td class="number">${line.totalPrice.toFixed(2)}</td>
          </tr>`).join('')}</tbody>
      </table>`;

    return `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>${escapeProposalHtml(proposal.proposalNumber)}</title>
      <style>
        body{font-family:Arial,sans-serif;color:#1f2937;margin:28px;font-size:13px}
        h1{font-size:22px;margin:0 0 5px}h2{font-size:15px;margin:0 0 18px;color:#4b5563}
        h3{font-size:14px;margin:18px 0 6px;border-bottom:1px solid #d1d5db;padding-bottom:4px}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:5px 24px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;margin-bottom:10px}th,td{border:1px solid #d1d5db;padding:6px 8px}
        th{background:#f3f4f6;text-align:left}.number{text-align:right;white-space:nowrap}
        .totals{margin:18px 0 0 auto;width:320px}.totals div{display:flex;justify-content:space-between;padding:4px 0}
        .grand{font-size:17px;font-weight:700;border-top:2px solid #374151;margin-top:4px;padding-top:8px!important}
        .note{margin-top:18px;padding:10px;background:#f9fafb;border:1px solid #d1d5db}
        @media print{body{margin:12mm}}
      </style></head><body>
      <h1>Комерційна пропозиція ${escapeProposalHtml(proposal.proposalNumber)}</h1>
      <h2>${escapeProposalHtml(snapshot.eventName || 'Подія')}</h2>
      <div class="meta">
        <div><strong>Замовник:</strong> ${escapeProposalHtml(snapshot.customerName || '—')}</div>
        <div><strong>Контакти:</strong> ${escapeProposalHtml(snapshot.customerContacts || '—')}</div>
        <div><strong>Дата події:</strong> ${new Date(snapshot.eventDate).toLocaleDateString('uk-UA')}</div>
        <div><strong>Місце:</strong> ${escapeProposalHtml(snapshot.eventVenue || '—')}</div>
        <div><strong>Учасники:</strong> ${snapshot.guestCount} / ${snapshot.childCount} / ${snapshot.contractorCount}</div>
        <div><strong>Дійсна до:</strong> ${new Date(proposal.validUntil).toLocaleDateString('uk-UA')}</div>
        <div><strong>Статус:</strong> ${escapeProposalHtml(statusLabel)}</div>
        <div><strong>Створено:</strong> ${new Date(proposal.createdAtUtc).toLocaleString('uk-UA')}</div>
      </div>
      ${buildProposalLinesHtml('Основне меню', snapshot.dishes)}
      ${buildProposalLinesHtml('Дитяче меню', snapshot.children)}
      ${buildProposalLinesHtml('Меню підрядників', snapshot.contractors)}
      ${customSections}
      ${services}
      <div class="totals">
        <div><span>Проміжна сума</span><strong>${snapshot.subtotal.toFixed(2)} грн</strong></div>
        <div><span>Обслуговування (${snapshot.servicePercent.toFixed(1)}%)</span><strong>${snapshot.serviceAmount.toFixed(2)} грн</strong></div>
        <div><span>Знижка (${snapshot.discountPercent.toFixed(1)}%)</span><strong>-${snapshot.discountAmount.toFixed(2)} грн</strong></div>
        <div class="grand"><span>Разом</span><span>${snapshot.totalPrice.toFixed(2)} грн</span></div>
      </div>
      ${proposal.notes ? `<div class="note"><strong>Примітка:</strong><br/>${escapeProposalHtml(proposal.notes)}</div>` : ''}
      </body></html>`;
  };

  useEffect(() => {
    if (!requestedEventOrderId) {
      handledEventOrderIdRef.current = null;
      return;
    }
    if (handledEventOrderIdRef.current === requestedEventOrderId) return;
    handledEventOrderIdRef.current = requestedEventOrderId;

    const id = Number(requestedEventOrderId);
    if (!Number.isInteger(id) || id <= 0) {
      setListMessage({ type: 'error', text: 'Некоректний ідентифікатор замовлення в посиланні.' });
      return;
    }

    api.get<EventOrder>(`/eventorders/${id}`)
      .then(response => setViewOrder(response.data))
      .catch(() => setListMessage({ type: 'error', text: 'Не вдалося відкрити замовлення з посилання.' }));
  }, [requestedEventOrderId]);
  const writeOffErrorSummary = useMemo(() => {
    if (!viewOrder?.lastWriteOffError) return null;
    const text = viewOrder.lastWriteOffError;
    const sectionMatch = text.match(/розділі\s+([^,]+)/i);
    const dishMatch = text.match(/страва\s+(.+?)(?:\s+\(#\d+\))?(?:[,.]|$)/i);
    const ingredientMatch = text.match(/інгредієнт\s+([^,.]+)/i);
    return {
      section: sectionMatch?.[1]?.trim() || null,
      dish: dishMatch?.[1]?.trim() || null,
      ingredient: ingredientMatch?.[1]?.trim() || null,
    };
  }, [viewOrder]);
  const [dishPreviewModalOpen, setDishPreviewModalOpen] = useState(false);
  const [dishPreview, setDishPreview] = useState<any | null>(null);
  const [dishPreviewLoading, setDishPreviewLoading] = useState(false);
  const [dishPreviewError, setDishPreviewError] = useState<string | null>(null);
  const [listSortBy, setListSortBy] = useState<EventOrderSort>('date-desc');
  const [statusFilter, setStatusFilter] = useState('active');
  const [listPage, setListPage] = useState(1);
  const [customerFilter, setCustomerFilter] = useState('');
  const [venueFilter, setVenueFilter] = useState('');
  const [prepRows, setPrepRows] = useState<PreparationDishRow[]>([]);
  const [prepCategoryFilter, setPrepCategoryFilter] = useState('');
  const [prepDishFilter, setPrepDishFilter] = useState('');
  const [prepSortBy, setPrepSortBy] = useState<'day-asc' | 'day-desc' | 'category-asc' | 'category-desc' | 'dish-asc' | 'dish-desc' | 'portions-asc' | 'portions-desc'>('day-asc');
  const modalSnapshotRef = useRef<string>('');

  useEffect(() => {
    dispatch(fetchDishes());
    dispatch(fetchAdditionalServices());
    dispatch(fetchEventVenues());
  }, [dispatch]);

  const refreshOrders = useCallback(() =>
    dispatch(fetchEventOrders({
      excludeCompleted: statusFilter === 'active',
      statusId: statusFilter && statusFilter !== 'active' ? Number(statusFilter) : undefined,
    })), [dispatch, statusFilter]);

  useEffect(() => {
    refreshOrders().unwrap().catch((error) => {
      console.error('EventOrdersPage.fetchEventOrders failed', error);
      const responseData = (error as { response?: { data?: unknown } }).response?.data;
      if (responseData !== undefined) {
        console.error('EventOrdersPage.fetchEventOrders response body', responseData);
      }
    });
  }, [refreshOrders]);

  useEffect(() => {
    let cancelled = false;
    api.get<PrintSettings>('/permissions/print-settings')
      .then((response) => { if (!cancelled) setPrintSettings(response.data); })
      .catch(() => { if (!cancelled) setPrintSettings(null); });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadStatuses = async () => {
      try {
        const response = await api.get<EventOrderStatusOption[]>('/eventorders/statuses');
        if (cancelled || !Array.isArray(response.data) || response.data.length === 0) return;
        setStatusOptions([...response.data].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id));
      } catch {
        // keep fallback statuses
      }
    };

    loadStatuses();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadWriteOffIssues = async () => {
      try {
        const response = await api.get('/eventorders/write-off-issues');
        if (!cancelled && Array.isArray(response.data)) {
          setWriteOffIssues(response.data);
        }
      } catch {
        if (!cancelled) setWriteOffIssues([]);
      }
    };
    loadWriteOffIssues();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!viewOrder) {
      setProfitabilityError('');
      setProfitabilityLoadingId(null);
      return;
    }

    let cancelled = false;
    setProfitabilityLoadingId(viewOrder.id);
    setProfitabilityError('');

    api.get<EventOrderProfitability>(`/eventorders/${viewOrder.id}/profitability`)
      .then((response) => {
        if (cancelled) return;
        setProfitabilityByOrderId((current) => ({ ...current, [viewOrder.id]: response.data }));
      })
      .catch(() => {
        if (!cancelled) setProfitabilityError('Не вдалося завантажити P&L по замовленню.');
      })
      .finally(() => {
        if (!cancelled) setProfitabilityLoadingId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [viewOrder]);

  useEffect(() => {
    setExpandedPlanSections({ plan: false, semi: false, raw: false });
  }, [viewOrder?.id]);

  useEffect(() => {
    if (!viewOrder) {
      setEventReadiness(null);
      setEventReadinessError('');
      setEventReadinessLoading(false);
      setEventProductionPlan(null);
      setEventProductionPlanLoading(false);
      return;
    }

    let cancelled = false;
    setEventReadinessLoading(true);
    setEventReadinessError('');
    api.get<EventReadiness>(`/eventorders/${viewOrder.id}/readiness`)
      .then((response) => {
        if (!cancelled) setEventReadiness(response.data);
      })
      .catch(() => {
        if (!cancelled) {
          setEventReadiness(null);
          setEventReadinessError('Не вдалося завантажити паспорт готовності події.');
        }
      })
      .finally(() => {
        if (!cancelled) setEventReadinessLoading(false);
      });
    setEventProductionPlanLoading(true);
    api.get<EventProductionPlan>(`/eventorders/${viewOrder.id}/production-plan`)
      .then((response) => {
        if (!cancelled) setEventProductionPlan(response.data);
      })
      .catch(() => {
        if (!cancelled) setEventProductionPlan(null);
      })
      .finally(() => {
        if (!cancelled) setEventProductionPlanLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [viewOrder]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(EVENT_ORDER_PRINT_LAYOUT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<EventOrderPrintLayoutSettings>;
      const keys = Object.keys(defaultEventOrderPrintLayout) as Array<keyof EventOrderPrintLayoutSettings>;
      const widthKeys: Array<keyof EventOrderPrintLayoutSettings> = [
        'indexColWidth', 'portionsColWidth', 'priceColWidth', 'amountColWidth',
        'outputColWidth', 'totalOutputColWidth', 'timingColWidth', 'feastColWidth', 'mixColWidth', 'notesColWidth',
      ];
      const restored: EventOrderPrintLayoutSettings = { ...defaultEventOrderPrintLayout };
      keys.forEach((key) => {
        const value = parsed[key];
        if (typeof value !== 'number' || !Number.isFinite(value)) return;
        // Backward compatibility: old saved values were in px; convert to %.
        if (widthKeys.includes(key) && value > 50) {
          restored[key] = value / 10;
          return;
        }
        restored[key] = value;
      });
      setPrintLayout(restored);
      setPrintLayoutDraft(toEventOrderPrintLayoutDraft(restored));
    } catch {
      // ignore invalid localStorage payload
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(EVENT_ORDER_PRINT_SECTIONS_STORAGE_KEY);
      if (raw == null) return;
      setPrintSplitSections(raw === '1');
    } catch {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(EVENT_ORDER_PRINT_SECTIONS_STORAGE_KEY, printSplitSections ? '1' : '0');
    } catch {
      // ignore storage errors
    }
  }, [printSplitSections]);

  const prepCategoryOptions = useMemo(
    () => Array.from(new Set(prepRows.map((r) => r.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'uk')),
    [prepRows]
  );
  const prepDishOptions = useMemo(
    () => Array.from(new Set(prepRows.map((r) => r.dishName).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'uk')),
    [prepRows]
  );
  const prepCategorySearchItems = useMemo(
    () => prepCategoryOptions.map((category) => ({ id: category, name: category })),
    [prepCategoryOptions]
  );
  const prepDishSearchItems = useMemo(
    () => prepDishOptions.map((dishName) => ({ id: dishName, name: dishName })),
    [prepDishOptions]
  );
  const prepFilteredRows = useMemo(() => {
    const filtered = prepRows.filter((row) => {
      if (prepCategoryFilter && row.category !== prepCategoryFilter) return false;
      if (prepDishFilter && row.dishName !== prepDishFilter) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      switch (prepSortBy) {
        case 'day-desc': return b.day.localeCompare(a.day, 'uk');
        case 'category-asc': return a.category.localeCompare(b.category, 'uk');
        case 'category-desc': return b.category.localeCompare(a.category, 'uk');
        case 'dish-asc': return a.dishName.localeCompare(b.dishName, 'uk');
        case 'dish-desc': return b.dishName.localeCompare(a.dishName, 'uk');
        case 'portions-asc': return a.adjustedPortions - b.adjustedPortions;
        case 'portions-desc': return b.adjustedPortions - a.adjustedPortions;
        case 'day-asc':
        default: return a.day.localeCompare(b.day, 'uk');
      }
    });
  }, [prepRows, prepCategoryFilter, prepDishFilter, prepSortBy]);

  const buildModalSnapshot = useCallback(
    () =>
      JSON.stringify({
        form,
        rows,
        childRows,
        contractorRows,
        customSections,
        additionalServiceRows,
        showChildren,
        showContractors,
        showAdditionalServices,
        dishExtras: Array.from(dishExtras).sort(),
        childExtras: Array.from(childExtras).sort(),
        contractorExtras: Array.from(contractorExtras).sort(),
        serviceExtras: Array.from(serviceExtras).sort(),
      }),
    [
      form,
      rows,
      childRows,
      contractorRows,
      customSections,
      additionalServiceRows,
      showChildren,
      showContractors,
      showAdditionalServices,
      dishExtras,
      childExtras,
      contractorExtras,
      serviceExtras,
    ]
  );

  useEffect(() => {
    if (!modalOpen) {
      modalSnapshotRef.current = '';
      return;
    }
    if (!modalSnapshotRef.current) {
      modalSnapshotRef.current = buildModalSnapshot();
    }
  }, [modalOpen, buildModalSnapshot]);

  const hasModalUnsavedChanges = modalOpen &&
    modalSnapshotRef.current.length > 0 &&
    modalSnapshotRef.current !== buildModalSnapshot();

  const nextKey = () => {
    const k = rowKey + 1;
    setRowKey(k);
    return k;
  };

  const openNew = () => {
    setEditingId(null);
    setForm({
      eventDate: new Date().toISOString().slice(0, 10),
      orderStatusId: String(DEFAULT_ORDER_STATUS_ID),
      meetingTime: '',
      ceremonyTime: '',
      feastTime: '',
      customerName: '',
      customerContacts: '',
      guestCount: '0',
      childCount: '0',
      contractorCount: '0',
      childServeTiming: '',
      contractorServeTiming: '',
      menuCompiledBy: username || '',
      servicePercent: '10',
      childServicePercent: '10',
      contractorServicePercent: '10',
      discountPercent: '0',
      advanceAmount: '0',
      serviceLaborCost: '0',
      otherCost: '0',
      costDataConfirmed: false,
      additionalEventInfo: '',
      notes: '',
      eventVenue: '',
      eventName: '',
      sourceWarehouseId: '',
    });
    setRows([emptyDishRow(0)]);
    setChildRows([]);
    setContractorRows([]);
    setCustomSections([]);
    setAdditionalServiceRows([]);
    setShowChildren(false);
    setShowContractors(false);
    setShowAdditionalServices(false);
    setDishExtras(new Set());
    setChildExtras(new Set());
    setContractorExtras(new Set());
    setServiceExtras(new Set());
    setRowKey(1);
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (order: EventOrder) => {
    const parsedOrderNotes = parseOrderNotesMetadata(order.notes ?? '', order.servicePercent ?? 10);
    const separatorState = parsedOrderNotes.separators;
    setEditingId(order.id);
    setForm({
      eventDate: order.eventDate ? order.eventDate.slice(0, 10) : '',
      orderStatusId: String(parseOrderStatusId(order.orderStatusId)),
      meetingTime: order.meetingTime ?? '',
      ceremonyTime: order.ceremonyTime ?? '',
      feastTime: order.feastTime ?? '',
      customerName: order.customerName ?? '',
      customerContacts: order.customerContacts ?? '',
      guestCount: String(order.guestCount ?? 0),
      childCount: String(order.childCount ?? 0),
      contractorCount: String(order.contractorCount ?? 0),
      childServeTiming: (order as any).childServeTiming ?? '',
      contractorServeTiming: (order as any).contractorServeTiming ?? '',
      menuCompiledBy: order.menuCompiledBy || username || '',
      servicePercent: String(parsedOrderNotes.financials.mainServicePercent),
      childServicePercent: String(parsedOrderNotes.financials.childServicePercent),
      contractorServicePercent: String(parsedOrderNotes.financials.contractorServicePercent),
      discountPercent: String(order.discountPercent ?? 0),
      advanceAmount: String(parsedOrderNotes.financials.advanceAmount ?? 0),
      serviceLaborCost: String(order.serviceLaborCost ?? 0),
      otherCost: String(order.otherCost ?? 0),
      costDataConfirmed: Boolean(order.costDataConfirmed),
      additionalEventInfo: order.additionalEventInfo ?? '',
      notes: parsedOrderNotes.cleanNotes,
      eventVenue: order.eventVenue ?? '',
      eventName: order.eventName ?? '',
      sourceWarehouseId: order.sourceWarehouseId ? String(order.sourceWarehouseId) : '',
    });
    let k = 0;
    const baseRows: DishRow[] = order.dishes.length > 0
      ? order.dishes.map((d) => {
          const dish = dishOptionMap.get(d.dishId);
          const portions = d.portions ?? 0;
          const normalizedDishOutput = resolveOrderDishOutput(d.dishOutput, d.output, portions, dish?.output ?? 0);
          return {
            key: k++,
            rowKind: 'dish',
            separatorLabel: '',
            dishId: String(d.dishId),
            dishOutput: String(normalizedDishOutput),
            portions: String(portions),
            unitPrice: String(d.unitPrice ?? dish?.price ?? 0),
            servingTiming: d.servingTiming ?? '',
            feastTableNumber: d.feastTableNumber ?? '',
            isMixedServe: Boolean(d.isMixedServe),
            mixedGroup: d.mixedGroup ?? '',
            dishNotes: (d as any).dishNotes ?? '',
          };
        })
      : [];
    const separatorMap = new Map<number, string[]>();
    separatorState.main.forEach((separator) => {
      const bucket = separatorMap.get(separator.index) ?? [];
      bucket.push(separator.label || '');
      separatorMap.set(separator.index, bucket);
    });
    const rowsWithSeparators: DishRow[] = [];
    for (let i = 0; i <= baseRows.length; i += 1) {
      const separators = separatorMap.get(i) ?? [];
      for (const label of separators) {
        rowsWithSeparators.push({ ...emptySeparatorRow(k++), separatorLabel: label });
      }
      if (i < baseRows.length) rowsWithSeparators.push(baseRows[i]);
    }
    setRows(rowsWithSeparators.length > 0 ? rowsWithSeparators : [emptyDishRow(k++)]);
    const orderChildren = order.children ?? [];
    const orderContractors = order.contractors ?? [];
    const orderCustomSections = order.customSections ?? [];
    const orderAdditionalServices = order.additionalServices ?? [];
    const baseChildRows: ChildRow[] = orderChildren.length > 0
      ? orderChildren.map((c) => {
          const dish = dishOptionMap.get(c.dishId);
          const normalizedDishOutput = resolveOrderDishOutput(c.dishOutput, c.output, c.portions ?? 0, dish?.output ?? 0);
          return {
            key: k++,
            rowKind: 'dish',
            separatorLabel: '',
            dishId: String(c.dishId),
            dishOutput: String(normalizedDishOutput),
            portions: String(c.portions ?? 0),
            unitPrice: String(c.unitPrice ?? 0),
            servingTiming: '',
            feastTableNumber: '',
            isMixedServe: false,
            mixedGroup: '',
            dishNotes: '',
          };
        })
      : [];
    setChildRows(
      insertSeparatorsIntoRows(baseChildRows, separatorState.children, (label) => ({
        ...emptyChildSeparatorRow(k++),
        separatorLabel: label,
      }))
    );
    const baseContractorRows: ContractorRow[] = orderContractors.length > 0
      ? orderContractors.map((c) => {
          const dish = dishOptionMap.get(c.dishId);
          const normalizedDishOutput = resolveOrderDishOutput(c.dishOutput, c.output, c.portions ?? 0, dish?.output ?? 0);
          return {
            key: k++,
            rowKind: 'dish',
            separatorLabel: '',
            dishId: String(c.dishId),
            dishOutput: String(normalizedDishOutput),
            portions: String(c.portions ?? 0),
            unitPrice: String(c.unitPrice ?? 0),
            servingTiming: '',
            feastTableNumber: '',
            isMixedServe: false,
            mixedGroup: '',
            dishNotes: '',
          };
        })
      : [];
    setContractorRows(
      insertSeparatorsIntoRows(baseContractorRows, separatorState.contractors, (label) => ({
        ...emptyContractorSeparatorRow(k++),
        separatorLabel: label,
      }))
    );
    setAdditionalServiceRows(
      orderAdditionalServices.length > 0
        ? orderAdditionalServices.map((s) => ({
            key: k++,
            additionalServiceId: String(s.additionalServiceId),
            quantity: String(s.quantity ?? 1),
            unitPrice: String(s.unitPrice ?? 0),
            unitCost: String(s.unitCost ?? 0),
            serviceNotes: '',
          }))
        : []
    );
    setCustomSections(
      orderCustomSections.map((section) => ({
        key: k++,
        name: section.name ?? '',
        participantCount: String(section.participantCount ?? 0),
        serveTiming: section.serveTiming ?? '',
        rows: (section.dishes ?? []).map((d) => {
          const dish = dishOptionMap.get(d.dishId);
          const normalizedDishOutput = resolveOrderDishOutput(d.dishOutput, d.output, d.portions ?? 0, dish?.output ?? 0);
          return {
            key: k++,
            dishId: String(d.dishId),
            dishOutput: String(normalizedDishOutput),
            portions: String(d.portions ?? 0),
            unitPrice: String(d.unitPrice ?? 0),
            servingTiming: d.servingTiming ?? '',
            feastTableNumber: d.feastTableNumber ?? '',
            isMixedServe: !!d.isMixedServe,
            mixedGroup: d.mixedGroup ?? '',
            dishNotes: d.dishNotes ?? '',
          };
        }),
      }))
    );
    setShowChildren(orderChildren.length > 0);
    setShowContractors(orderContractors.length > 0);
    setShowAdditionalServices(orderAdditionalServices.length > 0);
    setRowKey(k);
    setError(null);
    setModalOpen(true);
  };

  const closeModal = (force = false) => {
    if (!force && hasModalUnsavedChanges && !window.confirm('Є незбережені зміни. Закрити вікно без збереження?')) {
      return;
    }
    setModalOpen(false);
    setError(null);
  };

  const addRow = () => setRows((prev) => [...prev, emptyDishRow(nextKey())]);
  const insertRowAfter = (targetKey: number) =>
    setRows((prev) => {
      const insertIndex = prev.findIndex((r) => r.key === targetKey);
      const newRow = emptyDishRow(nextKey());
      if (insertIndex < 0) return [...prev, newRow];
      return [...prev.slice(0, insertIndex + 1), newRow, ...prev.slice(insertIndex + 1)];
    });
  const insertSeparatorAfter = (targetKey: number) =>
    setRows((prev) => {
      const insertIndex = prev.findIndex((r) => r.key === targetKey);
      const newSeparator = emptySeparatorRow(nextKey());
      if (insertIndex < 0) return [...prev, newSeparator];
      return [...prev.slice(0, insertIndex + 1), newSeparator, ...prev.slice(insertIndex + 1)];
    });
  const addSeparatorRow = () => setRows((prev) => [...prev, emptySeparatorRow(nextKey())]);
  const removeRow = (key: number) => setRows((prev) => {
    const target = prev.find((r) => r.key === key);
    if (target?.rowKind === 'dish' && target.dishId && !window.confirm('Видалити цю страву із замовлення?')) return prev;
    return prev.filter((r) => r.key !== key);
  });
  const updateRow = (key: number, patch: Partial<DishRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const reorderByKey = <T extends { key: number }>(items: T[], fromKey: number, toKey: number): T[] => {
    const fromIndex = items.findIndex((item) => item.key === fromKey);
    const toIndex = items.findIndex((item) => item.key === toKey);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return items;
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  };
  const moveMainRow = (fromKey: number, toKey: number) => {
    setRows((prev) => reorderByKey(prev, fromKey, toKey));
  };
  const moveChildRow = (fromKey: number, toKey: number) => {
    setChildRows((prev) => reorderByKey(prev, fromKey, toKey));
  };
  const moveContractorRow = (fromKey: number, toKey: number) => {
    setContractorRows((prev) => reorderByKey(prev, fromKey, toKey));
  };
  const moveCustomSectionRow = (sectionKey: number, fromKey: number, toKey: number) => {
    setCustomSections((prev) =>
      prev.map((section) =>
        section.key === sectionKey
          ? { ...section, rows: reorderByKey(section.rows, fromKey, toKey) }
          : section
      )
    );
  };
  const addChildRow = () => setChildRows((prev) => [...prev, emptyChildRow(nextKey())]);
  const insertChildRowAfter = (targetKey: number) =>
    setChildRows((prev) => {
      const insertIndex = prev.findIndex((r) => r.key === targetKey);
      const newRow = emptyChildRow(nextKey());
      if (insertIndex < 0) return [...prev, newRow];
      return [...prev.slice(0, insertIndex + 1), newRow, ...prev.slice(insertIndex + 1)];
    });
  const addChildSeparatorRow = () => setChildRows((prev) => [...prev, emptyChildSeparatorRow(nextKey())]);
  const insertChildSeparatorAfter = (targetKey: number) =>
    setChildRows((prev) => {
      const insertIndex = prev.findIndex((r) => r.key === targetKey);
      const newRow = emptyChildSeparatorRow(nextKey());
      if (insertIndex < 0) return [...prev, newRow];
      return [...prev.slice(0, insertIndex + 1), newRow, ...prev.slice(insertIndex + 1)];
    });
  const removeChildRow = (key: number) => setChildRows((prev) => {
    const target = prev.find((r) => r.key === key);
    if (target?.rowKind === 'dish' && target.dishId && !window.confirm('Видалити цю позицію з блоку "Діти"?')) return prev;
    return prev.filter((r) => r.key !== key);
  });
  const updateChildRow = (key: number, patch: Partial<ChildRow>) =>
    setChildRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addContractorRow = () => setContractorRows((prev) => [...prev, emptyContractorRow(nextKey())]);
  const insertContractorRowAfter = (targetKey: number) =>
    setContractorRows((prev) => {
      const insertIndex = prev.findIndex((r) => r.key === targetKey);
      const newRow = emptyContractorRow(nextKey());
      if (insertIndex < 0) return [...prev, newRow];
      return [...prev.slice(0, insertIndex + 1), newRow, ...prev.slice(insertIndex + 1)];
    });
  const addContractorSeparatorRow = () => setContractorRows((prev) => [...prev, emptyContractorSeparatorRow(nextKey())]);
  const insertContractorSeparatorAfter = (targetKey: number) =>
    setContractorRows((prev) => {
      const insertIndex = prev.findIndex((r) => r.key === targetKey);
      const newRow = emptyContractorSeparatorRow(nextKey());
      if (insertIndex < 0) return [...prev, newRow];
      return [...prev.slice(0, insertIndex + 1), newRow, ...prev.slice(insertIndex + 1)];
    });
  const removeContractorRow = (key: number) => setContractorRows((prev) => {
    const target = prev.find((r) => r.key === key);
    if (target?.rowKind === 'dish' && target.dishId && !window.confirm('Видалити цю позицію з блоку "Підрядники"?')) return prev;
    return prev.filter((r) => r.key !== key);
  });
  const updateContractorRow = (key: number, patch: Partial<ContractorRow>) =>
    setContractorRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addAdditionalServiceRow = () => setAdditionalServiceRows((prev) => [...prev, emptyAdditionalServiceRow(nextKey())]);
  const insertAdditionalServiceRowAfter = (targetKey: number) =>
    setAdditionalServiceRows((prev) => {
      const insertIndex = prev.findIndex((r) => r.key === targetKey);
      const newRow = emptyAdditionalServiceRow(nextKey());
      if (insertIndex < 0) return [...prev, newRow];
      return [...prev.slice(0, insertIndex + 1), newRow, ...prev.slice(insertIndex + 1)];
    });
  const removeAdditionalServiceRow = (key: number) => setAdditionalServiceRows((prev) => {
    const target = prev.find((r) => r.key === key);
    const hasData = !!target && (target.additionalServiceId.trim() || (parseFloat(target.quantity) || 0) > 0 || (parseFloat(target.unitPrice) || 0) > 0 || (parseFloat(target.unitCost) || 0) > 0);
    if (hasData && !window.confirm('Видалити цю додаткову послугу?')) return prev;
    return prev.filter((r) => r.key !== key);
  });
  const updateAdditionalServiceRow = (key: number, patch: Partial<AdditionalServiceRow>) =>
    setAdditionalServiceRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addCustomSection = () =>
    setCustomSections((prev) => {
      if (prev.length >= 5) return prev;
      return [
        ...prev,
        {
          key: nextKey(),
          name: '',
          participantCount: '0',
          serveTiming: '',
          rows: [emptyCustomSectionRow(nextKey())],
        },
      ];
    });
  const removeCustomSection = (key: number) => setCustomSections((prev) => {
    const target = prev.find((s) => s.key === key);
    const hasDishes = !!target && target.rows.some((r) => r.dishId.trim().length > 0);
    if (hasDishes && !window.confirm('Видалити вкладку разом з доданими стравами?')) return prev;
    return prev.filter((s) => s.key !== key);
  });
  const updateCustomSection = (key: number, patch: Partial<CustomSection>) =>
    setCustomSections((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const addCustomSectionRow = (sectionKey: number) =>
    setCustomSections((prev) =>
      prev.map((s) => (s.key === sectionKey ? { ...s, rows: [...s.rows, emptyCustomSectionRow(nextKey())] } : s))
    );
  const insertCustomSectionRowAfter = (sectionKey: number, targetKey: number) =>
    setCustomSections((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) return section;
        const insertIndex = section.rows.findIndex((row) => row.key === targetKey);
        const newRow = emptyCustomSectionRow(nextKey());
        if (insertIndex < 0) return { ...section, rows: [...section.rows, newRow] };
        return { ...section, rows: [...section.rows.slice(0, insertIndex + 1), newRow, ...section.rows.slice(insertIndex + 1)] };
      })
    );
  const removeCustomSectionRow = (sectionKey: number, rowKeyValue: number) =>
    setCustomSections((prev) =>
      prev.map((s) =>
        s.key === sectionKey
          ? (() => {
              const row = s.rows.find((r) => r.key === rowKeyValue);
              if (row?.dishId && !window.confirm('Видалити цю страву з додаткової вкладки?')) return s;
              return { ...s, rows: s.rows.filter((r) => r.key !== rowKeyValue) };
            })()
          : s
      )
    );
  const updateCustomSectionRow = (sectionKey: number, rowKeyValue: number, patch: Partial<CustomSectionRow>) =>
    setCustomSections((prev) =>
      prev.map((s) =>
        s.key === sectionKey
          ? { ...s, rows: s.rows.map((r) => (r.key === rowKeyValue ? { ...r, ...patch } : r)) }
          : s
      )
    );

  const getSuggestedPortions = (dish: DishOption | undefined, participantCountValue: string | number) => {
    return forecastPortions(participantCountValue, dish?.servingsCount);
  };

  const applyServingsCountToRows = <T extends { rowKind: 'dish' | 'separator'; key: number; dishId: string; portions: string }>(
    rowsState: T[],
    update: (key: number, patch: Partial<T>) => void,
    participantCountValue: string | number
  ) => {
    rowsState.forEach((row) => {
      if (row.rowKind !== 'dish' || !row.dishId) return;
      const dish = dishOptionMap.get(Number(row.dishId));
      const portions = getSuggestedPortions(dish, participantCountValue);
      if (portions !== null) {
        update(row.key, { portions } as Partial<T>);
      }
    });
  };

  const getDishSelectionPatch = (dishId: string, participantCountValue: string | number) => {
    const dish = dishOptionMap.get(Number(dishId));
    const patch: { dishId: string; dishOutput: string; unitPrice: string; portions?: string } = {
      dishId,
      dishOutput: dish ? String(dish.output) : '',
      unitPrice: dish ? String(dish.price) : '',
    };
    const portions = getSuggestedPortions(dish, participantCountValue);
    if (portions !== null) {
      patch.portions = portions;
    }
    return patch;
  };

  const formatVisibleDishName = (dishId: number | string | null | undefined, dishName: string) => {
    const normalizedId = Number(dishId);
    const dish = Number.isFinite(normalizedId) ? dishOptionMap.get(normalizedId) : undefined;
    return formatDishName(
      {
        name: dishName,
        output: dish?.output,
        servingsCount: dish?.servingsCount,
      },
      printSettings
    );
  };

  const openDishPreviewModal = async (dishId: number | string | null | undefined) => {
    const normalizedId = Number(dishId);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
    setDishPreviewModalOpen(true);
    setDishPreviewLoading(true);
    setDishPreviewError(null);
    try {
      const res = await api.get(`/dishes/${normalizedId}`);
      setDishPreview(res.data);
    } catch (err: any) {
      setDishPreview(null);
      setDishPreviewError(err?.response?.data?.message || 'Не вдалося завантажити калькуляцію страви.');
    } finally {
      setDishPreviewLoading(false);
    }
  };

  const closeDishPreviewModal = () => {
    setDishPreviewModalOpen(false);
    setDishPreview(null);
    setDishPreviewLoading(false);
    setDishPreviewError(null);
  };

  const applyServingsCountToCustomSection = (sectionKey: number, rowsState: CustomSectionRow[]) => {
    const participantCountValue = customSections.find((section) => section.key === sectionKey)?.participantCount ?? '0';
    rowsState.forEach((row) => {
      if (!row.dishId) return;
      const dish = dishOptionMap.get(Number(row.dishId));
      const portions = getSuggestedPortions(dish, participantCountValue);
      if (portions !== null) {
        updateCustomSectionRow(sectionKey, row.key, { portions });
      }
    });
  };

  const getRowOutput = (r: DishRow) => (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0);
  const getRowAmount = (r: DishRow) => (parseFloat(r.portions) || 0) * (parseFloat(r.unitPrice) || 0);
  const getMenuRowAmount = (quantity: string, unitPrice: string) => (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);
  const getMenuBlockRowOutput = (dishOutput: string, portions: string) => (parseFloat(dishOutput) || 0) * (parseFloat(portions) || 0);
  const getCustomRowOutput = (r: CustomSectionRow) => (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0);
  const getCustomRowAmount = (r: CustomSectionRow) => (parseFloat(r.portions) || 0) * (parseFloat(r.unitPrice) || 0);
  const mainMenuOutput = rows.reduce((sum, r) => sum + getRowOutput(r), 0);
  const childrenOutput = childRows.filter((r) => r.rowKind === 'dish').reduce((sum, r) => sum + getMenuBlockRowOutput(r.dishOutput, r.portions), 0);
  const contractorsOutput = contractorRows.filter((r) => r.rowKind === 'dish').reduce((sum, r) => sum + getMenuBlockRowOutput(r.dishOutput, r.portions), 0);
  const customSectionsOutput = customSections.reduce((sum, s) => sum + s.rows.reduce((inner, r) => inner + getCustomRowOutput(r), 0), 0);
  const dishesSubtotal = rows.reduce((sum, r) => sum + getRowAmount(r), 0);
  const childrenSubtotal = childRows.filter((r) => r.rowKind === 'dish').reduce((sum, r) => sum + getMenuRowAmount(r.portions, r.unitPrice), 0);
  const contractorsSubtotal = contractorRows.filter((r) => r.rowKind === 'dish').reduce((sum, r) => sum + getMenuRowAmount(r.portions, r.unitPrice), 0);
  const customSectionsSubtotal = customSections.reduce((sum, s) => sum + s.rows.reduce((inner, r) => inner + getCustomRowAmount(r), 0), 0);
  const additionalServicesSubtotal = additionalServiceRows.reduce((sum, r) => sum + ((parseFloat(r.quantity) || 0) * (parseFloat(r.unitPrice) || 0)), 0);
  const totalOutput = mainMenuOutput + childrenOutput + contractorsOutput + customSectionsOutput;
  const servicePercent = parseFloat(form.servicePercent) || 0;
  const childServicePercent = parseFloat(form.childServicePercent) || 0;
  const contractorServicePercent = parseFloat(form.contractorServicePercent) || 0;
  const discountPercent = parseFloat(form.discountPercent) || 0;
  const advanceAmount = parseFloat(form.advanceAmount) || 0;
  const financialSummary = calculateOrderFinancialSummary({
    dishesSubtotal,
    childrenSubtotal,
    contractorsSubtotal,
    customSectionsSubtotal,
    additionalServicesSubtotal,
    mainServicePercent: servicePercent,
    childServicePercent,
    contractorServicePercent,
    discountPercent,
    advanceAmount,
  });
  const subtotal = financialSummary.subtotal;
  const mainServiceAmount = financialSummary.mainServiceAmount;
  const childrenServiceAmount = financialSummary.childServiceAmount;
  const contractorsServiceAmount = financialSummary.contractorServiceAmount;
  const serviceAmount = financialSummary.serviceAmount;
  const effectiveServicePercent = financialSummary.effectiveServicePercent;
  const discountAmount = financialSummary.discountAmount;
  const total = financialSummary.total;
  const balanceDue = financialSummary.balanceDue;
  const guestCount = parseInt(form.guestCount, 10) || 0;
  const childCount = parseInt(form.childCount, 10) || 0;
  const contractorCount = parseInt(form.contractorCount, 10) || 0;
  const foodPerGuest = guestCount > 0 ? mainMenuOutput / guestCount : 0;
  const foodPerChild = childCount > 0 ? childrenOutput / childCount : 0;
  const foodPerContractor = contractorCount > 0 ? contractorsOutput / contractorCount : 0;
  // Ціна з людини: тільки страви основного меню + їх частина обслуговування, поділена на кількість гостей
  const dishesWithService = dishesSubtotal + dishesSubtotal * (servicePercent / 100);
  const perGuestPrice = guestCount > 0 ? dishesWithService / guestCount : 0;

  const openPrintWindow = (html: string) => {
    void openAndPrintHtml(html, {
      windowFeatures: 'width=980,height=780',
      minimumDelayMs: 500,
      onPopupBlocked: () => alert('Дозвольте спливаючі вікна для друку'),
    });
  };

  const getMetaString = (obj: unknown, key: string): string => {
    if (!obj || typeof obj !== 'object') return '';
    const value = (obj as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : '';
  };
  const getPrintableRowMeta = (row: unknown) => {
    if (!row || typeof row !== 'object') {
      return { isSeparator: false, servingTiming: '', feastTableNumber: '', mixedLabel: '', dishNotes: '' };
    }
    const record = row as Record<string, unknown>;
    return {
      isSeparator: record.isSeparator === true,
      servingTiming: typeof record.servingTiming === 'string' ? record.servingTiming : '',
      feastTableNumber: typeof record.feastTableNumber === 'string' ? record.feastTableNumber : '',
      mixedLabel: typeof record.mixedLabel === 'string' ? record.mixedLabel : '',
      dishNotes: typeof record.dishNotes === 'string' ? record.dishNotes : '',
    };
  };

  const buildPrintHtml = (
    title: string,
    meta: {
      eventDate: string;
      orderStatus: string;
      meetingTime: string;
      ceremonyTime: string;
      feastTime: string;
      customerName: string;
      customerContacts: string;
      guestCount: number;
      childCount: number;
      contractorCount: number;
      childServeTiming: string;
      contractorServeTiming: string;
      menuCompiledBy: string;
      additionalEventInfo: string;
      notes: string;
      eventVenue: string;
      eventName: string;
    },
    totals: {
      subtotal: number;
      dishesSubtotal: number;
      childrenSubtotal: number;
      contractorsSubtotal: number;
      customSectionsSubtotal: number;
      additionalServicesSubtotal: number;
      mainServicePercent: number;
      childServicePercent: number;
      contractorServicePercent: number;
      mainServiceAmount: number;
      childServiceAmount: number;
      contractorServiceAmount: number;
      serviceAmount: number;
      discountPercent: number;
      discountAmount: number;
      total: number;
      advanceAmount: number;
      balanceDue: number;
      mainMenuOutput: number;
      childrenOutput: number;
      contractorsOutput: number;
      totalOutput: number;
      foodPerGuest: number;
      foodPerChild: number;
      foodPerContractor: number;
      perGuestPrice: number;
    },
    rowData: Array<{ name: string; portions: number; unitPrice: number; amount: number; dishOutput: number; output: number; servingTiming: string; feastTableNumber: string; mixedLabel: string; dishNotes: string; isSeparator?: boolean; separatorLabel?: string }>,
    childrenData: Array<{ name: string; portions: number; unitPrice: number; amount: number; dishOutput: number; output: number; isSeparator?: boolean; separatorLabel?: string }>,
    contractorData: Array<{ name: string; portions: number; unitPrice: number; amount: number; dishOutput: number; output: number; isSeparator?: boolean; separatorLabel?: string }>,
    customSectionData: Array<{ name: string; participantCount: number; serveTiming: string; rows: Array<{ name: string; portions: number; unitPrice: number; amount: number; dishOutput: number; output: number }> }>,
    additionalServiceData: Array<{ name: string; quantity: number; unitPrice: number; amount: number }>,
    cols: { output: boolean; totalOutput: boolean; timing: boolean; feast: boolean; mix: boolean; notes: boolean },
    layout: EventOrderPrintLayoutSettings,
    splitSections: boolean
  ) => {
    const buildDishSection = (
      titleText: string,
      items: Array<{ name: string; portions: number; unitPrice: number; amount: number; dishOutput: number; output: number; servingTiming?: string; feastTableNumber?: string; mixedLabel?: string; dishNotes?: string; isSeparator?: boolean; separatorLabel?: string }>,
      cols: { output: boolean; totalOutput: boolean; timing: boolean; feast: boolean; mix: boolean; notes: boolean },
      sectionMetaLine = ''
    ) => {
      const colCount = 5 + Number(cols.output) + Number(cols.totalOutput) + Number(cols.timing) + Number(cols.feast) + Number(cols.mix) + Number(cols.notes);
      let dishIndex = 0;
      const rowsHtml = items
        .map((r) => {
          if (r.isSeparator) {
            const separatorLabel = (r.separatorLabel || '').trim();
            return `
      <tr style="border-bottom:2px solid #9aa7b4;">
        <td colspan="${colCount}" style="padding:${separatorLabel ? '8px 10px' : '14px 10px'};background:#f8fafb;color:#2c3e50;font-weight:${separatorLabel ? '700' : '400'};font-style:${separatorLabel ? 'normal' : 'italic'};text-align:center;border-left:1px solid #7f8c99;border-right:1px solid #7f8c99;border-top:1px solid #c9d2da;border-bottom:2px solid #9aa7b4;">${separatorLabel || '&nbsp;'}</td>
      </tr>`;
          }
          dishIndex += 1;
          return `
      <tr class="data-row" style="border-bottom:2px solid #8b98a5;">
        <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:center;border:1px solid #9aa7b4;">${dishIndex}</td>
        <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;font-weight:700;border:1px solid #9aa7b4;">${r.name}</td>
        <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;font-weight:700;border:1px solid #9aa7b4;">${r.portions.toFixed(2)}</td>
        <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;border:1px solid #9aa7b4;">${Math.round(r.unitPrice)}</td>
        <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;color:#1a7a3c;font-weight:700;border:1px solid #9aa7b4;">${r.amount.toFixed(2)}</td>
        ${cols.output ? `<td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;border:1px solid #9aa7b4;">${r.dishOutput.toFixed(3)}</td>` : ''}
        ${cols.totalOutput ? `<td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;font-weight:600;border:1px solid #9aa7b4;">${r.output.toFixed(3)}</td>` : ''}
        ${cols.timing ? `<td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;border:1px solid #9aa7b4;">${r.servingTiming ? `<strong>${r.servingTiming}</strong>` : '—'}</td>` : ''}
        ${cols.feast ? `<td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;border:1px solid #9aa7b4;">${r.feastTableNumber ? `<strong>${r.feastTableNumber}</strong>` : '—'}</td>` : ''}
        ${cols.mix ? `<td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;border:1px solid #9aa7b4;">${r.mixedLabel && r.mixedLabel !== 'Ні' ? `<strong>${r.mixedLabel}</strong>` : (r.mixedLabel || '—')}</td>` : ''}
        ${cols.notes ? `<td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;border:1px solid #9aa7b4;">${r.dishNotes ? `<strong>${r.dishNotes}</strong>` : '—'}</td>` : ''}
      </tr>`;
        })
        .join('');

      if (items.length === 0) {
        return '';
      }

      return `
      ${titleText ? `<div class="print-section-title">${titleText}</div>` : ''}
      ${sectionMetaLine ? `<div class="subsection-meta">${sectionMetaLine}</div>` : ''}
      <table>
        <thead>
          <tr>
            <th style="width:${layout.indexColWidth}%;">#</th><th>Страва</th>
            <th style="width:${layout.portionsColWidth}%;text-align:right;">Порції</th>
            <th style="width:${layout.priceColWidth}%;text-align:right;">Ціна</th>
            <th style="width:${layout.amountColWidth}%;text-align:right;">Сума</th>
            ${cols.output ? `<th style="width:${layout.outputColWidth}%;text-align:right;">Вихід страви</th>` : ''}
            ${cols.totalOutput ? `<th style="width:${layout.totalOutputColWidth}%;text-align:right;">Заг. вихід</th>` : ''}
            ${cols.timing ? `<th style="width:${layout.timingColWidth}%;">Таймінг</th>` : ''}
            ${cols.feast ? `<th style="width:${layout.feastColWidth}%;">№ застілля</th>` : ''}
            ${cols.mix ? `<th style="width:${layout.mixColWidth}%;">Мікс подачі</th>` : ''}
            ${cols.notes ? `<th style="width:${layout.notesColWidth}%;">Нотатки</th>` : ''}
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>`;
    };

    const buildCustomSections = (
      sections: Array<{ name: string; participantCount: number; serveTiming: string; rows: Array<{ name: string; portions: number; unitPrice: number; amount: number; dishOutput: number; output: number }> }>
    ) => {
      if (sections.length === 0) return '';
      return `
        ${sections
          .map((section) => {
            const sectionName = (section.name || '').trim() || 'Додаткова вкладка';
            const sectionMetaParts = [
              section.participantCount > 0 ? `<span>К-сть: <strong>${section.participantCount}</strong></span>` : '',
              section.serveTiming ? `<span>Таймінг: <strong>${section.serveTiming}</strong></span>` : '',
            ].filter(Boolean).join('');
            return `
            ${buildDishSection(sectionName, section.rows, cols, sectionMetaParts)}
          `;
          })
          .join('')}
      `;
    };

    const buildSimpleSection = (
      titleText: string,
      items: Array<{ name: string; quantity: number; unitPrice: number; amount: number }>
    ) =>
      items.length === 0
        ? ''
        : `
      <div class="print-section-title">${titleText}</div>
      <table>
        <thead>
          <tr>
            <th style="width:${layout.indexColWidth}%;">#</th><th>Назва</th>
            <th style="width:${layout.portionsColWidth}%;text-align:right;">К-сть</th>
            <th style="width:${layout.priceColWidth}%;text-align:right;">Ціна</th>
            <th style="width:${layout.amountColWidth}%;text-align:right;">Сума</th>
          </tr>
        </thead>
        <tbody>
          ${items
            .map(
              (r, i) => `
            <tr class="data-row" style="border-bottom:2px solid #8b98a5;">
              <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:center;border:1px solid #9aa7b4;">${i + 1}</td>
              <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;border:1px solid #9aa7b4;">${r.name}</td>
              <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;border:1px solid #9aa7b4;"><strong>${r.quantity.toFixed(2)}</strong></td>
              <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;border:1px solid #9aa7b4;">${r.unitPrice.toFixed(2)}</td>
              <td style="padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:right;color:#1a7a3c;font-weight:700;border:1px solid #9aa7b4;">${r.amount.toFixed(2)}</td>
            </tr>`
            )
            .join('')}
        </tbody>
      </table>`;

    return `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>${title}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'Segoe UI',Arial,sans-serif;font-size:${layout.cellFontSize}px;color:#1a1a1a;padding:16px 18px;background:#fff;}
        .header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #c0392b;padding-bottom:8px;margin-bottom:10px;gap:12px;}
        .header-title{font-size:18px;font-weight:700;color:#c0392b;letter-spacing:.2px;line-height:1.15;}
        .header-sub{font-size:11px;color:#666;margin-top:2px;}
        .status-badge{display:inline-block;padding:3px 10px;border-radius:16px;font-size:11px;font-weight:700;background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7;}
        .meta-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:5px 9px;background:#f8f9fa;border:1px solid #dee2e6;border-radius:8px;padding:8px 9px;margin-bottom:8px;}
        .meta-item{font-size:12px;color:#555;}
        .meta-item strong{color:#1a1a1a;display:block;font-size:12px;}
        .meta-item.highlight{background:#fff3cd;border-radius:4px;padding:4px 6px;}
        .time-strip{display:flex;justify-content:center;align-items:center;gap:6px;margin:0 0 8px 0;padding:7px 9px;background:#f3f8ff;border:1px solid #cfe3ff;border-radius:8px;white-space:nowrap;overflow:hidden;}
        .time-chip{font-size:11px;color:#2c3e50;background:#fff;border:1px solid #d6e6ff;border-radius:999px;padding:3px 9px;line-height:1.2;text-align:center;}
        .time-chip strong{font-size:12px;color:#0f4c81;}
        .print-section-title{display:block;width:100%;margin:12px 0 6px;padding:7px 10px;font-size:14px;font-weight:700;color:#fff;background-color:rgb(44, 62, 80);border:none;border-radius:0;print-color-adjust:exact;-webkit-print-color-adjust:exact;}
        .subsection-meta{display:flex;gap:12px;flex-wrap:wrap;align-items:center;font-size:12px;color:#4b5563;margin:8px 0 6px;padding:6px 8px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;}
        .subsection-meta strong{color:#1f2937;}
        table{width:100%;border-collapse:collapse;margin-bottom:10px;border:2px solid #7f8c99;}
        thead tr{background:#2c3e50;color:#fff;}
        th{padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;text-align:left;font-size:${layout.headerFontSize}px;font-weight:600;border:1px solid #6b7785;}
        tbody tr.data-row:nth-child(odd){background:#f8fbff;}
        tbody tr.data-row td:nth-child(2){font-weight:600;background:#f1f5f9;border-left:2px solid #93c5fd;}
        td{padding:${layout.cellPaddingY}px ${layout.cellPaddingX}px;font-size:${layout.cellFontSize}px;border:1px solid #9aa7b4;}
        .totals-box{background:#fef9e7;border:1px solid #f39c12;border-radius:8px;padding:14px;margin-top:14px;display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;}
        .totals-box .total-row{font-size:12px;color:#555;}
        .totals-box .total-row strong{color:#1a1a1a;}
        .total-final{grid-column:1/-1;border-top:2px solid #f39c12;margin-top:4px;padding-top:8px;font-size:16px;font-weight:700;color:#c0392b;text-align:right;}
        .per-guest{grid-column:1/-1;font-size:13px;color:#1e8449;font-weight:600;}
        .notes-section{margin-top:14px;font-size:12px;border:1px solid #dee2e6;border-radius:6px;padding:10px 14px;background:#f8f9fa;}
        .notes-section h3{font-size:13px;margin-bottom:4px;color:#2c3e50;}
        .print-page-break{break-before:page;page-break-before:always;}
        @media print{
          body{padding:12px 16px;print-color-adjust:exact;-webkit-print-color-adjust:exact;}
          *{print-color-adjust:exact;-webkit-print-color-adjust:exact;}
          .print-section-title{background-color:rgb(44, 62, 80) !important;color:#fff !important;}
          @page{margin:10mm;}
        }
      </style></head><body>
      <div class="header">
        <div>
          <div class="header-title">📋 ${title}</div>
          <div class="header-sub">${meta.eventName ? `<em>${meta.eventName}</em> • ` : ''}${meta.eventVenue ? `📍 ${meta.eventVenue}` : ''}</div>
        </div>
        <div style="text-align:right;">
          <span class="status-badge">${meta.orderStatus || '—'}</span>
          <div style="font-size:11px;color:#888;margin-top:6px;">Меню: ${meta.menuCompiledBy || '—'}</div>
        </div>
      </div>
      <div class="meta-grid">
        <div class="meta-item"><strong>${meta.eventDate ? new Date(meta.eventDate).toLocaleDateString('uk-UA') : '—'}</strong>Дата події</div>
        <div class="meta-item"><strong>${meta.eventName || '—'}</strong>Назва заходу</div>
        <div class="meta-item"><strong>${meta.eventVenue || '—'}</strong>Місце проведення</div>
        <div class="meta-item"><strong>${meta.customerContacts || '—'}</strong>Контакти</div>
        <div class="meta-item"><strong>${meta.customerName || '—'}</strong>Замовник</div>
        <div class="meta-item"><strong>${meta.guestCount || 0} / ${meta.childCount || 0} / ${meta.contractorCount || 0}</strong>Гості / Діти / Підрядники</div>
      </div>
      <div class="time-strip">
        <div class="time-chip">Зустріч: <strong>${meta.meetingTime || '—'}</strong></div>
        <div class="time-chip">Церемонія: <strong>${meta.ceremonyTime || '—'}</strong></div>
        <div class="time-chip">Застілля: <strong>${meta.feastTime || '—'}</strong></div>
      </div>
      ${[
        buildDishSection('Основне меню', rowData, cols),
        buildDishSection(
          'Діти',
          childrenData,
          cols,
          [
            meta.childCount > 0 ? `<span>К-сть: <strong>${meta.childCount}</strong></span>` : '',
            meta.childServeTiming ? `<span>Таймінг: <strong>${meta.childServeTiming}</strong></span>` : '',
          ].filter(Boolean).join('')
        ),
        buildDishSection(
          'Підрядники',
          contractorData,
          cols,
          [
            meta.contractorCount > 0 ? `<span>К-сть: <strong>${meta.contractorCount}</strong></span>` : '',
            meta.contractorServeTiming ? `<span>Таймінг: <strong>${meta.contractorServeTiming}</strong></span>` : '',
          ].filter(Boolean).join('')
        ),
        buildCustomSections(customSectionData),
        buildSimpleSection('Додаткові послуги', additionalServiceData),
      ]
        .filter(Boolean)
        .map((content, index) => `<section class="${splitSections && index > 0 ? 'print-page-break' : ''}">${content}</section>`)
        .join('')}
      <div class="totals-box">
        ${totals.dishesSubtotal > 0 ? `<div class="total-row">Страви основного меню: <strong>${totals.dishesSubtotal.toFixed(2)} грн</strong></div>` : ''}
        ${totals.childrenSubtotal > 0 ? `<div class="total-row">Діти: <strong>${totals.childrenSubtotal.toFixed(2)} грн</strong></div>` : ''}
        ${totals.contractorsSubtotal > 0 ? `<div class="total-row">Підрядники: <strong>${totals.contractorsSubtotal.toFixed(2)} грн</strong></div>` : ''}
        ${totals.customSectionsSubtotal > 0 ? `<div class="total-row">Додаткові секції: <strong>${totals.customSectionsSubtotal.toFixed(2)} грн</strong></div>` : ''}
        ${totals.additionalServicesSubtotal > 0 ? `<div class="total-row">Додаткові послуги: <strong>${totals.additionalServicesSubtotal.toFixed(2)} грн</strong></div>` : ''}
        <div class="total-row">Проміжна сума: <strong>${totals.subtotal.toFixed(2)} грн</strong></div>
        ${totals.mainServiceAmount > 0 ? `<div class="total-row">Обслуговування основного (${totals.mainServicePercent.toFixed(1)}%): <strong>+ ${totals.mainServiceAmount.toFixed(2)} грн</strong></div>` : ''}
        ${totals.childServiceAmount > 0 ? `<div class="total-row">Обслуговування дітей (${totals.childServicePercent.toFixed(1)}%): <strong>+ ${totals.childServiceAmount.toFixed(2)} грн</strong></div>` : ''}
        ${totals.contractorServiceAmount > 0 ? `<div class="total-row">Обслуговування підрядників (${totals.contractorServicePercent.toFixed(1)}%): <strong>+ ${totals.contractorServiceAmount.toFixed(2)} грн</strong></div>` : ''}
        ${totals.serviceAmount > 0 ? `<div class="total-row">Усього обслуговування: <strong>+ ${totals.serviceAmount.toFixed(2)} грн</strong></div>` : ''}
        ${totals.discountAmount > 0 ? `<div class="total-row">Знижка (${totals.discountPercent.toFixed(1)}%): <strong>- ${totals.discountAmount.toFixed(2)} грн</strong></div>` : ''}
        ${totals.advanceAmount > 0 ? `<div class="total-row">Аванс: <strong>- ${totals.advanceAmount.toFixed(2)} грн</strong></div>` : ''}
        <div class="total-row">Вихід основного меню: <strong>${totals.mainMenuOutput.toFixed(3)}</strong></div>
        <div class="total-row">Вихід дітей: <strong>${totals.childrenOutput.toFixed(3)}</strong></div>
        <div class="total-row">Вихід підрядників: <strong>${totals.contractorsOutput.toFixed(3)}</strong></div>
        <div class="total-row">Загальний вихід: <strong>${totals.totalOutput.toFixed(3)}</strong></div>
        ${meta.guestCount > 0 ? `<div class="per-guest">Їжі на гостя: <strong>${totals.foodPerGuest.toFixed(3)}</strong></div>` : ''}
        ${meta.childCount > 0 ? `<div class="per-guest">Їжі на дитину: <strong>${totals.foodPerChild.toFixed(3)}</strong></div>` : ''}
        ${meta.contractorCount > 0 ? `<div class="per-guest">Їжі на підрядника: <strong>${totals.foodPerContractor.toFixed(3)}</strong></div>` : ''}
        ${totals.perGuestPrice > 0 ? `<div class="per-guest">Ціна з особи (основне меню + обслуг.): <strong>${totals.perGuestPrice.toFixed(2)} грн</strong></div>` : ''}
        <div class="total-final">Разом до сплати: ${totals.total.toFixed(2)} грн</div>
        ${totals.advanceAmount > 0 ? `<div class="per-guest" style="color:${totals.balanceDue > 0 ? '#b9770e' : '#1e8449'};">Залишок після авансу: <strong>${totals.balanceDue.toFixed(2)} грн</strong></div>` : ''}
      </div>
      ${(meta.additionalEventInfo || meta.notes) ? `<div class="notes-section">
        ${meta.additionalEventInfo ? `<h3>Додаткові відомості</h3><div>${meta.additionalEventInfo}</div>` : ''}
        ${meta.notes ? `<h3 style="margin-top:8px;">Примітки</h3><div>${meta.notes}</div>` : ''}
      </div>` : ''}
      </body></html>`;
  };

  const buildOrderPrintHtml = (order: EventOrder) => {
    const parsedOrderNotes = parseOrderNotesMetadata(order.notes || '', order.servicePercent ?? 10);
    const separatorState = parsedOrderNotes.separators;
    const mainDishRows = order.dishes.map((d) => {
      const dish = dishOptionMap.get(d.dishId);
      const normalizedDishOutput = resolveOrderDishOutput(d.dishOutput, d.output, d.portions || 0, dish?.output ?? 0);
      const portions = d.portions || 0;
      return {
        name: formatDishName({ name: d.dishName, output: normalizedDishOutput, servingsCount: dish?.servingsCount }, printSettings),
        portions,
        unitPrice: d.unitPrice || 0,
        amount: d.totalPrice || 0,
        dishOutput: normalizedDishOutput,
        output: normalizedDishOutput * portions,
        servingTiming: d.servingTiming || '',
        feastTableNumber: d.feastTableNumber || '',
        mixedLabel: d.isMixedServe ? (d.mixedGroup || 'Так') : 'Ні',
        dishNotes: d.dishNotes || '',
      };
    });
    const separatorMap = new Map<number, string[]>();
    separatorState.main.forEach((separator) => {
      const bucket = separatorMap.get(separator.index) ?? [];
      bucket.push(separator.label || '');
      separatorMap.set(separator.index, bucket);
    });
    const rowData: Array<{ name: string; portions: number; unitPrice: number; amount: number; dishOutput: number; output: number; servingTiming: string; feastTableNumber: string; mixedLabel: string; dishNotes: string; isSeparator?: boolean; separatorLabel?: string }> = [];
    for (let i = 0; i <= mainDishRows.length; i += 1) {
      const separators = separatorMap.get(i) ?? [];
      separators.forEach((label) => {
        rowData.push({
          name: '',
          portions: 0,
          unitPrice: 0,
          amount: 0,
          dishOutput: 0,
          output: 0,
          servingTiming: '',
          feastTableNumber: '',
          mixedLabel: '',
          dishNotes: '',
          isSeparator: true,
          separatorLabel: label,
        });
      });
      if (i < mainDishRows.length) rowData.push(mainDishRows[i]);
    }
    const childrenData = insertSeparatorsIntoRows(
      (order.children ?? []).map((c) => {
        const meta = c as unknown as Record<string, unknown>;
        const dish = dishOptionMap.get(c.dishId);
        const normalizedDishOutput = resolveOrderDishOutput(c.dishOutput, c.output, c.portions || 0, dish?.output ?? 0);
        const portions = c.portions || 0;
        return {
          name: formatDishName({ name: c.dishName, output: normalizedDishOutput, servingsCount: dish?.servingsCount }, printSettings),
          portions,
          unitPrice: c.unitPrice || 0,
          amount: c.totalPrice || 0,
          dishOutput: normalizedDishOutput,
          output: normalizedDishOutput * portions,
          servingTiming: getMetaString(meta, 'servingTiming'),
          feastTableNumber: getMetaString(meta, 'feastTableNumber'),
          mixedLabel: Boolean(meta.isMixedServe) ? (getMetaString(meta, 'mixedGroup') || 'Так') : 'Ні',
          dishNotes: getMetaString(meta, 'dishNotes'),
          rowKind: 'dish' as const,
          separatorLabel: '',
        };
      }),
      separatorState.children,
      (label) => ({
        name: '',
        portions: 0,
        unitPrice: 0,
        amount: 0,
        dishOutput: 0,
        output: 0,
        rowKind: 'separator' as const,
        separatorLabel: label,
      })
    ).map((row) => ({
      name: row.name,
      portions: row.portions,
      unitPrice: row.unitPrice,
      amount: row.amount,
      dishOutput: row.dishOutput,
      output: row.output,
      servingTiming: row.rowKind === 'dish' ? row.servingTiming : '',
      feastTableNumber: row.rowKind === 'dish' ? row.feastTableNumber : '',
      mixedLabel: row.rowKind === 'dish' ? row.mixedLabel : '',
      dishNotes: row.rowKind === 'dish' ? row.dishNotes : '',
      isSeparator: row.rowKind === 'separator',
      separatorLabel: row.separatorLabel,
    }));
    const contractorData = insertSeparatorsIntoRows(
      (order.contractors ?? []).map((c) => {
        const meta = c as unknown as Record<string, unknown>;
        const dish = dishOptionMap.get(c.dishId);
        const normalizedDishOutput = resolveOrderDishOutput(c.dishOutput, c.output, c.portions || 0, dish?.output ?? 0);
        const portions = c.portions || 0;
        return {
          name: formatDishName({ name: c.dishName, output: normalizedDishOutput, servingsCount: dish?.servingsCount }, printSettings),
          portions,
          unitPrice: c.unitPrice || 0,
          amount: c.totalPrice || 0,
          dishOutput: normalizedDishOutput,
          output: normalizedDishOutput * portions,
          servingTiming: getMetaString(meta, 'servingTiming'),
          feastTableNumber: getMetaString(meta, 'feastTableNumber'),
          mixedLabel: Boolean(meta.isMixedServe) ? (getMetaString(meta, 'mixedGroup') || 'Так') : 'Ні',
          dishNotes: getMetaString(meta, 'dishNotes'),
          rowKind: 'dish' as const,
          separatorLabel: '',
        };
      }),
      separatorState.contractors,
      (label) => ({
        name: '',
        portions: 0,
        unitPrice: 0,
        amount: 0,
        dishOutput: 0,
        output: 0,
        rowKind: 'separator' as const,
        separatorLabel: label,
      })
    ).map((row) => ({
      name: row.name,
      portions: row.portions,
      unitPrice: row.unitPrice,
      amount: row.amount,
      dishOutput: row.dishOutput,
      output: row.output,
      servingTiming: row.rowKind === 'dish' ? row.servingTiming : '',
      feastTableNumber: row.rowKind === 'dish' ? row.feastTableNumber : '',
      mixedLabel: row.rowKind === 'dish' ? row.mixedLabel : '',
      dishNotes: row.rowKind === 'dish' ? row.dishNotes : '',
      isSeparator: row.rowKind === 'separator',
      separatorLabel: row.separatorLabel,
    }));
    const additionalServiceData = (order.additionalServices ?? []).map((s) => ({
      name: s.serviceName,
      quantity: s.quantity || 0,
      unitPrice: s.unitPrice || 0,
      amount: s.totalPrice || 0,
    }));
    const customSectionData = (order.customSections ?? []).map((section) => ({
      name: section.name || '',
      participantCount: section.participantCount || 0,
      serveTiming: section.serveTiming || '',
      rows: (section.dishes ?? []).map((d) => {
        const meta = d as unknown as Record<string, unknown>;
        const dish = dishOptionMap.get(d.dishId);
        const normalizedDishOutput = resolveOrderDishOutput(d.dishOutput, d.output, d.portions || 0, dish?.output ?? 0);
        const portions = d.portions || 0;
        return {
          name: formatDishName({ name: d.dishName, output: normalizedDishOutput, servingsCount: dish?.servingsCount }, printSettings),
          portions,
          unitPrice: d.unitPrice || 0,
          amount: d.totalPrice || 0,
          dishOutput: normalizedDishOutput,
          output: normalizedDishOutput * portions,
          servingTiming: getMetaString(meta, 'servingTiming'),
          feastTableNumber: getMetaString(meta, 'feastTableNumber'),
          mixedLabel: Boolean(meta.isMixedServe) ? (getMetaString(meta, 'mixedGroup') || 'Так') : 'Ні',
          dishNotes: getMetaString(meta, 'dishNotes'),
        };
      }),
    }));
    const printMeta = {
      eventDate: order.eventDate || '',
      orderStatus: getStatusLabel(order.orderStatusId, order.orderStatus),
      meetingTime: order.meetingTime || '',
      ceremonyTime: order.ceremonyTime || '',
      feastTime: order.feastTime || '',
      customerName: order.customerName || '',
      customerContacts: order.customerContacts || '',
      guestCount: order.guestCount || 0,
      childCount: order.childCount || 0,
      contractorCount: order.contractorCount || 0,
      childServeTiming: parsedOrderNotes.sectionMeta.childServeTiming || '',
      contractorServeTiming: parsedOrderNotes.sectionMeta.contractorServeTiming || '',
      menuCompiledBy: order.menuCompiledBy || '',
      additionalEventInfo: order.additionalEventInfo || '',
      notes: parsedOrderNotes.cleanNotes,
      eventVenue: order.eventVenue || '',
      eventName: order.eventName || '',
    };
    const printDishesSubtotal = rowData.reduce((s, r) => s + r.amount, 0);
    const printChildrenSubtotal = childrenData.reduce((s, r) => s + r.amount, 0);
    const printContractorsSubtotal = contractorData.reduce((s, r) => s + r.amount, 0);
    const printCustomSectionsSubtotal = customSectionData.reduce((s, section) => s + section.rows.reduce((inner, r) => inner + r.amount, 0), 0);
    const printAdditionalServicesSubtotal = additionalServiceData.reduce((s, r) => s + r.amount, 0);
    const printFinancialSummary = calculateOrderFinancialSummary({
      dishesSubtotal: printDishesSubtotal,
      childrenSubtotal: printChildrenSubtotal,
      contractorsSubtotal: printContractorsSubtotal,
      customSectionsSubtotal: printCustomSectionsSubtotal,
      additionalServicesSubtotal: printAdditionalServicesSubtotal,
      mainServicePercent: parsedOrderNotes.financials.mainServicePercent,
      childServicePercent: parsedOrderNotes.financials.childServicePercent,
      contractorServicePercent: parsedOrderNotes.financials.contractorServicePercent,
      discountPercent: order.discountPercent || 0,
      advanceAmount: parsedOrderNotes.financials.advanceAmount,
    });
    const printMainMenuOutput = rowData.reduce((s, r) => s + r.output, 0);
    const printChildrenOutput = childrenData.reduce((s, r) => s + r.output, 0);
    const printContractorsOutput = contractorData.reduce((s, r) => s + r.output, 0);
    const printCustomSectionsOutput = customSectionData.reduce((s, section) => s + section.rows.reduce((inner, r) => inner + r.output, 0), 0);
    const printTotalOutput = printMainMenuOutput + printChildrenOutput + printContractorsOutput + printCustomSectionsOutput;
    const printGuestCount = order.guestCount ?? 0;
    const printChildCount = order.childCount ?? 0;
    const printContractorCount = order.contractorCount ?? 0;
    const printTotals = {
      subtotal: printFinancialSummary.subtotal,
      dishesSubtotal: printDishesSubtotal,
      childrenSubtotal: printChildrenSubtotal,
      contractorsSubtotal: printContractorsSubtotal,
      customSectionsSubtotal: printCustomSectionsSubtotal,
      additionalServicesSubtotal: printAdditionalServicesSubtotal,
      mainServicePercent: parsedOrderNotes.financials.mainServicePercent,
      childServicePercent: parsedOrderNotes.financials.childServicePercent,
      contractorServicePercent: parsedOrderNotes.financials.contractorServicePercent,
      mainServiceAmount: printFinancialSummary.mainServiceAmount,
      childServiceAmount: printFinancialSummary.childServiceAmount,
      contractorServiceAmount: printFinancialSummary.contractorServiceAmount,
      serviceAmount: printFinancialSummary.serviceAmount,
      discountPercent: order.discountPercent || 0,
      discountAmount: printFinancialSummary.discountAmount,
      total: printFinancialSummary.total,
      advanceAmount: parsedOrderNotes.financials.advanceAmount,
      balanceDue: printFinancialSummary.balanceDue,
      mainMenuOutput: printMainMenuOutput,
      childrenOutput: printChildrenOutput,
      contractorsOutput: printContractorsOutput,
      totalOutput: printTotalOutput,
      foodPerGuest: printGuestCount > 0 ? printMainMenuOutput / printGuestCount : 0,
      foodPerChild: printChildCount > 0 ? printChildrenOutput / printChildCount : 0,
      foodPerContractor: printContractorCount > 0 ? printContractorsOutput / printContractorCount : 0,
      perGuestPrice: printGuestCount > 0 ? (printDishesSubtotal + printDishesSubtotal * (parsedOrderNotes.financials.mainServicePercent / 100)) / printGuestCount : 0,
    };
    // Auto-enable timing/feast/mix/notes if any dish has data for those fields.
    const printRowsForAuto = [
      ...rowData,
      ...childrenData,
      ...contractorData,
      ...customSectionData.flatMap((section) => section.rows.map((r) => ({ ...r, isSeparator: false }))),
    ].map(getPrintableRowMeta);
    const autoTiming = printRowsForAuto.some((r) => !r.isSeparator && !!r.servingTiming);
    const autoFeast  = printRowsForAuto.some((r) => !r.isSeparator && !!r.feastTableNumber);
    const autoMix    = printRowsForAuto.some((r) => !r.isSeparator && r.mixedLabel && r.mixedLabel !== 'Ні');
    const autoNotes  = printRowsForAuto.some((r) => !r.isSeparator && !!r.dishNotes);
    const effectivePrintColumns = {
      output: printColumns.output,
      totalOutput: printColumns.totalOutput,
      timing: autoTiming,
      feast: autoFeast,
      mix: autoMix,
      notes: autoNotes,
    };
    return buildPrintHtml(`Замовлення #${order.id}`, printMeta, printTotals, rowData, childrenData, contractorData, customSectionData, additionalServiceData, effectivePrintColumns, printLayout, printSplitSections);
  };

  const extractBodyFromPrintHtml = (html: string) => {
    const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return match ? match[1] : html;
  };

  const buildMultiOrderPrintHtml = (ordersToPrint: EventOrder[]) => {
    const docs = ordersToPrint.map(buildOrderPrintHtml);
    const styleMatch = docs[0]?.match(/<style>([\s\S]*?)<\/style>/i);
    const styleContent = styleMatch ? styleMatch[1] : '';
    const bodyContent = docs
      .map((doc, index) => `<section class="${index > 0 ? 'print-page-break' : ''}">${extractBodyFromPrintHtml(doc)}</section>`)
      .join('');
    return `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/><title>Друк замовлень</title><style>${styleContent}.print-page-break{break-before:page;page-break-before:always;}</style></head><body>${bodyContent}</body></html>`;
  };

  const handlePrintOrder = (order: EventOrder) => {
    openPrintWindow(buildOrderPrintHtml(order));
  };

  const handlePrintSelectedOrders = () => {
    const selectedOrdersForPrint = sortedOrders.filter((order) => selectedPrintOrderIds.includes(order.id));
    if (selectedOrdersForPrint.length === 0) {
      window.alert('Оберіть хоча б одне замовлення для друку.');
      return;
    }
    openPrintWindow(buildMultiOrderPrintHtml(selectedOrdersForPrint));
  };

  const handleExportOrderPdf = async (order: EventOrder) => {
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.left = '-10000px';
    host.style.top = '0';
    host.style.width = '1122px';
    host.style.background = '#fff';
    host.style.padding = '0';
    host.style.boxSizing = 'border-box';
    const multiHtml = buildMultiOrderPrintHtml([order]);
    const styleContent = multiHtml.match(/<style>([\s\S]*?)<\/style>/i)?.[1] || '';
    const bodyContent = extractBodyFromPrintHtml(multiHtml);
    host.innerHTML = `<style>${styleContent}</style>${bodyContent}`;
    document.body.appendChild(host);
    try {
      const canvas = await html2canvas(host, { scale: 2, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      doc.save(`Замовлення_${order.id}.pdf`);
    } finally {
      document.body.removeChild(host);
    }
  };

  const handlePreparationPortionsChange = (key: string, value: string) => {
    const parsed = parseFloat(value.replace(',', '.'));
    setPrepRows((prev) =>
      prev.map((row) =>
        row.key === key
          ? { ...row, adjustedPortions: Number.isFinite(parsed) ? parsed : 0 }
          : row
      )
    );
  };

  const handlePreparationExcelExport = () => {
    if (!prepOrder || prepFilteredRows.length === 0) {
      alert('Немає даних для експорту.');
      return;
    }

    const rowsForExcel: Array<Record<string, string | number>> = [];
    prepFilteredRows.forEach((row) => {
      if (row.semiItems.length === 0) {
        rowsForExcel.push({
          'День/таймінг': row.day,
          'Розділ': row.section,
          'Категорія': row.category,
          'Страва': row.dishName,
          'Порції в меню': row.menuPortions,
          'Скориговані порції': row.adjustedPortions,
          'Н/Ф': '',
          'Н/Ф на 1 порцію': '',
          'Н/Ф на скориговані порції': '',
        });
        return;
      }
      row.semiItems.forEach((semi) => {
        rowsForExcel.push({
          'День/таймінг': row.day,
          'Розділ': row.section,
          'Категорія': row.category,
          'Страва': row.dishName,
          'Порції в меню': row.menuPortions,
          'Скориговані порції': row.adjustedPortions,
          'Н/Ф': semi.name,
          'Н/Ф на 1 порцію': Number(semi.qtyPerPortion.toFixed(4)),
          'Н/Ф на скориговані порції': Number((semi.qtyPerPortion * row.adjustedPortions).toFixed(4)),
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(rowsForExcel);
    ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 14 },
      { wch: 18 }, { wch: 28 }, { wch: 16 }, { wch: 24 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Бланк підготовки');
    XLSX.writeFile(wb, `preparation_blank_order_${prepOrder.id}.xlsx`);
  };

  const handlePreparationPdfExport = () => {
    if (!prepOrder || prepFilteredRows.length === 0) {
      alert('Немає даних для експорту.');
      return;
    }
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const grouped = prepFilteredRows.reduce<Record<string, PreparationDishRow[]>>((acc, row) => {
      if (!acc[row.day]) acc[row.day] = [];
      acc[row.day].push(row);
      return acc;
    }, {});

    const body = Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b, 'uk'))
      .map(([day, dayRows]) => `
        <h2>${escapeHtml(day)}</h2>
        <table>
          <thead>
            <tr>
              <th>Розділ</th><th>Категорія</th><th>Страва</th><th>Порції в меню</th><th>Скориговані</th><th>Н/Ф на 1 порцію</th><th>Н/Ф на скориговані порції</th>
            </tr>
          </thead>
          <tbody>
            ${dayRows.map((row) => {
              const semisOne = row.semiItems.length
                ? row.semiItems.map((s) => `${escapeHtml(s.name)}: ${s.qtyPerPortion.toFixed(4)}`).join('<br/>')
                : '—';
              const semisTotal = row.semiItems.length
                ? row.semiItems.map((s) => `${escapeHtml(s.name)}: ${(s.qtyPerPortion * row.adjustedPortions).toFixed(4)}`).join('<br/>')
                : '—';
              return `
                <tr>
                  <td>${escapeHtml(row.section)}</td>
                  <td>${escapeHtml(row.category)}</td>
                  <td>${escapeHtml(formatDishName({ name: row.dishName, output: dishOptions.find((d) => d.id === row.dishId)?.output, servingsCount: dishOptions.find((d) => d.id === row.dishId)?.servingsCount }, printSettings))}</td>
                  <td style="text-align:right;">${row.menuPortions.toFixed(2)}</td>
                  <td style="text-align:right;">${row.adjustedPortions.toFixed(2)}</td>
                  <td>${semisOne}</td>
                  <td>${semisTotal}</td>
                </tr>`;
            }).join('')}
          </tbody>
        </table>
      `).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Бланк підготовки #${prepOrder.id}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; color: #1f2937; }
        h1 { margin: 0 0 8px 0; }
        h2 { margin: 18px 0 8px 0; color: #c0392b; }
        .meta { margin-bottom: 4px; color: #4b5563; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
        th, td { border: 1px solid #d1d5db; padding: 7px 8px; font-size: 12px; vertical-align: top; }
        th { background: #f3f4f6; text-align: left; }
      </style></head><body>
      <h1>Бланк підготовки до виконання робіт</h1>
      <div class="meta">Замовлення №${prepOrder.id}</div>
      <div class="meta">Подія: ${escapeHtml(prepOrder.eventName || '—')} | Замовник: ${escapeHtml(prepOrder.customerName || '—')}</div>
      ${body}
      </body></html>`;
    openPrintWindow(html);
  };

  const handlePrintModal = () => {
    const rowData = rows
      .flatMap((r) => {
        if (r.rowKind === 'separator') {
          return [{
            name: '',
            portions: 0,
            unitPrice: 0,
            amount: 0,
            dishOutput: 0,
            output: 0,
            servingTiming: '',
            feastTableNumber: '',
            mixedLabel: '',
            dishNotes: '',
            isSeparator: true,
            separatorLabel: r.separatorLabel || '',
          }];
        }
        if (!r.dishId) return [];
        const dish = dishOptions.find((d) => d.id === Number(r.dishId));
        return [{
          name: dish?.name ?? '—',
          portions: parseFloat(r.portions) || 0,
          unitPrice: parseFloat(r.unitPrice) || 0,
          amount: getRowAmount(r),
          dishOutput: parseFloat(r.dishOutput) || 0,
          output: getRowOutput(r),
          servingTiming: r.servingTiming || '',
          feastTableNumber: r.feastTableNumber || '',
          mixedLabel: r.isMixedServe ? (r.mixedGroup || 'Так') : 'Ні',
          dishNotes: r.dishNotes || '',
        }];
      });
    const childrenData = childRows.flatMap((r) => {
      if (r.rowKind === 'separator') {
        return [{
          name: '',
          portions: 0,
          unitPrice: 0,
          amount: 0,
          dishOutput: 0,
          output: 0,
          servingTiming: '',
          feastTableNumber: '',
          mixedLabel: '',
          dishNotes: '',
          isSeparator: true,
          separatorLabel: r.separatorLabel || '',
        }];
      }
      if (!r.dishId) return [];
      const dish = dishOptions.find((d) => d.id === Number(r.dishId));
      return [{
        name: dish?.name ?? '—',
        portions: parseFloat(r.portions) || 0,
        unitPrice: parseFloat(r.unitPrice) || 0,
        amount: getMenuRowAmount(r.portions, r.unitPrice),
        dishOutput: parseFloat(r.dishOutput) || 0,
        output: (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0),
        servingTiming: r.servingTiming || '',
        feastTableNumber: r.feastTableNumber || '',
        mixedLabel: r.isMixedServe ? (r.mixedGroup || 'Так') : 'Ні',
        dishNotes: r.dishNotes || '',
      }];
    });
    const contractorData = contractorRows.flatMap((r) => {
      if (r.rowKind === 'separator') {
        return [{
          name: '',
          portions: 0,
          unitPrice: 0,
          amount: 0,
          dishOutput: 0,
          output: 0,
          servingTiming: '',
          feastTableNumber: '',
          mixedLabel: '',
          dishNotes: '',
          isSeparator: true,
          separatorLabel: r.separatorLabel || '',
        }];
      }
      if (!r.dishId) return [];
      const dish = dishOptions.find((d) => d.id === Number(r.dishId));
      return [{
        name: dish?.name ?? '—',
        portions: parseFloat(r.portions) || 0,
        unitPrice: parseFloat(r.unitPrice) || 0,
        amount: getMenuRowAmount(r.portions, r.unitPrice),
        dishOutput: parseFloat(r.dishOutput) || 0,
        output: (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0),
        servingTiming: r.servingTiming || '',
        feastTableNumber: r.feastTableNumber || '',
        mixedLabel: r.isMixedServe ? (r.mixedGroup || 'Так') : 'Ні',
        dishNotes: r.dishNotes || '',
      }];
    });
    const additionalServiceData = additionalServiceRows
      .filter((r) => r.additionalServiceId)
      .map((r) => {
      const service = additionalServiceOptions.find((s) => s.id === Number(r.additionalServiceId));
      return {
        name: service?.name ?? '—',
        quantity: parseFloat(r.quantity) || 0,
        unitPrice: parseFloat(r.unitPrice) || 0,
        amount: (parseFloat(r.quantity) || 0) * (parseFloat(r.unitPrice) || 0),
      };
      });
    const customSectionData = customSections
      .filter((section) => section.name.trim())
      .map((section) => ({
        name: section.name.trim(),
        participantCount: parseInt(section.participantCount, 10) || 0,
        serveTiming: section.serveTiming || '',
        rows: section.rows
          .filter((r) => r.dishId)
          .map((r) => {
            const dish = dishOptions.find((d) => d.id === Number(r.dishId));
            return {
              name: dish?.name ?? '—',
              portions: parseFloat(r.portions) || 0,
              unitPrice: parseFloat(r.unitPrice) || 0,
              amount: getCustomRowAmount(r),
              dishOutput: parseFloat(r.dishOutput) || 0,
              output: getCustomRowOutput(r),
              servingTiming: r.servingTiming || '',
              feastTableNumber: r.feastTableNumber || '',
              mixedLabel: r.isMixedServe ? (r.mixedGroup || 'Так') : 'Ні',
              dishNotes: r.dishNotes || '',
            };
          }),
      }));
    const printMeta = {
      eventDate: `${form.eventDate}T00:00:00`,
      orderStatus: getStatusLabel(parseOrderStatusId(form.orderStatusId)),
      meetingTime: form.meetingTime,
      ceremonyTime: form.ceremonyTime,
      feastTime: form.feastTime,
      customerName: form.customerName,
      customerContacts: form.customerContacts,
      guestCount,
      childCount,
      contractorCount,
      childServeTiming: form.childServeTiming || '',
      contractorServeTiming: form.contractorServeTiming || '',
      menuCompiledBy: form.menuCompiledBy,
      additionalEventInfo: form.additionalEventInfo,
      notes: form.notes,
      eventVenue: form.eventVenue,
      eventName: form.eventName,
    };
    const printTotals = {
      subtotal,
      dishesSubtotal,
      childrenSubtotal,
      contractorsSubtotal,
      customSectionsSubtotal,
      additionalServicesSubtotal,
      mainServicePercent: servicePercent,
      childServicePercent,
      contractorServicePercent,
      mainServiceAmount,
      childServiceAmount: childrenServiceAmount,
      contractorServiceAmount: contractorsServiceAmount,
      serviceAmount,
      discountPercent,
      discountAmount,
      total,
      advanceAmount,
      balanceDue,
      mainMenuOutput,
      childrenOutput,
      contractorsOutput,
      totalOutput,
      foodPerGuest,
      foodPerChild,
      foodPerContractor,
      perGuestPrice,
    };
    const printRowsForAuto = [
      ...rowData,
      ...childrenData,
      ...contractorData,
      ...customSectionData.flatMap((section) => section.rows.map((r) => ({ ...r, isSeparator: false }))),
    ].map(getPrintableRowMeta);
    const effectivePrintColumns = {
      output: printColumns.output,
      totalOutput: printColumns.totalOutput,
      timing: printRowsForAuto.some((r) => !r.isSeparator && !!r.servingTiming),
      feast: printRowsForAuto.some((r) => !r.isSeparator && !!r.feastTableNumber),
      mix: printRowsForAuto.some((r) => !r.isSeparator && r.mixedLabel && r.mixedLabel !== 'Ні'),
      notes: printRowsForAuto.some((r) => !r.isSeparator && !!r.dishNotes),
    };
    openPrintWindow(
      buildPrintHtml(
        editingId ? `Замовлення #${editingId}` : 'Нове замовлення',
        printMeta,
        printTotals,
        rowData,
        childrenData,
        contractorData,
        customSectionData,
        additionalServiceData,
        effectivePrintColumns,
        printLayout,
        printSplitSections
      )
    );
  };

  const downloadExcelTemplate = async () => {
    setExcelImportError('');
    try {
      const response = await api.get('/eventorders/excel/template', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `CRM4Chef_шаблон_замовлення_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setExcelImportError(error?.response?.data?.message || 'Не вдалося завантажити шаблон.');
    }
  };

  const openExcelImport = () => {
    setExcelPreview(null);
    setExcelPreviews([]);
    setExcelPreviewIndex(0);
    setExcelImportProgress('');
    setExcelImportError('');
    setExcelImportOpen(true);
  };

  const previewExcelFile = async (file: File) => {
    setExcelImportBusy(true);
    setExcelImportError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<EventOrderExcelPreview[]>('/eventorders/excel/preview-batch', formData);
      const previews = response.data.map(preview => ({
        ...preview,
        rows: prepareExcelDishRows(preview.rows),
        children: prepareExcelDishRows(preview.children ?? []),
        contractors: prepareExcelDishRows(preview.contractors ?? []),
        additionalServices: preview.additionalServices ?? [],
      }));
      setExcelPreviews(previews);
      setExcelPreviewIndex(0);
      setExcelPreview(previews[0] ?? null);
      setExcelImportProgress('');
    } catch (error: any) {
      setExcelPreview(null);
      setExcelPreviews([]);
      setExcelImportError([error?.response?.data?.message || 'Не вдалося прочитати Excel-файл.', error?.response?.data?.details?.message].filter(Boolean).join(' '));
    } finally {
      setExcelImportBusy(false);
      if (excelFileInputRef.current) excelFileInputRef.current.value = '';
    }
  };

  const prepareExcelDishRows = (items: EventOrderExcelPreviewRow[]) => items.map((row) => ({
    ...row,
    updateB52Price: Boolean(row.matchedDishId && row.matchConfidence === 1 && row.unitPrice > 0),
  }));

  const updateExcelPreviewRow = (
    section: 'rows' | 'children' | 'contractors',
    rowNumber: number,
    patch: Partial<EventOrderExcelPreviewRow>) => {
    setExcelPreview((current) => current
      ? { ...current, [section]: current[section].map((row) => row.rowNumber === rowNumber ? { ...row, ...patch } : row) }
      : current);
  };

  const updateExcelServiceRow = (rowNumber: number, patch: Partial<EventOrderExcelServicePreviewRow>) => {
    setExcelPreview((current) => current
      ? { ...current, additionalServices: current.additionalServices.map((row) => row.rowNumber === rowNumber ? { ...row, ...patch } : row) }
      : current);
  };

  const importExcelOrder = async () => {
    if (!excelPreview) return;
    const previews = excelPreviews.map((preview, index) => index === excelPreviewIndex ? excelPreview : preview);
    const b52Prices = new Map<number, number>();
    for (const preview of previews) {
      for (const row of [...preview.rows, ...preview.children, ...preview.contractors]) {
        if (!row.updateB52Price || !row.matchedDishId) continue;
        const existingPrice = b52Prices.get(row.matchedDishId);
        if (existingPrice !== undefined && existingPrice !== row.unitPrice) {
          setExcelImportError(`Різні ціни B52 для страви «${row.matchedDishName || row.sourceDishName}» у файлі. Залиште прапорець оновлення лише біля потрібної ціни.`);
          return;
        }
        b52Prices.set(row.matchedDishId, row.unitPrice);
      }
    }
    for (let index = 0; index < previews.length; index++) {
    const excelPreview = previews[index];
    const allDishRows = [...excelPreview.rows, ...excelPreview.children, ...excelPreview.contractors];
    const invalidRows = allDishRows.filter((row) =>
      !row.matchedDishId || row.matchConfidence < .75 || row.portions <= 0 || row.unitPrice < 0);
    const invalidServices = excelPreview.additionalServices.filter((row) =>
      !row.matchedAdditionalServiceId || row.matchConfidence < .75 || row.quantity <= 0 || row.unitPrice < 0);
    if (!excelPreview.customerName.trim()) {
      setExcelImportError(`Замовлення ${index + 1}: вкажіть замовника.`);
      return;
    }
    if (!excelPreview.eventDate) {
      setExcelImportError(`Замовлення ${index + 1}: вкажіть дату події.`);
      return;
    }
    if (invalidRows.length > 0) {
      setExcelImportError(`Замовлення ${index + 1}: для кожного рядка оберіть страву та вкажіть кількість порцій більше нуля.`);
      return;
    }
    if (invalidServices.length > 0) {
      setExcelImportError(`Замовлення ${index + 1}: для кожного рядка додаткових послуг оберіть послугу та вкажіть кількість більше нуля.`);
      return;
    }
    }

    setExcelImportBusy(true);
    setExcelImportError('');
    const created: number[] = [];
    let updatedPrices = 0;
    try {
      for (let index = 0; index < previews.length; index++) {
      const excelPreview = previews[index];
      setExcelImportProgress(`Імпортується замовлення ${index + 1} / ${previews.length}. Створено: ${created.length}.`);
      const response = await api.post<{ id: number; updatedB52Prices: number }>('/eventorders/excel/import', {
        ...excelPreview,
        eventDate: `${excelPreview.eventDate.slice(0, 10)}T00:00:00`,
        rows: excelPreview.rows.map((row) => ({
          rowNumber: row.rowNumber,
          dishId: row.matchedDishId,
          portions: row.portions,
          unitPrice: row.unitPrice,
          dishOutput: row.dishOutput,
          servingTiming: row.servingTiming,
          feastTableNumber: row.feastTableNumber,
          dishNotes: row.dishNotes,
          updateB52Price: row.updateB52Price,
        })),
        children: excelPreview.children.map((row) => ({
          rowNumber: row.rowNumber,
          dishId: row.matchedDishId,
          portions: row.portions,
          unitPrice: row.unitPrice,
          dishOutput: row.dishOutput,
          servingTiming: row.servingTiming,
          feastTableNumber: row.feastTableNumber,
          dishNotes: row.dishNotes,
          updateB52Price: row.updateB52Price,
        })),
        contractors: excelPreview.contractors.map((row) => ({
          rowNumber: row.rowNumber,
          dishId: row.matchedDishId,
          portions: row.portions,
          unitPrice: row.unitPrice,
          dishOutput: row.dishOutput,
          servingTiming: row.servingTiming,
          feastTableNumber: row.feastTableNumber,
          dishNotes: row.dishNotes,
          updateB52Price: row.updateB52Price,
        })),
        additionalServices: excelPreview.additionalServices.map((row) => ({
          rowNumber: row.rowNumber,
          additionalServiceId: row.matchedAdditionalServiceId,
          quantity: row.quantity,
          unitPrice: row.unitPrice,
          unitCost: row.unitCost,
        })),
        serviceLaborCost: excelPreview.serviceLaborCost ?? 0,
        otherCost: excelPreview.otherCost ?? 0,
        costDataConfirmed: Boolean(excelPreview.costDataConfirmed),
      });
      created.push(response.data.id);
      updatedPrices += response.data.updatedB52Prices;
      }
      setExcelImportOpen(false);
      setExcelPreview(null);
      setExcelPreviews([]);
      setListMessage({
        type: 'success',
        text: `Імпортовано замовлень: ${created.length} (${created.map(id => `#${id}`).join(', ')}). Оновлено цін B52: ${updatedPrices}.`,
      });
    } catch (error: any) {
      const remaining = previews.slice(created.length);
      setExcelPreviews(remaining);
      setExcelPreviewIndex(0);
      setExcelPreview(remaining[0] ?? null);
      setExcelImportError(`${error?.response?.data?.message || 'Не вдалося імпортувати замовлення.'} Уже створено: ${created.length}${created.length ? ` (${created.map(id => `#${id}`).join(', ')})` : ''}. Імпорт зупинено. Перевірте список перед повтором: результат останнього запиту може бути невідомим.`);
    } finally {
      setExcelImportProgress('');
      setExcelImportBusy(false);
    }
    try {
      await refreshOrders();
    } catch {
      setExcelImportError(current => `${current} Не вдалося оновити список замовлень.`);
    }
  };

  const renderExcelDishSection = (
    title: string,
    section: 'children' | 'contractors',
    items: EventOrderExcelPreviewRow[]) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginTop: 18 }}>
        <h4 style={{ margin: '0 0 8px', color: section === 'children' ? 'var(--crm-success)' : 'var(--crm-info)' }}>{title} ({items.length})</h4>
        <div className="event-order-import-scroll" style={{ overflowX: 'auto', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }}>
          <table className="event-order-import-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead><tr>
              <th style={excelImportThStyle}>Рядок</th>
              <th style={excelImportThStyle}>Назва з Excel</th>
              <th style={{ ...excelImportThStyle, minWidth: 270 }}>Страва CRM *</th>
              <th style={excelImportThStyle}>Збіг</th>
              <th style={excelImportThStyle}>Ціна</th>
              <th style={excelImportThStyle}>Вихід</th>
              <th style={{ ...excelImportThStyle, textAlign: 'center' }}>Оновити B52</th>
            </tr></thead>
            <tbody>
              {items.map((row) => {
                const needsReview = !row.matchedDishId || row.matchConfidence < .75;
                return (
                  <tr key={`${section}-${row.rowNumber}`} style={{ background: needsReview ? 'var(--crm-warning-bg)' : undefined }}>
                    <td data-label="Рядок" style={excelImportTdStyle}>{row.rowNumber}</td>
                    <td data-label="Назва з Excel" style={excelImportTdStyle}>{row.sourceDishName || '—'}</td>
                    <td data-label="Страва CRM" style={excelImportTdStyle}>
                      <ItemQuantityField
                        items={itemQuantityDishOptions}
                        value={row.matchedDishId ? String(row.matchedDishId) : ''}
                        onChange={(value) => {
                          const id = parseInt(value, 10);
                          const dish = dishOptions.find((item) => Number(item.id) === id);
                          updateExcelPreviewRow(section, row.rowNumber, {
                            matchedDishId: dish ? Number(dish.id) : null,
                            matchedDishName: dish?.rawName ?? '',
                            dishOutput: row.dishOutput > 0 ? row.dishOutput : (dish?.output ?? 0),
                            matchConfidence: dish ? 1 : 0,
                            matchMethod: dish ? 'Обрано вручну' : 'Не знайдено',
                            updateB52Price: false,
                          });
                        }}
                        quantity={String(row.portions)}
                        onQuantityChange={(value) => updateExcelPreviewRow(section, row.rowNumber, { portions: parseFloat(value) || 0 })}
                        label="Страва"
                        quantityLabel="Порції"
                        unit="порц."
                        min={0}
                        step={0.01}
                        quantityRequired={false}
                        placeholder="🔍 Пошук страви..."
                      />
                    </td>
                    <td data-label="Збіг" style={excelImportTdStyle}>
                      <span style={{ color: needsReview ? 'var(--crm-warning)' : 'var(--crm-success)', fontWeight: 600 }}>{row.matchMethod}</span>
                      <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>{Math.round(row.matchConfidence * 100)}%</div>
                    </td>
                    <td data-label="Ціна" style={excelImportTdStyle}><input type="number" min="0" step="0.01" value={row.unitPrice} onChange={(e) => updateExcelPreviewRow(section, row.rowNumber, { unitPrice: parseFloat(e.target.value) || 0 })} style={{ ...inputStyle, width: 95 }} /></td>
                    <td data-label="Вихід" style={excelImportTdStyle}><input type="number" min="0" step="0.001" value={row.dishOutput} onChange={(e) => updateExcelPreviewRow(section, row.rowNumber, { dishOutput: parseFloat(e.target.value) || 0 })} style={{ ...inputStyle, width: 95 }} /></td>
                    <td data-label="Оновити B52" style={{ ...excelImportTdStyle, textAlign: 'center' }}><input type="checkbox" checked={row.updateB52Price} disabled={!row.matchedDishId} onChange={(e) => updateExcelPreviewRow(section, row.rowNumber, { updateB52Price: e.target.checked })} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderExcelServicesSection = (items: EventOrderExcelServicePreviewRow[]) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginTop: 18 }}>
        <h4 style={{ margin: '0 0 8px', color: 'var(--crm-warning)' }}>Додаткові послуги ({items.length})</h4>
        <div className="event-order-import-scroll" style={{ overflowX: 'auto', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }}>
          <table className="event-order-import-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={excelImportThStyle}>Рядок</th>
              <th style={excelImportThStyle}>Назва з Excel</th>
              <th style={{ ...excelImportThStyle, minWidth: 270 }}>Послуга CRM *</th>
              <th style={excelImportThStyle}>Збіг</th>
              <th style={excelImportThStyle}>Ціна</th>
            </tr></thead>
            <tbody>
              {items.map((row) => {
                const needsReview = !row.matchedAdditionalServiceId || row.matchConfidence < .75;
                return (
                  <tr key={`service-${row.rowNumber}`} style={{ background: needsReview ? 'var(--crm-warning-bg)' : undefined }}>
                    <td data-label="Рядок" style={excelImportTdStyle}>{row.rowNumber}</td>
                    <td data-label="Назва з Excel" style={excelImportTdStyle}>{row.sourceServiceName || '—'}</td>
                    <td data-label="Послуга CRM" style={excelImportTdStyle}>
                      <ItemQuantityField
                        items={itemQuantityServiceOptions}
                        value={row.matchedAdditionalServiceId ? String(row.matchedAdditionalServiceId) : ''}
                        onChange={(value) => {
                          const id = parseInt(value, 10);
                          const service = additionalServiceOptions.find((item) => Number(item.id) === id);
                          updateExcelServiceRow(row.rowNumber, {
                            matchedAdditionalServiceId: service ? Number(service.id) : null,
                            matchedAdditionalServiceName: service?.name ?? '',
                            matchConfidence: service ? 1 : 0,
                            matchMethod: service ? 'Обрано вручну' : 'Не знайдено',
                            unitPrice: row.unitPrice > 0 ? row.unitPrice : (service?.price ?? 0),
                          });
                        }}
                        quantity={String(row.quantity)}
                        onQuantityChange={(value) => updateExcelServiceRow(row.rowNumber, { quantity: parseFloat(value) || 0 })}
                        label="Послуга"
                        quantityLabel="Кількість"
                        min={0}
                        step={0.01}
                        quantityRequired={false}
                        placeholder="🔍 Пошук послуги..."
                      />
                    </td>
                    <td data-label="Збіг" style={excelImportTdStyle}>
                      <span style={{ color: needsReview ? 'var(--crm-warning)' : 'var(--crm-success)', fontWeight: 600 }}>{row.matchMethod}</span>
                      <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>{Math.round(row.matchConfidence * 100)}%</div>
                    </td>
                    <td data-label="Ціна" style={excelImportTdStyle}><input type="number" min="0" step="0.01" value={row.unitPrice} onChange={(e) => updateExcelServiceRow(row.rowNumber, { unitPrice: parseFloat(e.target.value) || 0 })} style={{ ...inputStyle, width: 105 }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleSave = async () => {
    if (!form.eventDate) {
      setError('Дата події обовʼязкова');
      return;
    }
    if (!form.customerName.trim()) {
      setError('Імʼя замовника обовʼязкове');
      return;
    }

    const parsePositiveId = (value: string) => {
      const id = parseInt(value, 10);
      return Number.isFinite(id) && id > 0 ? id : null;
    };

    const payload = {
      eventDate: `${form.eventDate}T00:00:00`,
      orderStatusId: parseOrderStatusId(form.orderStatusId),
      meetingTime: form.meetingTime || '',
      ceremonyTime: form.ceremonyTime || '',
      feastTime: form.feastTime || '',
      customerName: form.customerName.trim(),
      customerContacts: form.customerContacts.trim() || '',
      guestCount,
      childCount,
      contractorCount,
      menuCompiledBy: (username || form.menuCompiledBy || '').trim(),
      customerId: null,
      administratorId: null,
      servicePercent: effectiveServicePercent,
      discountPercent,
      additionalEventInfo: form.additionalEventInfo || '',
      notes: composeOrderNotesMetadata(
        form.notes || '',
        {
          main: rows.reduce<MenuSeparatorMeta[]>((acc, row) => {
            if (row.rowKind === 'separator') {
              const dishIndex = rows
                .slice(0, rows.findIndex((candidate) => candidate.key === row.key))
                .filter((candidate) => candidate.rowKind === 'dish').length;
              acc.push({ index: dishIndex, label: row.separatorLabel || '' });
            }
            return acc;
          }, []),
          children: extractSeparatorsFromRows(childRows),
          contractors: extractSeparatorsFromRows(contractorRows),
        },
        {
          mainServicePercent: servicePercent,
          childServicePercent,
          contractorServicePercent,
          advanceAmount,
        },
        {
          childServeTiming: form.childServeTiming || '',
          contractorServeTiming: form.contractorServeTiming || '',
        }
      ),
      eventVenue: form.eventVenue || '',
      eventName: form.eventName || '',
      sourceWarehouseId: form.sourceWarehouseId ? Number(form.sourceWarehouseId) : null,
      dishes: rows
        .filter((r) => r.rowKind === 'dish')
        .map((r) => ({ ...r, parsedDishId: parsePositiveId(r.dishId) }))
        .filter((r) => r.parsedDishId)
        .map((r) => ({
          dishId: r.parsedDishId as number,
          dishOutput: parseFloat(r.dishOutput) || 0,
          portions: parseFloat(r.portions) || 0,
          output: getRowOutput(r),
          unitPrice: parseFloat(r.unitPrice) || 0,
          servingTiming: r.servingTiming || '',
          feastTableNumber: r.feastTableNumber || '',
          isMixedServe: Boolean(r.isMixedServe),
          mixedGroup: r.mixedGroup || '',
          dishNotes: r.dishNotes || '',
        })),
      children: childRows
        .filter((r) => r.rowKind === 'dish')
        .map((r) => ({ ...r, parsedDishId: parsePositiveId(r.dishId) }))
        .filter((r) => r.parsedDishId)
        .map((r) => ({
          dishId: r.parsedDishId as number,
          dishOutput: parseFloat(r.dishOutput) || 0,
          portions: parseFloat(r.portions) || 0,
          output: (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0),
          unitPrice: parseFloat(r.unitPrice) || 0,
        })),
      contractors: contractorRows
        .filter((r) => r.rowKind === 'dish')
        .map((r) => ({ ...r, parsedDishId: parsePositiveId(r.dishId) }))
        .filter((r) => r.parsedDishId)
        .map((r) => ({
          dishId: r.parsedDishId as number,
          dishOutput: parseFloat(r.dishOutput) || 0,
          portions: parseFloat(r.portions) || 0,
          output: (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0),
          unitPrice: parseFloat(r.unitPrice) || 0,
        })),
      customSections: customSections
        .filter((section) => section.name.trim())
        .slice(0, 5)
        .map((section) => ({
          name: section.name.trim(),
          participantCount: parseInt(section.participantCount, 10) || 0,
          serveTiming: section.serveTiming || '',
          dishes: section.rows
            .map((r) => ({ ...r, parsedDishId: parsePositiveId(r.dishId) }))
            .filter((r) => r.parsedDishId)
            .map((r) => ({
              dishId: r.parsedDishId as number,
              dishOutput: parseFloat(r.dishOutput) || 0,
              portions: parseFloat(r.portions) || 0,
              output: getCustomRowOutput(r),
              unitPrice: parseFloat(r.unitPrice) || 0,
            })),
        })),
      additionalServices: additionalServiceRows
        .map((r) => ({ ...r, parsedServiceId: parsePositiveId(r.additionalServiceId) }))
        .filter((r) => r.parsedServiceId && (parseFloat(r.quantity) || 0) > 0)
        .map((r) => ({
          additionalServiceId: r.parsedServiceId as number,
          quantity: parseFloat(r.quantity) || 0,
          unitPrice: parseFloat(r.unitPrice) || 0,
          unitCost: r.unitCost === '' ? null : (parseFloat(r.unitCost) || 0),
        })),
      serviceLaborCost: parseFloat(form.serviceLaborCost) || 0,
      otherCost: parseFloat(form.otherCost) || 0,
      costDataConfirmed: form.costDataConfirmed,
    };

    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await dispatch(updateEventOrder({ id: editingId, data: payload })).unwrap();
        setProfitabilityByOrderId((current) => {
          const next = { ...current };
          delete next[editingId];
          return next;
        });
      } else {
        await dispatch(createEventOrder(payload)).unwrap();
      }
      await refreshOrders();
      closeModal(true);
    } catch (e: any) {
      const responseData = e?.response?.data ?? e?.data ?? e;
      const validationErrors =
        responseData?.errors && typeof responseData.errors === 'object'
          ? Object.values(responseData.errors).flat().filter(Boolean).join('; ')
          : null;
      const message =
        (typeof responseData === 'string' && responseData) ||
        validationErrors ||
        responseData?.message ||
        responseData?.title ||
        e?.message ||
        (Array.isArray(responseData) && responseData.length > 0
          ? responseData.map((x: any) => x?.description || x?.Description || '').filter(Boolean).join('; ')
          : null) ||
        'Не вдалося зберегти замовлення';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const openProductionRouting = async (order: EventOrder) => {
    const current = ++routingRequestSequence.current;
    setRoutingOrder(order); setProductionRouting(null);
    setRoutingLoading(true); setRoutingError('');
    try {
      const [routingResponse, kitchensResponse] = await Promise.all([
        api.get<ProductionRouting>(`/eventorders/${order.id}/production-routing`),
        api.get<Array<{ id: number; name: string }>>('/producing-kitchens'),
      ]);
      if (current !== routingRequestSequence.current) return;
      setProductionRouting(routingResponse.data);
      setProductionKitchens(kitchensResponse.data);
    } catch (requestError: unknown) {
      if (current === routingRequestSequence.current) {
        setRoutingError(axios.isAxiosError<{ message?: string }>(requestError)
          ? requestError.response?.data?.message || 'Не вдалося завантажити маршрутизацію.'
          : 'Не вдалося завантажити маршрутизацію.');
      }
    } finally {
      if (current === routingRequestSequence.current) setRoutingLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Видалити замовлення?')) return;
    await dispatch(deleteEventOrder(id));
  };

  const buildOrderUpdatePayload = (
    order: EventOrder,
    options?: {
      statusId?: number;
      resolveDishUnitPrice?: (dishId: number, currentUnitPrice: number) => number;
    }
  ) => {
    const resolveDishUnitPrice = options?.resolveDishUnitPrice;
    let changedDishPriceRows = 0;
    const applyPrice = (dishId: number, currentUnitPrice: number) => {
      if (!resolveDishUnitPrice) return currentUnitPrice;
      const nextPrice = resolveDishUnitPrice(dishId, currentUnitPrice);
      if (Math.abs(nextPrice - currentUnitPrice) > 0.0001) changedDishPriceRows += 1;
      return nextPrice;
    };

    const dishes = order.dishes.map((d) => ({
      dishId: d.dishId,
      dishOutput: d.dishOutput,
      portions: d.portions,
      output: d.output,
      unitPrice: applyPrice(d.dishId, d.unitPrice),
      servingTiming: d.servingTiming ?? '',
      feastTableNumber: d.feastTableNumber ?? '',
      isMixedServe: d.isMixedServe,
      mixedGroup: d.mixedGroup ?? '',
      dishNotes: d.dishNotes ?? '',
    }));
    const children = (order.children ?? []).map((c) => ({
      dishId: c.dishId,
      dishOutput: c.dishOutput,
      portions: c.portions,
      output: c.output,
      unitPrice: applyPrice(c.dishId, c.unitPrice),
    }));
    const contractors = (order.contractors ?? []).map((c) => ({
      dishId: c.dishId,
      dishOutput: c.dishOutput,
      portions: c.portions,
      output: c.output,
      unitPrice: applyPrice(c.dishId, c.unitPrice),
    }));
    const customSections = (order.customSections ?? []).map((s) => ({
      name: s.name,
      participantCount: s.participantCount,
      serveTiming: s.serveTiming,
      dishes: s.dishes.map((d) => ({
        dishId: d.dishId,
        dishOutput: d.dishOutput,
        portions: d.portions,
        output: d.output,
        unitPrice: applyPrice(d.dishId, d.unitPrice),
      })),
    }));

    return {
      payload: {
        eventDate: order.eventDate,
        orderStatusId: options?.statusId ?? parseOrderStatusId(order.orderStatusId),
        meetingTime: order.meetingTime ?? '',
        ceremonyTime: order.ceremonyTime ?? '',
        feastTime: order.feastTime ?? '',
        customerName: order.customerName,
        customerContacts: order.customerContacts ?? '',
        guestCount: order.guestCount,
        childCount: order.childCount,
        contractorCount: order.contractorCount,
        menuCompiledBy: order.menuCompiledBy,
        customerId: null,
        administratorId: null,
        servicePercent: order.servicePercent,
        discountPercent: order.discountPercent,
        additionalEventInfo: order.additionalEventInfo ?? '',
        notes: order.notes ?? '',
        eventVenue: order.eventVenue ?? '',
        eventName: order.eventName ?? '',
        sourceWarehouseId: order.sourceWarehouseId ?? null,
        dishes,
        children,
        contractors,
        customSections,
        additionalServices: order.additionalServices?.map((s) => ({ additionalServiceId: s.additionalServiceId, quantity: s.quantity, unitPrice: s.unitPrice, unitCost: s.unitCost })) ?? [],
        serviceLaborCost: order.serviceLaborCost ?? 0,
        otherCost: order.otherCost ?? 0,
        costDataConfirmed: Boolean(order.costDataConfirmed),
      },
      changedDishPriceRows,
    };
  };

  const handleDuplicate = async (order: EventOrder) => {
    const { payload } = buildOrderUpdatePayload(order, { statusId: DEFAULT_ORDER_STATUS_ID });
    const duplicatePayload = {
      ...payload,
      eventDate: new Date().toISOString().slice(0, 10),
      eventName: payload.eventName ? `${payload.eventName} (копія)` : 'Копія замовлення',
      orderStatusId: DEFAULT_ORDER_STATUS_ID,
    };

    try {
      await dispatch(createEventOrder(duplicatePayload)).unwrap();
      await refreshOrders();
      setListMessage({ type: 'success', text: `Створено копію замовлення #${order.id} зі статусом ${getStatusLabel(DEFAULT_ORDER_STATUS_ID)}.` });
    } catch {
      setListMessage({ type: 'error', text: 'Не вдалося створити копію замовлення.' });
    }
  };

  const handleQuickStatusUpdate = async (order: EventOrder, newStatusId: number) => {
    const nextStatusId = parseOrderStatusId(newStatusId);
    setStatusSaving(true);
    setStatusProcessingIds((prev) => new Set(prev).add(order.id));
    try {
      await api.patch(`/eventorders/${order.id}/status`, { orderStatusId: nextStatusId });
      const updatedOrder = { ...order, orderStatusId: nextStatusId, orderStatusCode: getStatusCode(nextStatusId, order.orderStatusCode), orderStatus: getStatusLabel(nextStatusId, order.orderStatus) };
      if (viewOrder?.id === order.id) {
        setViewOrder(updatedOrder);
      }
      setProfitabilityByOrderId((current) => {
        const next = { ...current };
        delete next[order.id];
        return next;
      });
      await refreshOrders();
    } catch (_) {}
    setStatusProcessingIds((prev) => {
      const next = new Set(prev);
      next.delete(order.id);
      return next;
    });
    setStatusSaving(false);
    setEditingStatusId(null);
  };

  const handleRebuildWriteOffs = async () => {
    const unavailableNotice = getHistoricalWriteOffUnavailableNotice(
      operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready);
    if (!operatingGroup.isCurrent(null) || unavailableNotice) {
      setRebuildMessage(unavailableNotice || 'Робочий контекст змінився; масову перебудову не розпочато.');
      return;
    }
    if (rebuildRunning.current) return;
    if (rebuildFromDate && rebuildToDate && rebuildFromDate > rebuildToDate) {
      setRebuildMessage('Дата початку не може бути пізніше дати завершення.');
      return;
    }
    rebuildRunning.current = true;
    const controller = new AbortController();
    rebuildAbort.current = controller;
    bulkRebuildAbort.current = controller;
    setRebuildBusy(true);
    setRebuildMessage('');
    setRebuildProgress(null);
    try {
      const res = await api.get<number[]>('/eventorders/rebuild-write-offs/plan', {
        signal: controller.signal,
        params: {
          fromDate: rebuildFromDate || undefined,
          toDate: rebuildToDate || undefined,
          batchSize: 50,
        },
      });
      if (!operatingGroup.isCurrent(null)) {
        setRebuildMessage(getHistoricalWriteOffUnavailableNotice(
          operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready)
          || 'Робочий контекст змінився; масову перебудову не продовжено.');
        return;
      }
      await rebuildWriteOffsWithProgress(res.data, async orderId => {
        if (!operatingGroup.isCurrent(null)) {
          throw new Error('Робочий контекст змінився; масову перебудову зупинено.');
        }
        await api.post(`/eventorders/rebuild-write-offs/${orderId}`, null, { signal: controller.signal });
      }, setRebuildProgress);
      setRebuildMessage(res.data.length
        ? `Перерахунок завершено: ${res.data.length} замовлень.`
        : 'У вибраному діапазоні немає завершених замовлень.');
    } catch {
      setRebuildMessage(operatingGroup.isCurrent(null)
        ? 'Перерахунок зупинено через помилку. Результат поточного запиту може бути невідомим — перевірте списання цього замовлення перед повтором.'
        : getHistoricalWriteOffUnavailableNotice(operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready)
          || 'Робочий контекст змінився; подальшу перебудову зупинено. Перевірте результат останнього запиту.');
    } finally {
      setProfitabilityByOrderId({});
      setRebuildBusy(false);
      rebuildRunning.current = false;
      if (bulkRebuildAbort.current === controller) bulkRebuildAbort.current = null;
    }
    try {
      await refreshOrders();
    } catch {
      setRebuildMessage(current => `${current} Не вдалося оновити список замовлень.`);
    }
  };

  const handleRebuildSingleWriteOff = async (orderId: number) => {
    if (rebuildRunning.current) return;
    rebuildRunning.current = true;
    const controller = new AbortController();
    rebuildAbort.current = controller;
    setRebuildBusy(true);
    setRebuildMessage('');
    setRebuildProgress(null);
    try {
      await rebuildWriteOffsWithProgress([orderId], async id => {
        await api.post(`/eventorders/rebuild-write-offs/${id}`, null, { signal: controller.signal });
      }, setRebuildProgress);
      setRebuildMessage(`Перераховано замовлення #${orderId}.`);
    } catch (error: unknown) {
      setRebuildMessage(extractApiErrorMessage(error,
        `Помилка перерахунку замовлення #${orderId}. Перевірте його списання перед повтором: результат запиту може бути невідомим.`));
    } finally {
      setProfitabilityByOrderId((current) => {
        const next = { ...current };
        delete next[orderId];
        return next;
      });
      setRebuildBusy(false);
      rebuildRunning.current = false;
    }
    try {
      await refreshOrders();
    } catch {
      setRebuildMessage(current => `${current} Не вдалося оновити список замовлень.`);
    }
  };

  const handleRecalculateCreatedOrderDishPrices = async () => {
    if (orders.length === 0) {
      setListMessage({ type: 'error', text: 'Немає замовлень для перерахунку.' });
      return;
    }

    const dishPriceById = new Map<number, number>(
      dishes.map((dish) => [dish.id, Number(dish.finalPrice ?? 0)])
    );
    const resolveCurrentPrice = (dishId: number, fallback: number) => {
      const current = dishPriceById.get(dishId);
      return Number.isFinite(current) ? (current as number) : fallback;
    };
    const updates = orders.map(order => {
      const { payload, changedDishPriceRows } = buildOrderUpdatePayload(order, {
        resolveDishUnitPrice: resolveCurrentPrice,
      });
      return { orderId: order.id, payload, changedDishPriceRows };
    }).filter(order => order.changedDishPriceRows > 0);

    if (updates.length === 0) {
      setListMessage({ type: 'success', text: `Ціни вже актуальні — серед ${orders.length} завантажених замовлень змін не знайдено.` });
      return;
    }
    const rowsToUpdate = updates.reduce((sum, order) => sum + order.changedDishPriceRows, 0);
    if (!window.confirm(
      `За поточними цінами зміняться ${updates.length} з ${orders.length} завантажених бланків, ` +
      `${rowsToUpdate} позицій страв. Перерахунок зберігає нові ціни в бланках без автоматичного скасування. Продовжити?`
    )) return;

    setRecalculatingDishPrices(true);
    setListMessage(null);
    setDishPriceRecalcProgress({
      completed: 0,
      total: updates.length,
      updatedOrders: 0,
      updatedRows: 0,
      failedOrderIds: [],
    });
    let updatedOrdersCount = 0;
    let updatedRowsCount = 0;
    const failedOrderIds: number[] = [];

    try {
      for (let index = 0; index < updates.length; index += 1) {
        const update = updates[index];
        try {
          await dispatch(updateEventOrder({ id: update.orderId, data: update.payload })).unwrap();
          updatedOrdersCount += 1;
          updatedRowsCount += update.changedDishPriceRows;
        } catch {
          failedOrderIds.push(update.orderId);
        }
        setDishPriceRecalcProgress({
          completed: index + 1,
          total: updates.length,
          updatedOrders: updatedOrdersCount,
          updatedRows: updatedRowsCount,
          failedOrderIds: [...failedOrderIds],
        });
      }

      let listRefreshFailed = false;
      try {
        await refreshOrders();
      } catch {
        listRefreshFailed = true;
      }

      const updatedSummary = `Оновлено бланків: ${updatedOrdersCount}, позицій страв: ${updatedRowsCount}.`;
      const failedSummary = failedOrderIds.length
        ? ` Не вдалося оновити замовлення: ${failedOrderIds.slice(0, 8).map(id => `#${id}`).join(', ')}${failedOrderIds.length > 8 ? ` та ще ${failedOrderIds.length - 8}` : ''}.`
        : '';
      const refreshSummary = listRefreshFailed ? ' Список не оновився — перезавантажте сторінку, щоб побачити актуальні дані.' : '';
      if (failedOrderIds.length === 0 && !listRefreshFailed) {
        setListMessage({ type: 'success', text: `Перерахунок завершено. ${updatedSummary}` });
      } else {
        setListMessage({ type: 'error', text: `Перерахунок завершено з проблемами. ${updatedSummary}${failedSummary}${refreshSummary}` });
      }
    } finally {
      setRecalculatingDishPrices(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-sm)',
    padding: '7px 10px',
    width: '100%',
    boxSizing: 'border-box',
    fontSize: '14px',
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text)',
  };
  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    color: 'var(--crm-text)',
    fontSize: '13px',
  };
  const th: React.CSSProperties = {
    padding: '9px 10px',
    background: 'var(--crm-primary)',
    color: 'var(--crm-primary-contrast)',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '13px',
    whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = { padding: '6px 8px', fontSize: '13px', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-table-row-border)' };
  const parsedViewOrderNotes = viewOrder ? parseOrderNotesMetadata(viewOrder.notes || '', viewOrder.servicePercent ?? 10) : null;
  const viewOrderProfitability = viewOrder ? profitabilityByOrderId[viewOrder.id] : null;
  const viewOrderTotals = useMemo(() => {
    if (!viewOrder || !parsedViewOrderNotes) return null;
    const viewDishesSubtotal = (viewOrder.dishes ?? []).reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const viewChildrenSubtotal = (viewOrder.children ?? []).reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const viewContractorsSubtotal = (viewOrder.contractors ?? []).reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const viewCustomSectionsSubtotal = (viewOrder.customSections ?? []).reduce(
      (sum, section) => sum + (section.dishes ?? []).reduce((inner, item) => inner + (item.totalPrice || 0), 0),
      0
    );
    const viewAdditionalServicesSubtotal = (viewOrder.additionalServices ?? []).reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    return calculateOrderFinancialSummary({
      dishesSubtotal: viewDishesSubtotal,
      childrenSubtotal: viewChildrenSubtotal,
      contractorsSubtotal: viewContractorsSubtotal,
      customSectionsSubtotal: viewCustomSectionsSubtotal,
      additionalServicesSubtotal: viewAdditionalServicesSubtotal,
      mainServicePercent: parsedViewOrderNotes.financials.mainServicePercent,
      childServicePercent: parsedViewOrderNotes.financials.childServicePercent,
      contractorServicePercent: parsedViewOrderNotes.financials.contractorServicePercent,
      discountPercent: viewOrder.discountPercent || 0,
      advanceAmount: parsedViewOrderNotes.financials.advanceAmount,
    });
  }, [parsedViewOrderNotes, viewOrder]);
  const sortedOrders = useMemo(() => {
    const toNumDate = (value?: string | null) => {
      const t = new Date(value || '').getTime();
      return Number.isFinite(t) ? t : 0;
    };
    const guestTotal = (o: EventOrder) => (o.guestCount || 0) + (o.childCount || 0) + (o.contractorCount || 0);
    const getStatusSortOrder = (order: EventOrder) =>
      statusOptionsById.get(parseOrderStatusId(order.orderStatusId))?.sortOrder ?? parseOrderStatusId(order.orderStatusId);
    return [...orders].sort((a, b) => {
      switch (listSortBy) {
        case 'date-asc': return toNumDate(a.eventDate) - toNumDate(b.eventDate);
        case 'date-desc': return toNumDate(b.eventDate) - toNumDate(a.eventDate);
        case 'customer-asc': return (a.customerName || '').localeCompare(b.customerName || '', 'uk');
        case 'customer-desc': return (b.customerName || '').localeCompare(a.customerName || '', 'uk');
        case 'venue-asc': return (a.eventVenue || '').localeCompare(b.eventVenue || '', 'uk');
        case 'venue-desc': return (b.eventVenue || '').localeCompare(a.eventVenue || '', 'uk');
        case 'event-asc': return (a.eventName || '').localeCompare(b.eventName || '', 'uk');
        case 'event-desc': return (b.eventName || '').localeCompare(a.eventName || '', 'uk');
        case 'status-asc': return getStatusSortOrder(a) - getStatusSortOrder(b);
        case 'status-desc': return getStatusSortOrder(b) - getStatusSortOrder(a);
        case 'guests-asc': return guestTotal(a) - guestTotal(b);
        case 'guests-desc': return guestTotal(b) - guestTotal(a);
        case 'updated-asc': return toNumDate(a.updatedDate) - toNumDate(b.updatedDate);
        case 'updated-desc':
        default:
          return toNumDate(b.updatedDate) - toNumDate(a.updatedDate);
      }
    });
  }, [orders, listSortBy, statusOptionsById]);
  const filteredOrders = useMemo(() => {
    const normalizedCustomer = customerFilter.trim().toLowerCase();
    const normalizedVenue = venueFilter.trim().toLowerCase();
    return sortedOrders.filter((order) => {
      if (statusFilter === 'active' && parseOrderStatusId(order.orderStatusId) === 3) return false;
      if (statusFilter !== 'active' && statusFilter && String(parseOrderStatusId(order.orderStatusId)) !== statusFilter) return false;
      if (normalizedCustomer && !(order.customerName || '').toLowerCase().includes(normalizedCustomer)) return false;
      if (normalizedVenue && !(order.eventVenue || '').toLowerCase().includes(normalizedVenue)) return false;
      return true;
    });
  }, [sortedOrders, statusFilter, customerFilter, venueFilter]);
  const { totalPages: totalListPages, currentPage: currentListPage, pageItems: pageOrders } =
    paginate(filteredOrders, listPage, ORDERS_PER_PAGE);

  useEffect(() => {
    const validIds = new Set(orders.map((order) => order.id));
    setSelectedPrintOrderIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [orders]);
  const getSortArrow = (ascKey: EventOrderSort, descKey: EventOrderSort) => {
    if (listSortBy === ascKey) return ' ↑';
    if (listSortBy === descKey) return ' ↓';
    return '';
  };
  const toggleListSort = (ascKey: EventOrderSort, descKey: EventOrderSort) => {
    setListSortBy((prev) => (prev === ascKey ? descKey : ascKey));
    setListPage(1);
  };

  return (
    <div className="page-shell page-shell--wide" style={{ maxWidth: '1400px' }}>
      <style>{`@keyframes eo-pulse { 0% { transform: translateX(-100%); opacity: .45; } 50% { opacity: 1; } 100% { transform: translateX(250%); opacity: .45; } }`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>
        <h2 style={{ color: 'var(--crm-text)', margin: 0 }}>Замовлення подій</h2>
        {!isWorker && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={downloadExcelTemplate}
              style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '9px 14px', cursor: 'pointer', fontWeight: 600 }}
            >
              ↓ Шаблон Excel
            </button>
            <button
              type="button"
              onClick={openExcelImport}
              style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent)', borderRadius: 'var(--crm-radius-sm)', padding: '9px 14px', cursor: 'pointer', fontWeight: 600 }}
            >
              ↑ Імпорт Excel
            </button>
            <button
              type="button"
              onClick={handleRecalculateCreatedOrderDishPrices}
              disabled={recalculatingDishPrices || orders.length === 0}
              style={{ background: recalculatingDishPrices || orders.length === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-success-bg)', color: recalculatingDishPrices || orders.length === 0 ? 'var(--crm-text-muted)' : 'var(--crm-success)', border: `1px solid ${recalculatingDishPrices || orders.length === 0 ? 'var(--crm-border)' : 'var(--crm-success)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '9px 14px', cursor: recalculatingDishPrices || orders.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 600 }}
            >
              {recalculatingDishPrices ? 'Перерахунок...' : '↻ Перерахувати вартість страв'}
            </button>
            <button onClick={openNew} style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '9px 18px', cursor: 'pointer', fontWeight: 600 }}>+ Додати</button>
          </div>
        )}
      </div>
      {listMessage && (
        <div role={listMessage.type === 'error' ? 'alert' : 'status'} aria-live="polite" style={{ marginTop: '10px', padding: '10px 12px', borderRadius: 'var(--crm-radius-md)', fontSize: '13px', background: listMessage.type === 'success' ? 'var(--crm-success-bg)' : 'var(--crm-danger-bg)', color: listMessage.type === 'success' ? 'var(--crm-success)' : 'var(--crm-danger)', border: `1px solid ${listMessage.type === 'success' ? 'var(--crm-success)' : 'var(--crm-danger)'}` }}>
          {listMessage.text}
        </div>
      )}
      {dishPriceRecalcProgress && (
        <div role="status" aria-live="polite" style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '8px', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', fontSize: '13px', marginBottom: '6px' }}>
            <strong>{recalculatingDishPrices ? 'Оновлення цін у бланках…' : 'Результат перерахунку'}</strong>
            <span>{dishPriceRecalcProgress.completed} / {dishPriceRecalcProgress.total} · успішно: {dishPriceRecalcProgress.updatedOrders} бланків / {dishPriceRecalcProgress.updatedRows} позицій · помилок: {dishPriceRecalcProgress.failedOrderIds.length}</span>
          </div>
          <progress
            aria-label="Прогрес перерахунку цін страв"
            max={dishPriceRecalcProgress.total || 1}
            value={dishPriceRecalcProgress.completed}
            style={{ width: '100%', height: '16px', accentColor: dishPriceRecalcProgress.failedOrderIds.length ? 'var(--crm-danger)' : 'var(--crm-success)' }}
          />
        </div>
      )}
      {writeOffIssues.length > 0 && (
        <div style={{ marginTop: '10px', padding: '12px', borderRadius: 'var(--crm-radius-md)', border: '1px solid var(--crm-danger)', background: 'var(--crm-danger-bg)' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-danger)', marginBottom: '8px' }}>Проблемні завершені замовлення без списання</div>
          <div style={{ display: 'grid', gap: '6px' }}>
            {writeOffIssues.map((issue) => (
              <div key={issue.id} style={{ fontSize: '12px', color: 'var(--crm-danger)', display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <span>#{issue.id} • {issue.customerName || '—'} • {issue.eventName || '—'}</span>
                <span>{issue.eventDate ? new Date(issue.eventDate).toLocaleDateString('uk-UA') : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ marginTop: '14px' }}>Завантаження...</p>
      ) : (
        <>
        {canRunHistoricalRebuild ? (
        <div className="event-order-rebuild-toolbar" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginTop: '16px', padding: '10px 14px', background: 'var(--crm-info-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-info)', marginRight: '4px' }}>♻️ Перерахунок списань:</span>
          <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Від
            <input type="date" disabled={rebuildBusy} value={rebuildFromDate} onChange={(e) => setRebuildFromDate(e.target.value)} style={{ ...inputStyle, width: '150px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>До
            <input type="date" disabled={rebuildBusy} value={rebuildToDate} onChange={(e) => setRebuildToDate(e.target.value)} style={{ ...inputStyle, width: '150px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <button
            type="button"
            onClick={handleRebuildWriteOffs}
            disabled={rebuildBusy}
            style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', marginLeft: '4px' }}
          >
            {rebuildBusy ? '\u041f\u0435\u0440\u0435\u0440\u0430\u0445\u0443\u043d\u043e\u043a...' : '\u041f\u0435\u0440\u0435\u0440\u0430\u0445\u0443\u0432\u0430\u0442\u0438 \u0441\u043f\u0438\u0441\u0430\u043d\u043d\u044f'}
          </button>
          <div style={{ width: '100%', fontSize: '11px', color: 'var(--crm-text-muted)' }}>За один запуск — до 50 завершених замовлень, від найранішої дати.</div>
        </div>
        ) : (
          <div role="note" style={{ marginTop: '16px', padding: '10px 14px', background: 'var(--crm-info-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)' }}>
            {historicalRebuildUnavailableNotice}
            {rebuildBusy && bulkRebuildAbort.current && ' Поточну масову перебудову зупинено.'}
          </div>
        )}
          {rebuildMessage && <span style={{ fontSize: '12px', color: 'var(--crm-text)' }}>{rebuildMessage}</span>}
          {(rebuildBusy || rebuildProgress) && (
            <div role="status" aria-live="polite" style={{ width: '100%', paddingTop: '8px', color: rebuildProgress?.errors ? 'var(--crm-danger)' : 'var(--crm-info)', fontSize: '13px' }}>
              {!rebuildProgress ? 'Підготовка списку завершених замовлень…' : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    <strong>
                      {rebuildBusy && rebuildProgress.currentOrderId
                        ? `Перераховується замовлення #${rebuildProgress.currentOrderId}`
                        : rebuildProgress.errors
                          ? `Зупинено на замовленні #${rebuildProgress.currentOrderId}`
                          : rebuildBusy ? 'Підготовка наступного замовлення…' : 'Перерахунок завершено'}
                    </strong>
                    <span>Успішно: {rebuildProgress.completed} / {rebuildProgress.total} · {rebuildProgress.total ? Math.floor(rebuildProgress.completed / rebuildProgress.total * 100) : 0}% · Помилок: {rebuildProgress.errors}</span>
                  </div>
                  <progress
                    aria-label="Прогрес перерахунку списань"
                    max={rebuildProgress.total || 1}
                    value={rebuildProgress.completed}
                    style={{ width: '100%', height: '18px', accentColor: rebuildProgress.errors ? 'var(--crm-danger)' : 'var(--crm-info)' }}
                  />
                  {rebuildBusy && <div style={{ marginTop: '4px', fontSize: '12px' }}>Зачекайте, не закривайте сторінку. Прогрес оновлюється після завершення кожного замовлення.</div>}
                </>
              )}
            </div>
          )}
        <div className="event-order-list-filters" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginTop: '16px', padding: '10px 14px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-text)', marginRight: '4px' }}>🔎 Фільтри списку:</span>
          <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Статус
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setListPage(1); }} style={{ ...inputStyle, width: '170px', padding: '5px 7px', fontSize: '12px' }}>
              <option value="active">Крім завершених</option>
              <option value="">Усі статуси</option>
              {statusOptions.map((status) => <option key={status.id} value={String(status.id)}>{status.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>ПІБ замовника
            <input value={customerFilter} onChange={(e) => { setCustomerFilter(e.target.value); setListPage(1); }} placeholder="Напр. Іван" style={{ ...inputStyle, width: '190px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Місце проведення
            <input value={venueFilter} onChange={(e) => { setVenueFilter(e.target.value); setListPage(1); }} placeholder="Напр. Ресторан" style={{ ...inputStyle, width: '190px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <button
            type="button"
            onClick={() => { setStatusFilter('active'); setCustomerFilter(''); setVenueFilter(''); setListPage(1); }}
            style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', marginLeft: '4px' }}
          >
            Скинути
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', padding: '10px 14px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-text)', marginRight: '4px' }}>📄 Колонки друку:</span>
          {([['output', 'Вихід страви'], ['totalOutput', 'Заг. вихід']] as [keyof typeof printColumns, string][]).map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={printColumns[key]} onChange={() => togglePrintCol(key)} style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
              {label}
            </label>
          ))}
          <button type="button" onClick={handlePrintSelectedOrders} style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, marginLeft: '8px' }}>
            🖨 Друк обраних ({selectedPrintOrderIds.length})
          </button>
        </div>
        <div className="event-order-print-layout-controls" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginTop: '10px', padding: '10px 14px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-text)', marginRight: '4px' }}>🧩 Параметри друку:</span>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Шрифт (px)
            <input type="text" inputMode="decimal" value={printLayoutDraft.cellFontSize} onChange={(e) => setPrintLayoutDraftValue('cellFontSize', e.target.value)} style={{ ...inputStyle, width: '76px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Висота (px)
            <input type="text" inputMode="decimal" value={printLayoutDraft.cellPaddingY} onChange={(e) => setPrintLayoutDraftValue('cellPaddingY', e.target.value)} style={{ ...inputStyle, width: '92px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Відступ X (px)
            <input type="text" inputMode="decimal" value={printLayoutDraft.cellPaddingX} onChange={(e) => setPrintLayoutDraftValue('cellPaddingX', e.target.value)} style={{ ...inputStyle, width: '92px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Нотатки (%)
            <input type="text" inputMode="decimal" value={printLayoutDraft.notesColWidth} onChange={(e) => setPrintLayoutDraftValue('notesColWidth', e.target.value)} style={{ ...inputStyle, width: '82px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Порції (%)
            <input type="text" inputMode="decimal" value={printLayoutDraft.portionsColWidth} onChange={(e) => setPrintLayoutDraftValue('portionsColWidth', e.target.value)} style={{ ...inputStyle, width: '82px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Ціна (%)
            <input type="text" inputMode="decimal" value={printLayoutDraft.priceColWidth} onChange={(e) => setPrintLayoutDraftValue('priceColWidth', e.target.value)} style={{ ...inputStyle, width: '82px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Сума (%)
            <input type="text" inputMode="decimal" value={printLayoutDraft.amountColWidth} onChange={(e) => setPrintLayoutDraftValue('amountColWidth', e.target.value)} style={{ ...inputStyle, width: '82px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-field" style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Таймінг (%)
            <input type="text" inputMode="decimal" value={printLayoutDraft.timingColWidth} onChange={(e) => setPrintLayoutDraftValue('timingColWidth', e.target.value)} style={{ ...inputStyle, width: '82px', padding: '5px 7px', fontSize: '12px' }} />
          </label>
          <label className="event-order-print-layout-toggle" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--crm-text)', marginLeft: '8px', paddingBottom: '6px' }}>
            <input type="checkbox" checked={printSplitSections} onChange={(e) => setPrintSplitSections(e.target.checked)} />
            Розбивати друк по сторінках
          </label>
          <button
            type="button"
            onClick={savePrintLayoutPreset}
            style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', marginLeft: '4px' }}
          >
            Зберегти параметри
          </button>
        </div>
        <div style={{ overflowX: 'auto', marginTop: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)', borderRadius: 'var(--crm-radius-md)', overflow: 'hidden', boxShadow: 'var(--crm-shadow-sm)', minWidth: '1100px' }}>
          <thead>
            <tr>
              <th style={th}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={pageOrders.length > 0 && pageOrders.every((order) => selectedPrintOrderIds.includes(order.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPrintOrderIds((previous) => Array.from(new Set([...previous, ...pageOrders.map((order) => order.id)])));
                        return;
                      }
                      const pageIds = new Set(pageOrders.map((order) => order.id));
                      setSelectedPrintOrderIds((previous) => previous.filter((id) => !pageIds.has(id)));
                    }}
                  />
                  #
                </label>
              </th>
              <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleListSort('date-asc', 'date-desc')} title="Сортувати за датою">
                Дата проведення{getSortArrow('date-asc', 'date-desc')}
              </th>
              <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleListSort('customer-asc', 'customer-desc')} title="Сортувати за замовником">
                ПІБ замовника{getSortArrow('customer-asc', 'customer-desc')}
              </th>
              <th style={th}>Телефон</th>
              <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleListSort('venue-asc', 'venue-desc')} title="Сортувати за місцем">
                Місце проведення{getSortArrow('venue-asc', 'venue-desc')}
              </th>
              <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleListSort('event-asc', 'event-desc')} title="Сортувати за назвою заходу">
                Назва заходу{getSortArrow('event-asc', 'event-desc')}
              </th>
              <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleListSort('status-asc', 'status-desc')} title="Сортувати за статусом">
                Статус{getSortArrow('status-asc', 'status-desc')}
              </th>
              <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleListSort('guests-asc', 'guests-desc')} title="Сортувати за кількістю гостей">
                Гостей/Діти/Підрядники{getSortArrow('guests-asc', 'guests-desc')}
              </th>
              <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleListSort('updated-asc', 'updated-desc')} title="Сортувати за оновленням">
                Оновлено{getSortArrow('updated-asc', 'updated-desc')}
              </th>
              <th style={th}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {pageOrders.map((o, i) => {
              const statusStyles: Record<string, { color: string; background: string }> = {
                preliminary: { color: 'var(--crm-warning)', background: 'var(--crm-warning-bg)' },
                confirmed: { color: 'var(--crm-success)', background: 'var(--crm-success-bg)' },
                completed: { color: 'var(--crm-info)', background: 'var(--crm-info-bg)' },
                cancelled: { color: 'var(--crm-danger)', background: 'var(--crm-danger-bg)' },
              };
              const statusId = parseOrderStatusId(o.orderStatusId);
              const statusCode = getStatusCode(statusId, o.orderStatusCode);
              const statusLabel = getStatusLabel(statusId, o.orderStatus);
              const statusStyle = statusStyles[statusCode] || { color: 'var(--crm-text-muted)', background: 'var(--crm-surface-alt)' };
              const statusColor = statusStyle.color;
              return (
                <tr key={o.id} style={{ background: i % 2 === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-surface)' }}>
                  <td style={td}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedPrintOrderIds.includes(o.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedPrintOrderIds((prev) => checked ? [...prev.filter((id) => id !== o.id), o.id] : prev.filter((id) => id !== o.id));
                        }}
                      />
                      <strong>{o.id}</strong>
                    </label>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{new Date(o.eventDate).toLocaleDateString('uk-UA')}</td>
                  <td style={td}>{o.customerName || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{o.customerContacts || '—'}</td>
                  <td style={td}>{o.eventVenue || '—'}</td>
                  <td style={td}>{o.eventName || '—'}</td>
                  <td style={{ ...td, minWidth: '140px' }}>
                    {editingStatusId === o.id ? (
                      <select
                        autoFocus
                        value={String(statusId)}
                        disabled={statusSaving}
                        onChange={(e) => handleQuickStatusUpdate(o, parseOrderStatusId(e.target.value))}
                        onBlur={() => setEditingStatusId(null)}
                        style={{ border: `2px solid ${statusColor}`, borderRadius: 'var(--crm-radius-sm)', padding: '4px 6px', fontSize: '12px', fontWeight: 600, color: statusColor, background: 'var(--crm-input-bg)', cursor: 'pointer' }}
                      >
                        {statusOptions.map((status) => <option key={status.id} value={String(status.id)}>{status.name}</option>)}
                      </select>
                    ) : (
                      <div style={{ display: 'grid', gap: '4px' }}>
                        <span
                          onClick={() => !isWorker && setEditingStatusId(o.id)}
                          title={isWorker ? '' : 'Клікніть щоб змінити статус'}
                          style={{ display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 700, background: statusStyle.background, color: statusColor, border: `1px solid ${statusColor}`, cursor: isWorker ? 'default' : 'pointer' }}
                        >
                          {statusLabel}
                        </span>
                        {statusProcessingIds.has(o.id) && (
                          <div style={{ display: 'grid', gap: '3px' }}>
                            <div style={{ fontSize: '11px', color: 'var(--crm-info)', fontWeight: 600 }}>Обробляється списання…</div>
                            <div style={{ height: '4px', borderRadius: '999px', background: 'var(--crm-info-bg)', overflow: 'hidden' }}>
                              <div style={{ width: '70%', height: '100%', background: 'var(--crm-info)', borderRadius: '999px', animation: 'eo-pulse 1.2s ease-in-out infinite' }} />
                            </div>
                          </div>
                        )}
                        {o.hasHistoricalProduction && <div role="note">Маршрут і кухонні роботи — історичні; меню виправлено.</div>}
                        {o.lastWriteOffError && (
                          <div style={{ fontSize: '11px', color: 'var(--crm-danger)', fontWeight: 600, lineHeight: 1.3 }}>
                            {o.lastWriteOffError}
                            {o.lastWriteOffErrorAt ? ` • ${new Date(o.lastWriteOffErrorAt).toLocaleString('uk-UA')}` : ''}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, fontSize: '12px', fontWeight: 600 }}>{`${o.guestCount || 0}/${o.childCount || 0}/${o.contractorCount || 0}`}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '12px', color: 'var(--crm-text-muted)' }}>{new Date(o.updatedDate).toLocaleString('uk-UA', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' })}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <button onClick={() => setViewOrder(o)} title="Переглянути" style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>👁</button>
                      {!isWorker && <button onClick={() => handleDuplicate(o)} title="Копіювати" style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>📄</button>}
                      {!isWorker && <button onClick={() => openEdit(o)} style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>✏️</button>}
                      <button onClick={() => handlePrintOrder(o)} title="Друк" style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>🖨</button>
                      <button onClick={() => handleExportOrderPdf(o)} title="Експорт PDF" style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>PDF</button>
                      <button onClick={() => navigate(`/management/preparation-worksheets?orderId=${o.id}`)} title="Бланк підготовки" style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>📋</button>
                      {!isWorker && <button onClick={() => navigate(`/management/kitchen-execution?orderId=${o.id}`)} title="Виконання роботи по операціях" style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', cursor: 'pointer', fontSize: 12 }}>Виконання роботи</button>}
                      {!isWorker && <button type="button" onClick={() => void openProductionRouting(o)} title="Розподіл порцій між виробничими кухнями" style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', cursor: 'pointer', fontSize: 12 }}>Кухні</button>}
                      {!isWorker && operatingGroup.ready && operatingGroup.groupId !== 'all' && <button onClick={() => handleRebuildSingleWriteOff(o.id)} disabled={rebuildBusy} title="Перерахувати списання для цього замовлення" style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', cursor: 'pointer', fontSize: '12px' }}>♻️</button>}
                      {!isWorker && <button onClick={() => handleDelete(o.id)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', cursor: 'pointer', fontSize: '12px' }}>🗑</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredOrders.length === 0 && <tr><td colSpan={10} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>За вибраними фільтрами замовлень не знайдено</td></tr>}
          </tbody>
        </table>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '12px', flexWrap: 'wrap', fontSize: '13px' }}>
          <span>Замовлення: {filteredOrders.length} • Сторінка {currentListPage} з {totalListPages} (по {ORDERS_PER_PAGE})</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" disabled={currentListPage === 1} onClick={() => setListPage(currentListPage - 1)}>← Назад</button>
            <button type="button" disabled={currentListPage === totalListPages} onClick={() => setListPage(currentListPage + 1)}>Далі →</button>
          </div>
        </div>
        </>
      )}

      {viewOrder && (
        <div className="event-order-view-modal" style={{ position: 'fixed', inset: 0, zIndex: 1002, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 12px' }}>
          <div className="event-order-view-card" style={{ borderRadius: '12px', width: '100%', maxWidth: '960px', marginBottom: '24px' }}>
            {/* Header */}
            <div className="event-order-view-header" style={{ padding: '14px 20px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="event-order-view-title" style={{ fontWeight: 700, fontSize: '16px' }}>📋 Замовлення #{viewOrder.id}</div>
                {viewOrder.eventName && <div className="event-order-view-subtitle" style={{ fontSize: '12px', marginTop: '2px' }}>{viewOrder.eventName}</div>}
                {statusProcessingIds.has(viewOrder.id) && <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--crm-info)', fontWeight: 600 }}>Обробляється списання…</div>}
                {viewOrder.lastWriteOffError && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--crm-danger)', fontWeight: 600 }}>
                    {viewOrder.lastWriteOffError}
                    {viewOrder.lastWriteOffErrorAt ? ` • ${new Date(viewOrder.lastWriteOffErrorAt).toLocaleString('uk-UA')}` : ''}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <SendPdfToTelegramButton endpoint={`/eventorders/${viewOrder.id}/send-telegram`} label="📨 Надіслати замовлення" />
                {!isWorker && (
                  <button
                    type="button"
                    onClick={async () => { await handleDuplicate(viewOrder); closeViewOrder(); }}
                    style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                  >
                    📄 Копія
                  </button>
                )}

                {dishPreviewModalOpen && (
                  <div className="event-order-dish-preview-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2095, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div className="event-order-dish-preview-card" style={{ width: '100%', maxWidth: '660px', maxHeight: '88vh', overflowY: 'auto', background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: '10px', boxShadow: 'var(--crm-shadow-sm)', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div className="event-order-dish-preview-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>{dishPreview?.name || 'Калькуляція страви'}</h3>
                        <button type="button" onClick={closeDishPreviewModal} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1, color: 'var(--crm-text)' }}>×</button>
                      </div>
                      {dishPreviewLoading && <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Завантаження...</div>}
                      {dishPreviewError && <div style={{ fontSize: '13px', color: 'var(--crm-danger)' }}>{dishPreviewError}</div>}
                      {dishPreview && !dishPreviewLoading && !dishPreviewError && (
                        <div style={{ display: 'grid', gap: '10px', fontSize: '13px', color: 'var(--crm-text)' }}>
                          <div>Категорія: <strong>{dishPreview.categoryName ?? '—'}</strong></div>
                          <div>Час приготування: <strong>{dishPreview.cookingTime ?? '—'}</strong></div>
                          <div>Час на видачу: <strong>{dishPreview.serveTime ?? '—'}</strong></div>
                          <div>Опис: <strong>{dishPreview.description || '—'}</strong></div>
                          {Array.isArray(dishPreview.ingredients) && dishPreview.ingredients.length > 0 && (
                            <div className="event-order-dish-preview-ingredients" style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', overflow: 'hidden', marginTop: '2px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                  <tr>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>#</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Інгредієнт</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Брутто</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Нетто</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Вихід</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dishPreview.ingredients.map((item: any, index: number) => (
                                    <tr key={`${item.name || 'ingredient'}-${index}`} style={{ borderTop: '1px solid var(--crm-border)' }}>
                                      <td style={{ padding: '6px 8px' }}>{index + 1}</td>
                                      <td style={{ padding: '6px 8px', fontWeight: 500 }}>{item.productName || item.semiFinishedProductName || item.subSemiFinishedProductName || item.name || '—'}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(item.bruttoWeight ?? item.brutto ?? 0).toFixed(4)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(item.nettoWeight ?? item.netto ?? 0).toFixed(4)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(item.output ?? 0).toFixed(4)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <button type="button" onClick={() => handlePrintOrder(viewOrder)} style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>🖨 Друк</button>
                <button type="button" onClick={() => { setViewOrder(null); navigate(`/management/preparation-worksheets?orderId=${viewOrder.id}`); }} style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>📋 Бланк</button>
                {!isWorker && <button type="button" onClick={() => { setViewOrder(null); navigate(`/management/kitchen-execution?orderId=${viewOrder.id}`); }} style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 14px', cursor: 'pointer' }}>Виконання роботи</button>}
                <button type="button" onClick={closeViewOrder} aria-label="Закрити перегляд замовлення" title="Закрити" style={{ ...modalCloseButtonStyle, color: 'var(--crm-paper-header-text)', fontSize: '24px' }}>×</button>
              </div>
            </div>
            <div className="event-order-view-body" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {viewOrder.lastWriteOffError && (
                <div className="event-order-view-alert">
                  <div style={{ fontWeight: 700, marginBottom: '6px' }}>Проблема списання</div>
                  <div style={{ fontSize: '13px', lineHeight: 1.4 }}>{viewOrder.lastWriteOffError}</div>
                  {(writeOffErrorSummary?.section || writeOffErrorSummary?.dish || writeOffErrorSummary?.ingredient) && (
                    <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.5 }}>
                      {writeOffErrorSummary.section && <div><strong>Розділ:</strong> {writeOffErrorSummary.section}</div>}
                      {writeOffErrorSummary.dish && <div><strong>Страва:</strong> {writeOffErrorSummary.dish}</div>}
                      {writeOffErrorSummary.ingredient && <div><strong>Інгредієнт:</strong> {writeOffErrorSummary.ingredient}</div>}
                    </div>
                  )}
                  {viewOrder.lastWriteOffErrorAt && (
                    <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: 600 }}>
                      Остання спроба: {new Date(viewOrder.lastWriteOffErrorAt).toLocaleString('uk-UA')}
                    </div>
                  )}
                </div>
              )}
              <div className="event-readiness-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 700 }}>Готовність події</div>
                  {eventReadiness && (
                    <span className={`event-readiness-pill event-readiness-${eventReadiness.overallStatus === 'ready' ? 'ready' : eventReadiness.overallStatus === 'warning' ? 'warning' : 'blocked'}`}>
                      {eventReadiness.overallStatus === 'ready' ? 'Готово' : eventReadiness.overallStatus === 'warning' ? 'Потребує уваги' : 'Заблоковано'}
                    </span>
                  )}
                </div>
                {eventReadinessLoading && <div className="event-readiness-muted" style={{ fontSize: '13px' }}>Завантаження...</div>}
                {eventReadinessError && <div className="event-readiness-blocked-text" style={{ fontSize: '13px' }}>{eventReadinessError}</div>}
                {eventReadiness && !eventReadinessLoading && (
                  <div style={{ display: 'grid', gap: '6px' }}>
                    {eventReadiness.checks.map((check) => (
                      <div key={check.code} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '13px' }}>
                        <span className={`event-readiness-icon event-readiness-${check.status === 'ready' ? 'ready' : check.status === 'warning' ? 'warning' : 'blocked'}`}>
                          {check.status === 'ready' ? '✓' : check.status === 'warning' ? '!' : '×'}
                        </span>
                        <strong>{check.label}:</strong>
                        <span>{check.message}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(eventProductionPlanLoading || eventProductionPlan) && (
                  <div className="event-plan-block">
                    <button
                      type="button"
                      className="event-plan-toggle"
                      aria-expanded={expandedPlanSections.plan}
                      onClick={() => setExpandedPlanSections((prev) => ({ ...prev, plan: !prev.plan }))}
                    >
                      <span className={`event-plan-chevron${expandedPlanSections.plan ? ' open' : ''}`}>▸</span>
                      <span>План Н/Ф та сировини</span>
                      {eventProductionPlan && !eventProductionPlanLoading && (
                        <span className="event-plan-count">
                          {eventProductionPlan.semiFinishedStockConsumption.length + eventProductionPlan.rawIngredientDemand.length}
                        </span>
                      )}
                    </button>
                    <div className={`event-plan-collapse${expandedPlanSections.plan ? ' open' : ''}`}>
                      <div className="event-plan-collapse-inner">
                        {eventProductionPlanLoading && <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)', paddingTop: '6px' }}>Розрахунок...</div>}
                        {eventProductionPlan && !eventProductionPlanLoading && (
                          <>
                            {([
                              { key: 'semi' as const, title: 'Н/Ф зі складу', items: eventProductionPlan.semiFinishedStockConsumption },
                              { key: 'raw' as const, title: 'Сировина для дефіциту Н/Ф', items: eventProductionPlan.rawIngredientDemand },
                            ]).filter((group) => group.items.length > 0).map((group) => (
                              <div key={group.key} className="event-plan-group">
                                <button
                                  type="button"
                                  className="event-plan-toggle event-plan-toggle-sub"
                                  aria-expanded={expandedPlanSections[group.key]}
                                  onClick={() => setExpandedPlanSections((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                                >
                                  <span className={`event-plan-chevron${expandedPlanSections[group.key] ? ' open' : ''}`}>▸</span>
                                  <span>{group.title}</span>
                                  <span className="event-plan-count">{group.items.length}</span>
                                </button>
                                <div className={`event-plan-collapse${expandedPlanSections[group.key] ? ' open' : ''}`}>
                                  <div className="event-plan-collapse-inner">
                                    <ul className="event-plan-list">
                                      {group.items.map((item, idx) => (
                                        <li key={`${item.name}-${idx}`}>
                                          <span>{item.name}</span>
                                          <strong>{item.quantity} {item.weightType}</strong>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {eventProductionPlan.semiFinishedStockConsumption.length === 0 && eventProductionPlan.rawIngredientDemand.length === 0 && (
                              <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)', paddingTop: '6px' }}>Потреба не визначена.</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Meta grid */}
              <div className="event-order-view-meta-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px' }}>
                {[
                  ['Дата події', viewOrder.eventDate ? new Date(viewOrder.eventDate).toLocaleDateString('uk-UA') : '—'],
                  ['Статус', getStatusLabel(viewOrder.orderStatusId, viewOrder.orderStatus)],
                  ['Замовник', viewOrder.customerName || '—'],
                  ['Контакти', viewOrder.customerContacts || '—'],
                  ['Місце', viewOrder.eventVenue || '—'],
                  ['Гості / Діти / Підрядники', `${viewOrder.guestCount || 0} / ${viewOrder.childCount || 0} / ${viewOrder.contractorCount || 0}`],
                  ['Зустріч', viewOrder.meetingTime || '—'],
                  ['Церемонія', viewOrder.ceremonyTime || '—'],
                  ['Застілля (час)', viewOrder.feastTime || '—'],
                  ['Меню склав', viewOrder.menuCompiledBy || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="event-order-view-meta-card" style={{ borderRadius: '8px', padding: '8px 10px', fontSize: '12px' }}>
                    <div className="event-order-view-meta-label" style={{ marginBottom: '2px' }}>{label}</div>
                    <div className="event-order-view-meta-value" style={{ fontWeight: 600 }}>{value}</div>
                  </div>
                ))}
              </div>
              {/* Dish sections */}
              {[
                { title: 'Основне меню', items: viewOrder.dishes ?? [] },
                { title: 'Діти', items: viewOrder.children ?? [] },
                { title: 'Підрядники', items: viewOrder.contractors ?? [] },
              ].filter((s) => s.items.length > 0).map((section) => (
                <div key={section.title} className="event-order-view-section" style={writeOffErrorSummary?.section === section.title ? { border: '2px solid var(--ev-danger)', borderRadius: '8px', padding: '10px', background: 'var(--crm-danger-bg)' } : undefined}>
                  <div className="event-order-view-section-title" style={{ fontWeight: 700, fontSize: '14px', borderLeft: '4px solid var(--crm-info)', paddingLeft: '10px', marginBottom: '8px' }}>{section.title}</div>
                  {section.title !== 'Основне меню' && (
                    (() => {
                      const sectionMetaParts = [
                        section.title === 'Діти' && (viewOrder.childCount || 0) > 0 ? `Кількість: ${viewOrder.childCount}` : '',
                        section.title === 'Підрядники' && (viewOrder.contractorCount || 0) > 0 ? `Кількість: ${viewOrder.contractorCount}` : '',
                        section.title === 'Діти' && parsedViewOrderNotes?.sectionMeta.childServeTiming ? `Таймінг: ${parsedViewOrderNotes.sectionMeta.childServeTiming}` : '',
                        section.title === 'Підрядники' && parsedViewOrderNotes?.sectionMeta.contractorServeTiming ? `Таймінг: ${parsedViewOrderNotes.sectionMeta.contractorServeTiming}` : '',
                      ].filter(Boolean).join(' • ');
                      return sectionMetaParts ? <div className="event-order-view-section-subline" style={{ fontSize: '12px', marginBottom: '6px' }}>{sectionMetaParts}</div> : null;
                    })()
                  )}
                  <div className="event-order-view-table-wrap">
                    <table className="event-order-view-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '6px 8px', textAlign: 'left' }}>#</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left' }}>Страва</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Порції</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ціна</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Собівартість од.</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Сума</th>
                          {(section.items as any[]).some((d) => d.servingTiming) && <th style={{ padding: '6px 8px' }}>Таймінг</th>}
                          {(section.items as any[]).some((d) => d.feastTableNumber) && <th style={{ padding: '6px 8px' }}>Застілля</th>}
                          {(section.items as any[]).some((d) => d.dishNotes) && <th style={{ padding: '6px 8px' }}>Нотатки</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(section.items as any[]).map((d, i) => (
                          <tr key={i} style={writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? { background: 'var(--crm-danger-bg)' } : undefined}>
                            <td style={{ padding: '6px 8px', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 700 : 400 }}>{i + 1}</td>
                            <td style={{ padding: '6px 8px', fontWeight: 500, color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined }}>
                              {d.dishName ? (
                                <button
                                  type="button"
                                  onClick={() => openDishPreviewModal((d as any).dishId)}
                                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--ev-info)', cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}
                                >
                                  {formatVisibleDishName((d as any).dishId, d.dishName)}
                                </button>
                              ) : '—'}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 700 : 400 }}>{(d.portions || 0).toFixed(2)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 700 : 400 }}>{Math.round(d.unitPrice || 0)}</td>
                            <td className="event-order-view-table-sum" style={{ padding: '6px 8px', textAlign: 'right', fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 800 : 700, color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined }}>{(d.totalPrice || 0).toFixed(2)}</td>
                            {(section.items as any[]).some((x) => x.servingTiming) && <td style={{ padding: '6px 8px', fontSize: '12px', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 600 : 400 }}>{d.servingTiming || '—'}</td>}
                            {(section.items as any[]).some((x) => x.feastTableNumber) && <td style={{ padding: '6px 8px', fontSize: '12px', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 600 : 400 }}>{d.feastTableNumber || '—'}</td>}
                            {(section.items as any[]).some((x) => x.dishNotes) && <td style={{ padding: '6px 8px', fontSize: '12px', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 600 : 400 }}>{d.dishNotes || '—'}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {/* Custom sections */}
              {(viewOrder.customSections ?? []).filter((s) => s.dishes?.length > 0).map((section, si) => (
                <div key={si} className="event-order-view-section" style={writeOffErrorSummary?.section === (section.name || '').trim() ? { border: '2px solid var(--ev-danger)', borderRadius: '8px', padding: '10px', background: 'var(--crm-danger-bg)' } : undefined}>
                  <div className="event-order-view-section-title" style={{ fontWeight: 700, fontSize: '14px', borderLeft: '4px solid var(--crm-accent)', paddingLeft: '10px', marginBottom: '4px' }}>{(section.name || '').trim() || 'Додаткова вкладка'}</div>
                  {(() => {
                    const sectionMetaParts = [
                      (section.participantCount || 0) > 0 ? `Кількість: ${section.participantCount}` : '',
                      section.serveTiming ? `Таймінг: ${section.serveTiming}` : '',
                    ].filter(Boolean).join(' • ');
                    return sectionMetaParts ? <div className="event-order-view-section-subline" style={{ fontSize: '12px', marginBottom: '6px' }}>{sectionMetaParts}</div> : null;
                  })()}
                  <div className="event-order-view-table-wrap">
                    <table className="event-order-view-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '6px 8px' }}>#</th><th style={{ padding: '6px 8px' }}>Страва</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Порції</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ціна</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Сума</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(section.dishes ?? []).map((d, i) => (
                          <tr key={i} style={writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? { background: 'var(--crm-danger-bg)' } : undefined}>
                            <td style={{ padding: '6px 8px', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 700 : 400 }}>{i + 1}</td>
                            <td style={{ padding: '6px 8px', fontWeight: 500, color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined }}>
                              {d.dishName ? (
                                <button
                                  type="button"
                                  onClick={() => openDishPreviewModal((d as any).dishId)}
                                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--ev-info)', cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}
                                >
                                  {formatVisibleDishName((d as any).dishId, d.dishName)}
                                </button>
                              ) : '—'}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 700 : 400 }}>{(d.portions || 0).toFixed(2)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined, fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 700 : 400 }}>{Math.round(d.unitPrice || 0)}</td>
                            <td className="event-order-view-table-sum" style={{ padding: '6px 8px', textAlign: 'right', fontWeight: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 800 : 700, color: writeOffErrorSummary?.dish && d.dishName && d.dishName.includes(writeOffErrorSummary.dish) ? 'var(--ev-danger)' : undefined }}>{(d.totalPrice || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {/* Additional services */}
              {(viewOrder.additionalServices ?? []).length > 0 && (
                <div className="event-order-view-section">
                  <div className="event-order-view-section-title" style={{ fontWeight: 700, fontSize: '14px', borderLeft: '4px solid var(--crm-warning)', paddingLeft: '10px', marginBottom: '8px' }}>Додаткові послуги</div>
                  <div className="event-order-view-table-wrap">
                    <table className="event-order-view-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '6px 8px' }}>#</th><th style={{ padding: '6px 8px' }}>Послуга</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>К-сть</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ціна</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Сума</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewOrder.additionalServices ?? []).map((s, i) => (
                          <tr key={i} style={viewOrder.lastWriteOffError ? { background: 'rgba(231, 76, 60, 0.16)' } : undefined}>
                            <td style={{ padding: '6px 8px', color: 'var(--ev-danger)', fontWeight: 700 }}>{i + 1}</td>
                            <td style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--ev-danger)' }}>{s.serviceName || '—'}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--ev-danger)', fontWeight: 700 }}>{(s.quantity || 0).toFixed(2)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--ev-danger)', fontWeight: 700 }}>{(s.unitPrice || 0).toFixed(2)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--ev-warning)', fontWeight: 700 }}>{(s.unitCost || 0).toFixed(2)}</td>
                            <td className="event-order-view-table-sum" style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: 'var(--ev-danger)' }}>{(s.totalPrice || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <EventStaffingPanel
                eventOrderId={viewOrder.id}
                eventDate={viewOrder.eventDate}
                canEdit={!isWorker}
                onLaborCostChanged={(laborCost) => {
                  setViewOrder(current => current?.id === viewOrder.id
                    ? { ...current, serviceLaborCost: laborCost, costDataConfirmed: false, updatedDate: new Date().toISOString() }
                    : current);
                }}
              />
              {/* Profitability / P&L */}
              <div className="event-order-view-section event-themed-panel">
                <div className="event-order-view-section-title" style={{ fontWeight: 700, fontSize: '14px', borderLeft: '4px solid var(--crm-success)', paddingLeft: '10px', marginBottom: '8px' }}>
                  P&amp;L / Прибутковість
                </div>
                {profitabilityLoadingId === viewOrder.id && (
                  <div style={{ fontSize: '13px', color: 'var(--crm-paper-muted)' }}>Завантаження розрахунку прибутковості…</div>
                )}
                {profitabilityError && (
                  <div style={{ fontSize: '13px', color: 'var(--ev-danger)', fontWeight: 600 }}>{profitabilityError}</div>
                )}
                {viewOrderProfitability && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', marginBottom: '10px' }}>
                      {[
                        ['Виторг', formatProfitabilityMoney(viewOrderProfitability.revenue.totalRevenue), 'var(--ev-info)'],
                        ['Собівартість', formatProfitabilityMoney(viewOrderProfitability.totalCost), 'var(--ev-warning)'],
                        [viewOrderProfitability.hasCompleteCostData ? 'Валовий прибуток' : 'Розрахунковий прибуток', formatProfitabilityMoney(viewOrderProfitability.grossProfit), viewOrderProfitability.grossProfit >= 0 ? 'var(--ev-success)' : 'var(--ev-danger)'],
                        [viewOrderProfitability.hasCompleteCostData ? 'Маржа' : 'Розрахункова маржа', formatProfitabilityMargin(viewOrderProfitability.marginPercent), viewOrderProfitability.grossProfit >= 0 ? 'var(--ev-success)' : 'var(--ev-danger)'],
                      ].map(([label, value, color]) => (
                        <div key={label} className="event-themed-card">
                          <div style={{ fontSize: '11px', color: 'var(--crm-paper-muted)', marginBottom: '3px' }}>{label}</div>
                          <div style={{ fontSize: '15px', fontWeight: 800, color }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--crm-paper-muted)', marginBottom: '8px' }}>
                      База витрат: <strong>{getProfitabilityBasisLabel(viewOrderProfitability.costBasis)}</strong>. {viewOrderProfitability.costBasisDescription}
                    </div>
                    {!viewOrderProfitability.hasCompleteCostData && (
                      <div className="event-themed-note event-themed-note-warning">
                        Розрахунок попередній: збережені витрати включено до підсумку, але відповідальний користувач ще не підтвердив перевірку всіх категорій.
                      </div>
                    )}
                    {viewOrderProfitability.hasCompleteCostData && (
                      <div className="event-themed-note event-themed-note-success">
                        Дані витрат підтверджено відповідальним користувачем.
                      </div>
                    )}
                    <div className="event-order-view-table-wrap">
                      <table className="event-order-view-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Категорія</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Сума</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Джерело</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>Планова собівартість меню</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{formatProfitabilityMoney(viewOrderProfitability.plannedFoodCost)}</td>
                            <td style={{ padding: '6px 8px' }}>Собівартість страв × порції</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>Оцінка складських списань</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{viewOrderProfitability.hasInventoryWriteOffEstimate ? formatProfitabilityMoney(viewOrderProfitability.estimatedInventoryWriteOffCost) : '—'}</td>
                            <td style={{ padding: '6px 8px' }}>{viewOrderProfitability.hasInventoryWriteOffEstimate ? 'Фактична кількість × поточна собівартість' : 'Списання ще не знайдено'}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>Обслуговування / робота</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{formatProfitabilityMoney(viewOrderProfitability.serviceCost.amount)}{!viewOrderProfitability.serviceCost.isAvailable ? ' *' : ''}</td>
                            <td style={{ padding: '6px 8px' }}>{viewOrderProfitability.serviceCost.description}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>Додаткові послуги</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{formatProfitabilityMoney(viewOrderProfitability.additionalServiceCost.amount)}{!viewOrderProfitability.additionalServiceCost.isAvailable ? ' *' : ''}</td>
                            <td style={{ padding: '6px 8px' }}>{viewOrderProfitability.additionalServiceCost.description}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>Інші витрати</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{formatProfitabilityMoney(viewOrderProfitability.otherCost.amount)}{!viewOrderProfitability.otherCost.isAvailable ? ' *' : ''}</td>
                            <td style={{ padding: '6px 8px' }}>{viewOrderProfitability.otherCost.description}</td>
                          </tr>
                          <tr style={{ borderTop: '2px solid var(--crm-paper-border)' }}>
                            <td style={{ padding: '7px 8px', fontWeight: 800 }}>Загальна собівартість</td>
                            <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 800 }}>{formatProfitabilityMoney(viewOrderProfitability.totalCost)}</td>
                            <td style={{ padding: '7px 8px' }}>{getProfitabilityStatusLabel(viewOrderProfitability.hasCompleteCostData)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              {!isWorker && (
                <div style={{ marginTop: '14px', border: `1px solid ${receivable?.isOverdue ? 'var(--crm-danger)' : 'var(--crm-border)'}`, borderRadius: '8px', padding: '14px', background: receivable?.isOverdue ? 'var(--crm-danger-bg)' : 'var(--crm-surface)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <strong style={{ color: 'var(--crm-text)' }}>Розрахунки з клієнтом</strong>
                      {receivable?.acceptedProposalNumber && <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--crm-accent)' }}>за {receivable.acceptedProposalNumber}</span>}
                    </div>
                    {receivableLoading && <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>Завантаження…</span>}
                  </div>
                  {receivable && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                        <div style={{ padding: '9px', borderRadius: '6px', background: 'var(--crm-info-bg)' }}><small>Сума договору</small><div style={{ fontWeight: 800 }}>{receivable.contractAmount.toFixed(2)} грн</div></div>
                        <div style={{ padding: '9px', borderRadius: '6px', background: 'var(--crm-success-bg)' }}><small>Отримано</small><div style={{ fontWeight: 800, color: 'var(--crm-success)' }}>{receivable.paidAmount.toFixed(2)} грн</div></div>
                        <div style={{ padding: '9px', borderRadius: '6px', background: receivable.isOverdue ? 'var(--crm-danger-bg)' : receivable.outstandingAmount > 0 ? 'var(--crm-warning-bg)' : 'var(--crm-success-bg)' }}><small>{receivable.isOverdue ? 'Прострочено' : 'Залишок'}</small><div style={{ fontWeight: 800, color: receivable.isOverdue ? 'var(--crm-danger)' : receivable.outstandingAmount > 0 ? 'var(--crm-warning)' : 'var(--crm-success)' }}>{receivable.outstandingAmount.toFixed(2)} грн</div></div>
                        <div style={{ padding: '9px', borderRadius: '6px', background: 'var(--crm-surface-alt)' }}><small>Строк оплати</small><div style={{ fontWeight: 800 }}>{new Date(receivable.dueDate).toLocaleDateString('uk-UA')}</div></div>
                      </div>
                      {receivable.outstandingAmount > 0 && (
                        <div className="event-order-customer-payment-form" style={{ display: 'grid', gridTemplateColumns: '145px 170px 160px 1fr auto', gap: '7px', marginBottom: '12px' }}>
                          <input aria-label="Дата платежу" type="date" value={paymentDate} onChange={event => setPaymentDate(event.target.value)} style={{ padding: '7px', border: '1px solid var(--crm-border)', borderRadius: '5px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                          <input aria-label="Сума платежу" type="number" min="0.01" max={receivable.outstandingAmount} step="0.01" value={paymentAmount} onChange={event => setPaymentAmount(event.target.value)} placeholder={`до ${receivable.outstandingAmount.toFixed(2)}`} style={{ padding: '7px', border: '1px solid var(--crm-border)', borderRadius: '5px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                          <select aria-label="Спосіб оплати" value={paymentMethod} onChange={event => setPaymentMethod(event.target.value)} style={{ padding: '7px', border: '1px solid var(--crm-border)', borderRadius: '5px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}>
                            <option>Банківський переказ</option>
                            <option>Картка</option>
                            <option>Готівка</option>
                            <option>Інше</option>
                          </select>
                          <input aria-label="Номер або референс платежу" value={paymentReference} onChange={event => setPaymentReference(event.target.value)} placeholder="Номер/референс" maxLength={100} style={{ padding: '7px', border: '1px solid var(--crm-border)', borderRadius: '5px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                          <button type="button" disabled={paymentSaving || !paymentAmount} onClick={addCustomerPayment} style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: '5px', padding: '7px 11px', cursor: 'pointer' }}>Додати платіж</button>
                        </div>
                      )}
                      {paymentMessage && <div style={{ fontSize: '12px', marginBottom: '8px', color: paymentMessage.startsWith('Не ') || paymentMessage.startsWith('Вкажіть') ? 'var(--crm-danger)' : 'var(--crm-info)' }}>{paymentMessage}</div>}
                      <div style={{ display: 'grid', gap: '6px' }}>
                        {receivable.payments.map(payment => (
                          <div key={payment.id} className="event-order-payment-history-row" style={{ display: 'grid', gridTemplateColumns: '110px 110px 1fr auto', gap: '9px', alignItems: 'center', padding: '7px 9px', border: '1px solid var(--crm-border)', borderRadius: '6px', background: 'var(--crm-surface-alt)', opacity: payment.isReversed ? .62 : 1, textDecoration: payment.isReversed ? 'line-through' : 'none', fontSize: '12px' }}>
                            <span>{new Date(payment.paymentDate).toLocaleDateString('uk-UA')}</span>
                            <strong>{payment.amount.toFixed(2)} грн</strong>
                            <span>{payment.method}{payment.reference ? ` · ${payment.reference}` : ''}{payment.proposalNumber ? ` · ${payment.proposalNumber}` : ''}{payment.isReversed ? ` · Сторновано: ${payment.reversalReason}` : ''}</span>
                            {!payment.isReversed && <button type="button" disabled={paymentSaving} onClick={() => reverseCustomerPayment(payment.id)} style={{ padding: '4px 7px', color: 'var(--crm-danger)' }}>Сторнувати</button>}
                          </div>
                        ))}
                        {receivable.payments.length === 0 && <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>Фактичних надходжень ще немає.</div>}
                      </div>
                    </>
                  )}
                </div>
              )}
              {!isWorker && (
                <div style={{ marginTop: '14px', border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '14px', background: 'var(--crm-surface)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                    <strong style={{ color: 'var(--crm-text)' }}>Комерційні пропозиції</strong>
                    {proposalLoading && <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>Завантаження…</span>}
                  </div>
                  <div className="event-order-proposal-form" style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 190px) 1fr auto', gap: '8px', marginBottom: '12px' }}>
                    <input aria-label="Дійсна до" type="date" value={proposalValidUntil} onChange={event => setProposalValidUntil(event.target.value)} style={{ padding: '7px', border: '1px solid var(--crm-border)', borderRadius: '5px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                    <input aria-label="Примітка для клієнта" value={proposalNotes} onChange={event => setProposalNotes(event.target.value)} placeholder="Примітка для клієнта" maxLength={1000} style={{ padding: '7px', border: '1px solid var(--crm-border)', borderRadius: '5px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                    <button type="button" disabled={proposalSaving || !proposalValidUntil} onClick={createCommercialProposal} style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: '5px', padding: '7px 12px', cursor: 'pointer' }}>
                      Нова версія
                    </button>
                  </div>
                  {proposalMessage && <div style={{ fontSize: '12px', marginBottom: '8px', color: proposalMessage.startsWith('Не ') ? 'var(--crm-danger)' : 'var(--crm-info)' }}>{proposalMessage}</div>}
                  <div style={{ display: 'grid', gap: '7px' }}>
                    {commercialProposals.map(proposal => {
                      const isExpired = proposal.validUntil.slice(0, 10) < new Date().toISOString().slice(0, 10)
                        && (proposal.status === 'Draft' || proposal.status === 'Sent');
                      return (
                      <div key={proposal.id} className="event-order-proposal-row" style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', alignItems: 'center', padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: '6px' }}>
                        <div>
                          <strong>{proposal.proposalNumber}</strong>
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                            до {new Date(proposal.validUntil).toLocaleDateString('uk-UA')} · {proposal.totalPrice.toFixed(2)} грн
                          </span>
                          {proposal.sentAtUtc && <div style={{ marginTop: '3px', fontSize: '11px', color: 'var(--crm-text-muted)' }}>Надіслано {new Date(proposal.sentAtUtc).toLocaleString('uk-UA')} · {proposal.sentByUsername || 'користувач'}</div>}
                          {proposal.decisionReason && <div style={{ marginTop: '3px', fontSize: '12px', color: 'var(--crm-danger)' }}>{proposal.decisionReason}</div>}
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: isExpired ? 'var(--crm-danger)' : 'var(--crm-text)' }}>
                          {isExpired ? 'Строк минув' : { Draft: 'Чернетка', Sent: 'Надіслано', Accepted: 'Прийнято', Rejected: 'Відхилено', Superseded: 'Замінено' }[proposal.status]}
                        </span>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button type="button" disabled={proposalDetailLoading} onClick={() => openCommercialProposal(proposal)} style={{ padding: '5px 8px' }}>Перегляд</button>
                          {proposal.status === 'Draft' && <button type="button" disabled={proposalSaving || isExpired} onClick={() => transitionCommercialProposal(proposal, 'Sent')} style={{ padding: '5px 8px' }}>Надіслати</button>}
                          {proposal.status === 'Sent' && <button type="button" disabled={proposalSaving || isExpired} onClick={() => transitionCommercialProposal(proposal, 'Accepted')} style={{ padding: '5px 8px', color: 'var(--crm-success)' }}>Прийняти</button>}
                          {(proposal.status === 'Draft' || proposal.status === 'Sent') && <button type="button" disabled={proposalSaving} onClick={() => transitionCommercialProposal(proposal, 'Rejected')} style={{ padding: '5px 8px', color: 'var(--crm-danger)' }}>Відхилити</button>}
                        </div>
                      </div>
                    )})}
                    {!proposalLoading && commercialProposals.length === 0 && <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>Версій ще немає. Нова версія зафіксує поточний склад і суму замовлення.</div>}
                  </div>
                </div>
              )}
              {/* Totals */}
              <div className="event-order-view-totals" style={{ borderRadius: '8px', padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
                {(viewOrderTotals?.subtotal ?? 0) > 0 && <div style={{ fontSize: '13px' }}>Проміжна сума: <strong>{(viewOrderTotals?.subtotal ?? 0).toFixed(2)} грн</strong></div>}
                {(viewOrderTotals?.mainServiceAmount ?? 0) > 0 && <div style={{ fontSize: '13px' }}>Обслуговування основного ({(parsedViewOrderNotes?.financials.mainServicePercent ?? 0).toFixed(1)}%): <strong>+{(viewOrderTotals?.mainServiceAmount ?? 0).toFixed(2)} грн</strong></div>}
                {(viewOrderTotals?.childServiceAmount ?? 0) > 0 && <div style={{ fontSize: '13px' }}>Обслуговування дітей ({(parsedViewOrderNotes?.financials.childServicePercent ?? 0).toFixed(1)}%): <strong>+{(viewOrderTotals?.childServiceAmount ?? 0).toFixed(2)} грн</strong></div>}
                {(viewOrderTotals?.contractorServiceAmount ?? 0) > 0 && <div style={{ fontSize: '13px' }}>Обслуговування підрядників ({(parsedViewOrderNotes?.financials.contractorServicePercent ?? 0).toFixed(1)}%): <strong>+{(viewOrderTotals?.contractorServiceAmount ?? 0).toFixed(2)} грн</strong></div>}
                {(viewOrderTotals?.discountAmount ?? 0) > 0 && <div style={{ fontSize: '13px' }}>Знижка ({(viewOrder.discountPercent ?? 0).toFixed(1)}%): <strong>-{(viewOrderTotals?.discountAmount ?? 0).toFixed(2)} грн</strong></div>}
                {(parsedViewOrderNotes?.financials.advanceAmount ?? 0) > 0 && <div style={{ fontSize: '13px' }}>Аванс: <strong>-{(parsedViewOrderNotes?.financials.advanceAmount ?? 0).toFixed(2)} грн</strong></div>}
                <div className="event-order-view-total-line" style={{ gridColumn: '1/-1', paddingTop: '8px', marginTop: '4px', fontSize: '17px', fontWeight: 700, textAlign: 'right' }}>
                  Разом до сплати: {(viewOrderTotals?.total ?? viewOrder.totalPrice ?? 0).toFixed(2)} грн
                </div>
                {(parsedViewOrderNotes?.financials.advanceAmount ?? 0) > 0 && (
                  <div className="event-order-view-balance-line" style={{ gridColumn: '1/-1', fontSize: '14px', fontWeight: 700, textAlign: 'right', color: (viewOrderTotals?.balanceDue ?? 0) > 0 ? 'var(--ev-warning)' : 'var(--ev-success)' }}>
                    Залишок після авансу: {(viewOrderTotals?.balanceDue ?? 0).toFixed(2)} грн
                  </div>
                )}
              </div>
              {(viewOrder.additionalEventInfo || parsedViewOrderNotes?.cleanNotes) && (
                <div className="event-order-view-notes" style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '13px' }}>
                  {viewOrder.additionalEventInfo && <><strong>Додаткові відомості:</strong><div style={{ marginTop: '4px' }}>{viewOrder.additionalEventInfo}</div></>}
                  {parsedViewOrderNotes?.cleanNotes && <><strong style={{ display: 'block', marginTop: viewOrder.additionalEventInfo ? '8px' : 0 }}>Примітки:</strong><div style={{ marginTop: '4px' }}>{parsedViewOrderNotes.cleanNotes}</div></>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {proposalDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 2200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 14px' }}>
          <div style={{ width: '100%', maxWidth: '900px', background: 'var(--crm-surface)', color: 'var(--crm-text)', borderRadius: '10px', boxShadow: 'var(--crm-shadow-sm)', overflow: 'hidden' }}>
            <div style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ fontSize: '16px' }}>Комерційна пропозиція {proposalDetail.proposal.proposalNumber}</strong>
                <div style={{ fontSize: '12px', opacity: .9 }}>{proposalDetail.snapshot.eventName || 'Подія'} · snapshot від {new Date(proposalDetail.proposal.createdAtUtc).toLocaleString('uk-UA')}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button type="button" onClick={() => openPrintWindow(buildCommercialProposalPrintHtml(proposalDetail))} style={{ background: 'var(--crm-surface)', color: 'var(--crm-primary)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 11px', cursor: 'pointer', fontWeight: 700 }}>Друк</button>
                <button type="button" onClick={() => setProposalDetail(null)} aria-label="Закрити комерційну пропозицію" title="Закрити" style={{ ...modalCloseButtonStyle, color: 'var(--crm-primary-contrast)', fontSize: '24px' }}>×</button>
              </div>
            </div>
            <div style={{ padding: '18px', display: 'grid', gap: '14px' }}>
              {proposalDetail.isExpired && <div style={{ border: '1px solid var(--crm-danger)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', padding: '9px 12px', borderRadius: 'var(--crm-radius-sm)', fontWeight: 700 }}>Строк дії цієї пропозиції минув.</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '8px', fontSize: '13px' }}>
                <div><strong>Замовник:</strong> {proposalDetail.snapshot.customerName || '—'}</div>
                <div><strong>Контакти:</strong> {proposalDetail.snapshot.customerContacts || '—'}</div>
                <div><strong>Дата:</strong> {new Date(proposalDetail.snapshot.eventDate).toLocaleDateString('uk-UA')}</div>
                <div><strong>Місце:</strong> {proposalDetail.snapshot.eventVenue || '—'}</div>
                <div><strong>Гості / діти / підрядники:</strong> {proposalDetail.snapshot.guestCount} / {proposalDetail.snapshot.childCount} / {proposalDetail.snapshot.contractorCount}</div>
                <div><strong>Дійсна до:</strong> {new Date(proposalDetail.proposal.validUntil).toLocaleDateString('uk-UA')}</div>
                {proposalDetail.proposal.sentAtUtc && <div><strong>Надіслано:</strong> {new Date(proposalDetail.proposal.sentAtUtc).toLocaleString('uk-UA')} · {proposalDetail.proposal.sentByUsername || '—'}</div>}
                {proposalDetail.proposal.decidedAtUtc && <div><strong>Рішення:</strong> {new Date(proposalDetail.proposal.decidedAtUtc).toLocaleString('uk-UA')} · {proposalDetail.proposal.decidedByUsername || '—'}</div>}
              </div>
              {[
                ['Основне меню', proposalDetail.snapshot.dishes],
                ['Дитяче меню', proposalDetail.snapshot.children],
                ['Меню підрядників', proposalDetail.snapshot.contractors],
              ].map(([title, lines]) => (lines as EventCommercialProposalLine[]).length > 0 && (
                <div key={title as string}>
                  <strong>{title as string}</strong>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6px', fontSize: '12px' }}>
                    <thead><tr><th style={th}>Найменування</th><th style={th}>Порції</th><th style={th}>Ціна</th><th style={th}>Сума</th></tr></thead>
                    <tbody>{(lines as EventCommercialProposalLine[]).map((line, index) => <tr key={`${line.dishId}-${index}`}><td style={td}>{line.name}</td><td style={td}>{line.portions.toFixed(2)}</td><td style={td}>{line.unitPrice.toFixed(2)}</td><td style={td}>{line.totalPrice.toFixed(2)}</td></tr>)}</tbody>
                  </table>
                </div>
              ))}
              {proposalDetail.snapshot.customSections.map((section, sectionIndex) => (
                <div key={`${section.name}-${sectionIndex}`}>
                  <strong>{section.name} ({section.participantCount} ос.)</strong>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6px', fontSize: '12px' }}>
                    <tbody>{section.dishes.map((line, index) => <tr key={`${line.dishId}-${index}`}><td style={td}>{line.name}</td><td style={td}>{line.portions.toFixed(2)}</td><td style={td}>{line.unitPrice.toFixed(2)} грн</td><td style={td}>{line.totalPrice.toFixed(2)} грн</td></tr>)}</tbody>
                  </table>
                </div>
              ))}
              {proposalDetail.snapshot.additionalServices.length > 0 && (
                <div>
                  <strong>Додаткові послуги</strong>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6px', fontSize: '12px' }}>
                    <tbody>{proposalDetail.snapshot.additionalServices.map((line, index) => <tr key={`${line.additionalServiceId}-${index}`}><td style={td}>{line.name}</td><td style={td}>{line.quantity.toFixed(2)}</td><td style={td}>{line.unitPrice.toFixed(2)} грн</td><td style={td}>{line.totalPrice.toFixed(2)} грн</td></tr>)}</tbody>
                  </table>
                </div>
              )}
              <div style={{ marginLeft: 'auto', width: 'min(100%, 360px)', display: 'grid', gap: '5px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Проміжна сума</span><strong>{proposalDetail.snapshot.subtotal.toFixed(2)} грн</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Обслуговування</span><strong>{proposalDetail.snapshot.serviceAmount.toFixed(2)} грн</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Знижка</span><strong>-{proposalDetail.snapshot.discountAmount.toFixed(2)} грн</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--crm-border)', paddingTop: '7px', fontSize: '17px' }}><span>Разом</span><strong>{proposalDetail.snapshot.totalPrice.toFixed(2)} грн</strong></div>
              </div>
              {proposalDetail.proposal.notes && <div style={{ padding: '10px', border: '1px solid var(--crm-border)', borderRadius: '6px' }}><strong>Примітка:</strong><div>{proposalDetail.proposal.notes}</div></div>}
              {proposalDetail.proposal.decisionReason && <div style={{ padding: '10px', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', color: 'var(--crm-danger)' }}><strong>Причина відхилення:</strong><div>{proposalDetail.proposal.decisionReason}</div></div>}
            </div>
          </div>
        </div>
      )}

      {excelImportOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}>
          <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', width: '100%', maxWidth: 1380, boxShadow: 'var(--crm-shadow-sm)', marginBottom: 24 }}>
            <div style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', padding: '14px 20px', borderRadius: 'var(--crm-radius-md) var(--crm-radius-md) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17 }}>Імпорт замовлень з одного Excel</h3>
                <div style={{ fontSize: 12, opacity: .9 }}>Автоматичне зіставлення назв із ручною перевіркою перед створенням</div>
              </div>
              <button type="button" onClick={() => setExcelImportOpen(false)} disabled={excelImportBusy} aria-label="Закрити імпорт замовлень" title="Закрити" style={{ ...modalCloseButtonStyle, color: 'var(--crm-primary-contrast)', fontSize: 24, cursor: excelImportBusy ? 'not-allowed' : 'pointer' }}>×</button>
            </div>

            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
                <button type="button" onClick={downloadExcelTemplate} style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '9px 14px', cursor: 'pointer' }}>↓ Завантажити шаблон</button>
                <button type="button" onClick={() => excelFileInputRef.current?.click()} disabled={excelImportBusy} style={{ background: excelImportBusy ? 'var(--crm-surface-alt)' : 'var(--crm-success-bg)', color: excelImportBusy ? 'var(--crm-text-muted)' : 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '9px 14px', cursor: excelImportBusy ? 'wait' : 'pointer' }}>
                  {excelImportBusy ? 'Обробка...' : 'Обрати заповнений .xlsx'}
                </button>
                <input
                  ref={excelFileInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) previewExcelFile(file);
                  }}
                />
                <span style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>До 50 замовлень, 10 МБ. Набори аркушів: «Замовлення 1», «Меню 1», …; «Замовлення 2», «Меню 2», … . Старий шаблон також підтримується.</span>
              </div>
              {excelPreviews.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                  {excelPreviews.map((preview, index) => (
                    <button key={index} type="button" disabled={excelImportBusy} onClick={() => {
                      if (index === excelPreviewIndex) return;
                      setExcelPreviews(current => current.map((item, position) => position === excelPreviewIndex && excelPreview ? excelPreview : item));
                      setExcelPreview(preview);
                      setExcelPreviewIndex(index);
                    }} style={{ padding: '8px 12px', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-primary)', background: index === excelPreviewIndex ? 'var(--crm-primary)' : 'var(--crm-surface)', color: index === excelPreviewIndex ? 'var(--crm-primary-contrast)' : 'var(--crm-primary)' }}>
                      {index + 1}. {index === excelPreviewIndex ? excelPreview?.customerName || 'Без замовника' : preview.customerName || 'Без замовника'}
                    </button>
                  ))}
                </div>
              )}
              {excelImportProgress && <div role="status" aria-live="polite" style={{ marginBottom: 12 }}>{excelImportProgress}</div>}

              {excelImportError && (
                <div role="alert" style={{ padding: '10px 12px', marginBottom: 14, borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)' }}>{excelImportError}</div>
              )}

              {excelPreview && (
                <fieldset disabled={excelImportBusy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <>
                  <div className="crm-mobile-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
                    <label style={labelStyle}>Дата події *
                      <input type="date" value={excelPreview.eventDate.slice(0, 10)} onChange={(e) => setExcelPreview({ ...excelPreview, eventDate: e.target.value })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Назва події
                      <input value={excelPreview.eventName} onChange={(e) => setExcelPreview({ ...excelPreview, eventName: e.target.value })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Замовник *
                      <input value={excelPreview.customerName} onChange={(e) => setExcelPreview({ ...excelPreview, customerName: e.target.value })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Контакти
                      <input value={excelPreview.customerContacts} onChange={(e) => setExcelPreview({ ...excelPreview, customerContacts: e.target.value })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Місце проведення
                      <SearchSelect
                        items={[
                          ...eventVenueOptions.map((venue) => ({ id: venue.name, name: venue.name })),
                          ...(
                            excelPreview.eventVenue
                            && !eventVenueOptions.some((venue) => venue.name === excelPreview.eventVenue)
                              ? [{ id: excelPreview.eventVenue, name: excelPreview.eventVenue }]
                              : []
                          ),
                        ]}
                        value={excelPreview.eventVenue}
                        onChange={(value) => setExcelPreview({ ...excelPreview, eventVenue: value })}
                        placeholder="🔍 Пошук місця..."
                      />
                    </label>
                    <label style={labelStyle}>Гостей
                      <input type="number" min="0" value={excelPreview.guestCount} onChange={(e) => setExcelPreview({ ...excelPreview, guestCount: parseInt(e.target.value, 10) || 0 })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Дітей
                      <input type="number" min="0" value={excelPreview.childCount} onChange={(e) => setExcelPreview({ ...excelPreview, childCount: parseInt(e.target.value, 10) || 0 })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Підрядників
                      <input type="number" min="0" value={excelPreview.contractorCount} onChange={(e) => setExcelPreview({ ...excelPreview, contractorCount: parseInt(e.target.value, 10) || 0 })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Зустріч
                      <input type="time" step={60} value={excelPreview.meetingTime} onChange={(e) => setExcelPreview({ ...excelPreview, meetingTime: e.target.value })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Церемонія
                      <input type="time" step={60} value={excelPreview.ceremonyTime} onChange={(e) => setExcelPreview({ ...excelPreview, ceremonyTime: e.target.value })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Застілля
                      <input type="time" step={60} value={excelPreview.feastTime} onChange={(e) => setExcelPreview({ ...excelPreview, feastTime: e.target.value })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Сервіс %
                      <input type="number" min="0" step="0.01" value={excelPreview.servicePercent} onChange={(e) => setExcelPreview({ ...excelPreview, servicePercent: parseFloat(e.target.value) || 0 })} style={inputStyle} />
                    </label>
                    <label style={labelStyle}>Знижка %
                      <input type="number" min="0" step="0.01" value={excelPreview.discountPercent} onChange={(e) => setExcelPreview({ ...excelPreview, discountPercent: parseFloat(e.target.value) || 0 })} style={inputStyle} />
                    </label>
                  </div>

                  <div className="event-order-import-scroll" style={{ overflowX: 'auto', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }}>
                    <table className="event-order-import-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1120 }}>
                      <thead>
                        <tr>
                          <th style={excelImportThStyle}>Рядок</th>
                          <th style={excelImportThStyle}>Назва з Excel</th>
                          <th style={{ ...excelImportThStyle, minWidth: 270 }}>Страва CRM *</th>
                          <th style={excelImportThStyle}>Збіг</th>
                          <th style={excelImportThStyle}>Ціна</th>
                          <th style={excelImportThStyle}>Вихід</th>
                          <th style={{ ...excelImportThStyle, textAlign: 'center' }}>Оновити ціну B52</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excelPreview.rows.map((row) => {
                          const confidence = Math.round(row.matchConfidence * 100);
                          const needsReview = !row.matchedDishId || row.matchConfidence < .75;
                          return (
                            <tr key={row.rowNumber} style={{ background: needsReview ? 'var(--crm-warning-bg)' : undefined }}>
                              <td data-label="Рядок" style={excelImportTdStyle}>{row.rowNumber}</td>
                              <td data-label="Назва з Excel" style={excelImportTdStyle}>
                                <div style={{ fontWeight: 600 }}>{row.sourceDishName || '—'}</div>
                                {row.b52Code && <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>B52: {row.b52Code}</div>}
                              </td>
                              <td data-label="Страва CRM" style={excelImportTdStyle}>
                                <ItemQuantityField
                                  items={itemQuantityDishOptions}
                                  value={row.matchedDishId ? String(row.matchedDishId) : ''}
                                  onChange={(value) => {
                                    const id = parseInt(value, 10);
                                    const dish = dishOptions.find((item) => item.id === id);
                                    updateExcelPreviewRow('rows', row.rowNumber, {
                                      matchedDishId: dish ? Number(dish.id) : null,
                                      matchedDishName: dish?.rawName ?? '',
                                      dishOutput: row.dishOutput > 0 ? row.dishOutput : (dish?.output ?? 0),
                                      matchConfidence: dish ? 1 : 0,
                                      matchMethod: dish ? 'Обрано вручну' : 'Не знайдено',
                                      updateB52Price: false,
                                    });
                                  }}
                                  quantity={String(row.portions)}
                                  onQuantityChange={(value) => updateExcelPreviewRow('rows', row.rowNumber, { portions: parseFloat(value) || 0 })}
                                  label="Страва"
                                  quantityLabel="Порції"
                                  unit="порц."
                                  min={0}
                                  step={0.01}
                                  quantityRequired={false}
                                  placeholder="🔍 Пошук страви..."
                                />
                              </td>
                              <td data-label="Збіг" style={excelImportTdStyle}>
                                <span style={{ color: needsReview ? 'var(--crm-warning)' : 'var(--crm-success)', fontWeight: 600 }}>{row.matchMethod}</span>
                                <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>{confidence}%</div>
                              </td>
                              <td data-label="Ціна" style={excelImportTdStyle}>
                                <input type="number" min="0" step="0.01" value={row.unitPrice} onChange={(e) => updateExcelPreviewRow('rows', row.rowNumber, { unitPrice: parseFloat(e.target.value) || 0 })} style={{ ...inputStyle, width: 95 }} />
                              </td>
                              <td data-label="Вихід" style={excelImportTdStyle}>
                                <input type="number" min="0" step="0.001" value={row.dishOutput} onChange={(e) => updateExcelPreviewRow('rows', row.rowNumber, { dishOutput: parseFloat(e.target.value) || 0 })} style={{ ...inputStyle, width: 95 }} />
                              </td>
                              <td data-label="Оновити ціну B52" style={{ ...excelImportTdStyle, textAlign: 'center' }}>
                                <input type="checkbox" checked={row.updateB52Price} disabled={!row.matchedDishId} onChange={(e) => updateExcelPreviewRow('rows', row.rowNumber, { updateB52Price: e.target.checked })} title="Внутрішня ціна страви не змінюється" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {renderExcelDishSection('Діти', 'children', excelPreview.children)}
                  {renderExcelDishSection('Підрядники', 'contractors', excelPreview.contractors)}
                  {renderExcelServicesSection(excelPreview.additionalServices)}

                  <div style={{ marginTop: 12, padding: 10, borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', fontSize: 13 }}>
                    Прапорець «Оновити ціну B52» змінює лише поле B52Price. Внутрішня ціна CRM (FinalPrice) залишається без змін.
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                    <button type="button" onClick={() => setExcelImportOpen(false)} disabled={excelImportBusy} style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 18px', cursor: 'pointer' }}>Скасувати</button>
                    <button type="button" onClick={importExcelOrder} disabled={excelImportBusy} style={{ background: excelImportBusy ? 'var(--crm-surface-alt)' : 'var(--crm-primary)', color: excelImportBusy ? 'var(--crm-text-muted)' : 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 20px', cursor: excelImportBusy ? 'wait' : 'pointer', fontWeight: 700 }}>
                      {excelImportBusy
                        ? 'Імпорт...'
                        : `Створити всі замовлення (${excelPreviews.length})`}
                    </button>
                  </div>
                </>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="event-order-modal event-order-modal--full" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}>
          <div className="event-order-modal-card" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', width: '100%', maxWidth: '1180px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '24px' }}>
            <div style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', padding: '16px 20px', borderRadius: 'var(--crm-radius-md) var(--crm-radius-md) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>{editingId ? 'Редагування замовлення' : 'Нове замовлення'}</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button type="button" onClick={() => closeModal()} aria-label="Закрити редактор замовлення" title="Закрити" style={{ ...modalCloseButtonStyle, color: 'var(--crm-primary-contrast)', fontSize: '22px' }}>×</button>
              </div>
            </div>

            <div className="event-order-modal-content" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div role="note" style={{ padding: 12, border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-warning-bg)', color: 'var(--crm-text)' }}>
                {eventOrderProtectedEditNotice}
                {' У завершеному замовленні можна виправити страви у відкритому обліковому періоді, навіть за наявності маршруту або кухонних робіт. Початкове виробниче меню буде збережено в історії; старі маршрути та роботи не переплановуються і не відповідають виправленому меню. Дата/час, місце події, склад та завершений статус залишаються незмінними. Після збереження окремо натисніть «Перерахувати списання» саме цього замовлення: до успішного перерахунку списання і складські залишки залишаються попередніми. Перерахунок використовує доступні рецептури на дату події, а не гарантований знімок первинного списання.'}
              </div>
              <div className="event-order-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px' }}>
                <label style={labelStyle}>Дата замовлення *<input type="date" style={inputStyle} value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} /></label>
                <label style={labelStyle}>Статус
                  <select
                    style={inputStyle}
                    value={form.orderStatusId}
                    onChange={(e) => setForm({ ...form, orderStatusId: e.target.value })}
                  >
                    {statusOptions.map((status) => <option key={status.id} value={String(status.id)}>{status.name}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>Зустріч (година)<input type="time" step={600} style={inputStyle} value={form.meetingTime} onChange={(e) => setForm({ ...form, meetingTime: e.target.value })} /></label>
                <label style={labelStyle}>Церемонія (година)<input type="time" step={600} style={inputStyle} value={form.ceremonyTime} onChange={(e) => setForm({ ...form, ceremonyTime: e.target.value })} /></label>
                <label style={labelStyle}>Застілля (година)<input type="time" step={600} style={inputStyle} value={form.feastTime} onChange={(e) => setForm({ ...form, feastTime: e.target.value })} /></label>
              </div>

              <div className="event-order-form-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1fr', gap: '12px' }}>
                <label style={labelStyle}>
                  📍 Місце проведення
                  <SearchSelect
                    items={eventVenueSearchOptions}
                    value={form.eventVenue}
                    onChange={(id) => setForm({ ...form, eventVenue: id })}
                    placeholder="🔍 Оберіть місце..."
                  />
                </label>
                <InventoryWarehouseSelect
                  label="Склад списання продуктів"
                  value={form.sourceWarehouseId}
                  onChange={(sourceWarehouseId) => setForm({ ...form, sourceWarehouseId })}
                  required={statusOptionsById.get(parseOrderStatusId(form.orderStatusId))?.code.toLowerCase() === 'completed'}
                />
                <label style={labelStyle}>🎉 Назва заходу<input style={inputStyle} value={form.eventName} onChange={(e) => setForm({ ...form, eventName: e.target.value })} placeholder="Весілля, ювілей, корпоратив..." /></label>
                <label style={labelStyle}>Меню складав<input style={{ ...inputStyle, background: 'var(--crm-input-bg-muted)' }} value={form.menuCompiledBy} readOnly /></label>
              </div>

              <div className="event-order-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px' }}>
                <label style={labelStyle}>Імʼя замовника *<input style={inputStyle} value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} placeholder="ПІБ / компанія" /></label>
                <label style={labelStyle}>Контакти замовника<input style={inputStyle} value={form.customerContacts} onChange={(e) => setForm({ ...form, customerContacts: e.target.value })} placeholder="телефон, email" /></label>
                <label style={labelStyle}>Кількість гостей<input type="number" min="0" step="1" style={inputStyle} value={form.guestCount} onChange={(e) => setForm({ ...form, guestCount: e.target.value })} /></label>
                <label style={labelStyle}>Кількість дітей<input type="number" min="0" step="1" style={inputStyle} value={form.childCount} onChange={(e) => setForm({ ...form, childCount: e.target.value })} /></label>
                <label style={labelStyle}>Кількість підрядників<input type="number" min="0" step="1" style={inputStyle} value={form.contractorCount} onChange={(e) => setForm({ ...form, contractorCount: e.target.value })} /></label>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowChildren((prev) => {
                      if (!prev && childRows.length === 0) setChildRows([emptyChildRow(nextKey())]);
                      return !prev;
                    });
                  }}
                  style={{ background: showChildren ? 'var(--crm-success-bg)' : 'var(--crm-surface-alt)', color: showChildren ? 'var(--crm-success)' : 'var(--crm-text)', border: `1px solid ${showChildren ? 'var(--crm-success)' : 'var(--crm-border)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}
                >
                  {showChildren ? '−' : '+'} Діти
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowContractors((prev) => {
                      if (!prev && contractorRows.length === 0) setContractorRows([emptyContractorRow(nextKey())]);
                      return !prev;
                    });
                  }}
                  style={{ background: showContractors ? 'var(--crm-accent)' : 'var(--crm-surface-alt)', color: showContractors ? 'var(--crm-accent-contrast)' : 'var(--crm-text)', border: `1px solid ${showContractors ? 'var(--crm-accent)' : 'var(--crm-border)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}
                >
                  {showContractors ? '−' : '+'} Підрядники
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAdditionalServices((prev) => {
                      if (!prev && additionalServiceRows.length === 0) setAdditionalServiceRows([emptyAdditionalServiceRow(nextKey())]);
                      return !prev;
                    });
                  }}
                  style={{ background: showAdditionalServices ? 'var(--crm-warning-bg)' : 'var(--crm-surface-alt)', color: showAdditionalServices ? 'var(--crm-warning)' : 'var(--crm-text)', border: `1px solid ${showAdditionalServices ? 'var(--crm-warning)' : 'var(--crm-border)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}
                >
                  {showAdditionalServices ? '−' : '+'} Додаткові послуги
                </button>
                <button
                  type="button"
                  onClick={addCustomSection}
                  disabled={customSections.length >= 5}
                  style={{ background: customSections.length >= 5 ? 'var(--crm-surface-alt)' : 'var(--crm-primary)', color: customSections.length >= 5 ? 'var(--crm-text-muted)' : 'var(--crm-primary-contrast)', border: `1px solid ${customSections.length >= 5 ? 'var(--crm-border)' : 'var(--crm-primary)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '8px 14px', cursor: customSections.length >= 5 ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                >
                  + Додаткова вкладка ({customSections.length}/5)
                </button>
              </div>

              <div className="order-edit-section" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', overflow: 'hidden' }}>
                <div style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                 <strong style={{ fontSize: '14px' }}>Страви в замовленні</strong>
                 </div>
                 <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {([['timing','⏱ Таймінг'],['feast','# Застілля'],['mix','🔀 Мікс'],['notes','📝 Нотатки']] as [string,string][]).map(([key,label]) => (
                    <button key={key} type="button" onClick={() => toggleDishExtra(key)}
                      style={{ background: dishExtras.has(key) ? 'var(--crm-primary)' : 'var(--crm-surface)', color: dishExtras.has(key) ? 'var(--crm-primary-contrast)' : 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '3px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: dishExtras.has(key) ? 700 : 400 }}>
                      {label}
                    </button>
                   ))}
                   <button type="button" onClick={addRow} style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}>+ Рядок</button>
                   <button type="button" onClick={() => applyServingsCountToRows(rows, updateRow, form.guestCount)} style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}>⚡ Автозаповнити порції</button>
                   <button type="button" onClick={addSeparatorRow} style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}>+ Роздільник</button>
                 </div>
                </div>
                <table className="order-edit-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col />
                    <col style={{ width: '78px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '72px' }} />
                    <col style={{ width: '78px' }} />
                    {dishExtras.has('timing') && <col style={{ width: '110px' }} />}
                    {dishExtras.has('feast') && <col style={{ width: '78px' }} />}
                    {dishExtras.has('mix') && <col style={{ width: '130px' }} />}
                    {dishExtras.has('notes') && <col style={{ width: '160px' }} />}
                    <col style={{ width: '34px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={th}>Страва</th>
                      <th style={{ ...th, textAlign: 'center' }}>Ціна</th>
                      <th style={{ ...th, textAlign: 'right' }}>Сума</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Вихід</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Заг.вихід</th>
                      {dishExtras.has('timing') && <th style={{ ...th, fontSize: '11px' }}>⏱ Таймінг</th>}
                      {dishExtras.has('feast') && <th style={{ ...th, fontSize: '11px' }}># Застілля</th>}
                      {dishExtras.has('mix') && <th style={{ ...th, fontSize: '11px' }}>🔀 Мікс</th>}
                      {dishExtras.has('notes') && <th style={{ ...th, fontSize: '11px' }}>📝 Нотатки</th>}
                      <th style={{ ...th, width: '34px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const rowOutput = getRowOutput(r);
                      const rowAmount = getRowAmount(r);
                      return (
                        <React.Fragment key={r.key}>
                          <tr
                            className={r.rowKind === 'separator' ? 'order-edit-separator-row' : undefined}
                            draggable
                            onDragStart={() => setDragMainRowKey(r.key)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { if (dragMainRowKey !== null) moveMainRow(dragMainRowKey, r.key); }}
                            onDragEnd={() => setDragMainRowKey(null)}
                            style={{ borderBottom: '1px solid #dfe6e9', cursor: 'grab', opacity: dragMainRowKey === r.key ? 0.55 : 1 }}
                          >
                            {r.rowKind === 'separator' ? (
                              <>
                                <td style={{ ...td, padding: '4px 8px', position: 'relative' }}>
                                  <input
                                    type="text"
                                    value={r.separatorLabel}
                                    onChange={(e) => updateRow(r.key, { separatorLabel: e.target.value })}
                                    className="order-edit-separator-input"
                                    style={{ ...inputStyle, padding: '6px 8px', fontSize: '13px', fontWeight: 700 }}
                                    placeholder="Роздільник (опційно, пустий = відступ у друці)"
                                  />
                                  <div className="order-edit-mobile-controls-row">
                                    <div className="order-edit-mobile-add-center">
                                      <button
                                        type="button"
                                        onClick={() => insertRowAfter(r.key)}
                                        className="order-edit-mobile-mini-add"
                                        title="Додати рядок під цією позицією"
                                        aria-label="Додати рядок під цією позицією"
                                      >
                                        +
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => insertSeparatorAfter(r.key)}
                                        className="order-edit-mobile-mini-separator"
                                        title="Додати роздільник під цією позицією"
                                        aria-label="Додати роздільник під цією позицією"
                                      >
                                        —
                                      </button>
                                    </div>
                                    <div className="order-edit-mobile-inline">
                                      <button
                                        type="button"
                                        onClick={() => removeRow(r.key)}
                                        className="order-edit-mobile-delete-btn"
                                        title="Видалити позицію"
                                        aria-label="Видалити позицію"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                  <div className="order-edit-inline-add-controls" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: '-13px', display: 'flex', gap: '4px', zIndex: 3, background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: '999px', boxShadow: 'var(--crm-shadow-sm)', padding: '2px 4px' }}>
                                    <button
                                      type="button"
                                      onClick={() => insertRowAfter(r.key)}
                                      style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: 'none', borderRadius: '999px', width: '18px', height: '18px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', fontWeight: 700, lineHeight: 1 }}
                                      title="Додати рядок під цією позицією"
                                      aria-label="Додати рядок під цією позицією"
                                    >
                                      +
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => insertSeparatorAfter(r.key)}
                                      style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '999px', width: '18px', height: '18px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px', fontWeight: 700, lineHeight: 1 }}
                                      title="Додати роздільник під цією позицією"
                                      aria-label="Додати роздільник під цією позицією"
                                    >
                                      —
                                    </button>
                                  </div>
                                </td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                {dishExtras.has('timing') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {dishExtras.has('feast') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {dishExtras.has('mix') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {dishExtras.has('notes') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                              </>
                            ) : (
                              <>
                                <td style={{ ...td, padding: '6px 8px', position: 'relative' }}>
                                  <ItemQuantityField
                                    items={itemQuantityDishOptions}
                                    value={r.dishId}
                                    onChange={(id) => updateRow(r.key, getDishSelectionPatch(id, form.guestCount))}
                                    quantity={r.portions}
                                    onQuantityChange={(value) => updateRow(r.key, { portions: value })}
                                    label="Страва"
                                    quantityLabel="Порції"
                                    unit="порц."
                                    min={0}
                                    step={0.01}
                                    quantityRequired={false}
                                  />
                                  <div className="order-edit-mobile-controls-row">
                                    <div className="order-edit-mobile-add-center">
                                      <button
                                        type="button"
                                        onClick={() => insertRowAfter(r.key)}
                                        className="order-edit-mobile-mini-add"
                                        title="Додати рядок під цією позицією"
                                        aria-label="Додати рядок під цією позицією"
                                      >
                                        +
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => insertSeparatorAfter(r.key)}
                                        className="order-edit-mobile-mini-separator"
                                        title="Додати роздільник під цією позицією"
                                        aria-label="Додати роздільник під цією позицією"
                                      >
                                        —
                                      </button>
                                    </div>
                                    <div className="order-edit-mobile-inline">
                                      <button
                                        type="button"
                                        onClick={() => removeRow(r.key)}
                                        className="order-edit-mobile-delete-btn"
                                        title="Видалити позицію"
                                        aria-label="Видалити позицію"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                  <div className="order-edit-inline-add-controls" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: '-13px', display: 'flex', gap: '4px', zIndex: 3, background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: '999px', boxShadow: 'var(--crm-shadow-sm)', padding: '2px 4px' }}>
                                    <button
                                      type="button"
                                      onClick={() => insertRowAfter(r.key)}
                                      style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: 'none', borderRadius: '999px', width: '18px', height: '18px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', fontWeight: 700, lineHeight: 1 }}
                                      title="Додати рядок під цією позицією"
                                      aria-label="Додати рядок під цією позицією"
                                    >
                                      +
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => insertSeparatorAfter(r.key)}
                                      style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '999px', width: '18px', height: '18px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px', fontWeight: 700, lineHeight: 1 }}
                                      title="Додати роздільник під цією позицією"
                                      aria-label="Додати роздільник під цією позицією"
                                    >
                                      —
                                    </button>
                                  </div>
                                </td>
                                <td style={{ ...td, padding: '4px 4px' }}>
                                  <input type="number" min="0" step="0.01" value={r.unitPrice} onChange={(e) => updateRow(r.key, { unitPrice: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '13px', textAlign: 'right' }} />
                                </td>
                                <td style={{ ...td, padding: '6px 8px', fontWeight: 700, color: 'var(--crm-success)', textAlign: 'right', whiteSpace: 'nowrap' }}>{rowAmount.toFixed(2)}</td>
                                <td style={{ ...td, padding: '6px 4px', textAlign: 'right', fontSize: '12px' }}>{(parseFloat(r.dishOutput) || 0).toFixed(2)}</td>
                                <td style={{ ...td, padding: '6px 4px', color: 'var(--crm-text)', fontWeight: 600, textAlign: 'right', fontSize: '12px' }}>{rowOutput.toFixed(2)}</td>
                                {dishExtras.has('timing') && (
                                  <td style={{ ...td, padding: '4px' }}>
                                    <input type="text" value={r.servingTiming} onChange={(e) => updateRow(r.key, { servingTiming: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="18:30" />
                                  </td>
                                )}
                                {dishExtras.has('feast') && (
                                  <td style={{ ...td, padding: '4px' }}>
                                    <input type="text" value={r.feastTableNumber} onChange={(e) => updateRow(r.key, { feastTableNumber: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="№" />
                                  </td>
                                )}
                                {dishExtras.has('mix') && (
                                  <td style={{ ...td, padding: '4px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input type="checkbox" checked={r.isMixedServe} onChange={(e) => updateRow(r.key, { isMixedServe: e.target.checked, mixedGroup: e.target.checked ? r.mixedGroup : '' })} />
                                      <input type="text" value={r.mixedGroup} onChange={(e) => updateRow(r.key, { mixedGroup: e.target.value })} disabled={!r.isMixedServe} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px', flex: 1 }} placeholder="Група" />
                                    </div>
                                  </td>
                                )}
                                {dishExtras.has('notes') && (
                                  <td style={{ ...td, padding: '4px' }}>
                                    <input type="text" value={r.dishNotes} onChange={(e) => updateRow(r.key, { dishNotes: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="Нотатка..." />
                                  </td>
                                )}
                              </>
                            )}
                            <td style={{ ...td, padding: '4px', textAlign: 'center' }}>
                              <button type="button" onClick={() => removeRow(r.key)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', width: '26px', height: '26px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>×</button>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                                          <tr>
                        <td colSpan={6 + dishExtras.size} style={{ ...td, padding: '10px 8px', textAlign: 'center', background: 'var(--crm-surface-alt)' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={addRow}
                              style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                              + Додати рядок в кінець списку
                            </button>
                            <button
                              type="button"
                              onClick={addSeparatorRow}
                              style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                              + Додати роздільник
                            </button>
                          </div>
                        </td>
                      </tr>
                      {rows.length === 0 && <tr><td colSpan={6 + dishExtras.size} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Натисніть "+ Рядок" щоб додати страви</td></tr>}
                  </tbody>
                </table>
              </div>

              {showChildren && (
                <div className="order-edit-section" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', overflow: 'hidden' }}>
                  <div style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '14px' }}>Діти</strong>
                      <button type="button" onClick={() => applyServingsCountToRows(childRows, updateChildRow, form.childCount)} style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 12px', cursor: 'pointer', fontSize: '12px' }}>⚡ Автозаповнити порції</button>
                      <input type="number" min="0" step="1" value={form.childCount} onChange={(e) => setForm({ ...form, childCount: e.target.value })} style={{ ...inputStyle, width: '72px', padding: '4px 7px', fontSize: '13px' }} placeholder="К-сть" />
                      <input type="text" value={form.childServeTiming} onChange={(e) => setForm({ ...form, childServeTiming: e.target.value })} style={{ ...inputStyle, width: '110px', padding: '4px 7px', fontSize: '13px' }} placeholder="⏱ Таймінг" />
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {([['timing','⏱'],['feast','#Зас.'],['mix','🔀'],['notes','📝']] as [string,string][]).map(([k,label]) => (
                        <button key={k} type="button" onClick={() => toggleChildExtra(k)} style={{ background: childExtras.has(k) ? 'var(--crm-primary)' : 'var(--crm-surface)', color: childExtras.has(k) ? 'var(--crm-primary-contrast)' : 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '3px 8px', cursor: 'pointer', fontSize: '12px', fontWeight: childExtras.has(k) ? 700 : 400 }}>{label}</button>
                      ))}
                      <button type="button" onClick={addChildRow} style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 14px', cursor: 'pointer', fontSize: '13px' }}>+ Рядок</button>
                      <button type="button" onClick={addChildSeparatorRow} style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 14px', cursor: 'pointer', fontSize: '13px' }}>+ Роздільник</button>
                    </div>
                  </div>
                  <table className="order-edit-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col /><col style={{ width: '78px' }} /><col style={{ width: '90px' }} /><col style={{ width: '72px' }} /><col style={{ width: '78px' }} />
                      {childExtras.has('timing') && <col style={{ width: '110px' }} />}
                      {childExtras.has('feast') && <col style={{ width: '78px' }} />}
                      {childExtras.has('mix') && <col style={{ width: '130px' }} />}
                      {childExtras.has('notes') && <col style={{ width: '160px' }} />}
                      <col style={{ width: '34px' }} />
                    </colgroup>
                    <thead><tr>
                      <th style={th}>Страва</th>
                      <th style={{ ...th, textAlign: 'center' }}>Ціна</th>
                      <th style={{ ...th, textAlign: 'right' }}>Сума</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Вихід</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Заг.вих.</th>
                      {childExtras.has('timing') && <th style={{ ...th, fontSize: '11px' }}>⏱</th>}
                      {childExtras.has('feast') && <th style={{ ...th, fontSize: '11px' }}>#</th>}
                      {childExtras.has('mix') && <th style={{ ...th, fontSize: '11px' }}>🔀</th>}
                      {childExtras.has('notes') && <th style={{ ...th, fontSize: '11px' }}>📝</th>}
                      <th style={{ ...th, width: '34px' }} />
                    </tr></thead>
                    <tbody>
                      {childRows.map((r) => {
                        const rowAmount = getMenuRowAmount(r.portions, r.unitPrice);
                        const rowOutput = (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0);
                        return (
                          <tr
                            key={r.key}
                            draggable
                            onDragStart={() => setDragChildRowKey(r.key)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { if (dragChildRowKey !== null) moveChildRow(dragChildRowKey, r.key); }}
                            onDragEnd={() => setDragChildRowKey(null)}
                            style={{ borderBottom: '1px solid #dfe6e9', cursor: 'grab', opacity: dragChildRowKey === r.key ? 0.55 : 1 }}
                          >
                            {r.rowKind === 'separator' ? (
                              <>
                                <td style={{ ...td, padding: '4px 8px' }}>
                                  <input type="text" value={r.separatorLabel} onChange={(e) => updateChildRow(r.key, { separatorLabel: e.target.value })} style={{ ...inputStyle, padding: '6px 8px', fontSize: '13px', fontWeight: 600 }} placeholder="Роздільник (опційно, пустий = відступ у друці)" />
                                  <div className="order-edit-mobile-controls-row">
                                    <div className="order-edit-mobile-add-center">
                                      <button
                                        type="button"
                                        onClick={() => insertChildRowAfter(r.key)}
                                        className="order-edit-mobile-mini-add"
                                        title="Додати рядок під цією позицією"
                                        aria-label="Додати рядок під цією позицією"
                                      >
                                        +
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => insertChildSeparatorAfter(r.key)}
                                        className="order-edit-mobile-mini-separator"
                                        title="Додати роздільник під цією позицією"
                                        aria-label="Додати роздільник під цією позицією"
                                      >
                                        —
                                      </button>
                                    </div>
                                    <div className="order-edit-mobile-inline">
                                      <button
                                        type="button"
                                        onClick={() => removeChildRow(r.key)}
                                        className="order-edit-mobile-delete-btn"
                                        title="Видалити позицію"
                                        aria-label="Видалити позицію"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                {childExtras.has('timing') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {childExtras.has('feast') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {childExtras.has('mix') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {childExtras.has('notes') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                              </>
                            ) : (
                              <>
                                <td style={{ ...td, padding: '6px 8px' }}>
                                  <ItemQuantityField
                                    items={itemQuantityDishOptions}
                                    value={r.dishId}
                                    onChange={(id) => updateChildRow(r.key, getDishSelectionPatch(id, form.childCount))}
                                    quantity={r.portions}
                                    onQuantityChange={(value) => updateChildRow(r.key, { portions: value })}
                                    label="Страва"
                                    quantityLabel="Порції"
                                    unit="порц."
                                    min={0}
                                    step={0.01}
                                    quantityRequired={false}
                                  />
                                  <div className="order-edit-mobile-controls-row">
                                    <div className="order-edit-mobile-add-center">
                                      <button
                                        type="button"
                                        onClick={() => insertChildRowAfter(r.key)}
                                        className="order-edit-mobile-mini-add"
                                        title="Додати рядок під цією позицією"
                                        aria-label="Додати рядок під цією позицією"
                                      >
                                        +
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => insertChildSeparatorAfter(r.key)}
                                        className="order-edit-mobile-mini-separator"
                                        title="Додати роздільник під цією позицією"
                                        aria-label="Додати роздільник під цією позицією"
                                      >
                                        —
                                      </button>
                                    </div>
                                    <div className="order-edit-mobile-inline">
                                      <button
                                        type="button"
                                        onClick={() => removeChildRow(r.key)}
                                        className="order-edit-mobile-delete-btn"
                                        title="Видалити позицію"
                                        aria-label="Видалити позицію"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ ...td, padding: '4px' }}><input type="number" min="0" step="0.01" value={r.unitPrice} onChange={(e) => updateChildRow(r.key, { unitPrice: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '13px', textAlign: 'right' }} /></td>
                                <td style={{ ...td, padding: '6px 8px', fontWeight: 700, color: 'var(--crm-success)', textAlign: 'right', whiteSpace: 'nowrap' }}>{rowAmount.toFixed(2)}</td>
                                <td style={{ ...td, padding: '6px 4px', textAlign: 'right', fontSize: '12px' }}>{(parseFloat(r.dishOutput) || 0).toFixed(2)}</td>
                                <td style={{ ...td, padding: '6px 4px', color: 'var(--crm-text)', fontWeight: 600, textAlign: 'right', fontSize: '12px' }}>{rowOutput.toFixed(2)}</td>
                                {childExtras.has('timing') && <td style={{ ...td, padding: '4px' }}><input type="text" value={r.servingTiming} onChange={(e) => updateChildRow(r.key, { servingTiming: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="18:30" /></td>}
                                {childExtras.has('feast') && <td style={{ ...td, padding: '4px' }}><input type="text" value={r.feastTableNumber} onChange={(e) => updateChildRow(r.key, { feastTableNumber: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="№" /></td>}
                                {childExtras.has('mix') && <td style={{ ...td, padding: '4px' }}><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={r.isMixedServe} onChange={(e) => updateChildRow(r.key, { isMixedServe: e.target.checked, mixedGroup: e.target.checked ? r.mixedGroup : '' })} /><input type="text" value={r.mixedGroup} onChange={(e) => updateChildRow(r.key, { mixedGroup: e.target.value })} disabled={!r.isMixedServe} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px', flex: 1 }} placeholder="Група" /></div></td>}
                                {childExtras.has('notes') && <td style={{ ...td, padding: '4px' }}><input type="text" value={r.dishNotes} onChange={(e) => updateChildRow(r.key, { dishNotes: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="Нотатка..." /></td>}
                              </>
                            )}
                            <td style={{ ...td, padding: '4px', textAlign: 'center' }}><button type="button" onClick={() => removeChildRow(r.key)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', width: '26px', height: '26px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>×</button></td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td colSpan={6 + childExtras.size} style={{ ...td, padding: '10px 8px', textAlign: 'center', background: 'var(--crm-surface-alt)' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={addChildRow}
                              style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                              + Додати рядок в кінець списку
                            </button>
                            <button
                              type="button"
                              onClick={addChildSeparatorRow}
                              style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                              + Додати роздільник
                            </button>
                          </div>
                        </td>
                      </tr>
                      {childRows.length === 0 && <tr><td colSpan={6 + childExtras.size} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Натисніть "+ Рядок" щоб додати дитяче меню</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {showContractors && (
                <div className="order-edit-section" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', overflow: 'hidden' }}>
                  <div style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '14px' }}>Підрядники</strong>
                      <button type="button" onClick={() => applyServingsCountToRows(contractorRows, updateContractorRow, form.contractorCount)} style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent-contrast)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 12px', cursor: 'pointer', fontSize: '12px' }}>⚡ Автозаповнити порції</button>
                      <input type="number" min="0" step="1" value={form.contractorCount} onChange={(e) => setForm({ ...form, contractorCount: e.target.value })} style={{ ...inputStyle, width: '72px', padding: '4px 7px', fontSize: '13px' }} placeholder="К-сть" />
                      <input type="text" value={form.contractorServeTiming} onChange={(e) => setForm({ ...form, contractorServeTiming: e.target.value })} style={{ ...inputStyle, width: '110px', padding: '4px 7px', fontSize: '13px' }} placeholder="⏱ Таймінг" />
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {([['timing','⏱'],['feast','#Зас.'],['mix','🔀'],['notes','📝']] as [string,string][]).map(([k,label]) => (
                        <button key={k} type="button" onClick={() => toggleContractorExtra(k)} style={{ background: contractorExtras.has(k) ? 'var(--crm-primary)' : 'var(--crm-surface)', color: contractorExtras.has(k) ? 'var(--crm-primary-contrast)' : 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '3px 8px', cursor: 'pointer', fontSize: '12px', fontWeight: contractorExtras.has(k) ? 700 : 400 }}>{label}</button>
                      ))}
                      <button type="button" onClick={addContractorRow} style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent-contrast)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 14px', cursor: 'pointer', fontSize: '13px' }}>+ Рядок</button>
                      <button type="button" onClick={addContractorSeparatorRow} style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 14px', cursor: 'pointer', fontSize: '13px' }}>+ Роздільник</button>
                    </div>
                  </div>
                  <table className="order-edit-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col /><col style={{ width: '78px' }} /><col style={{ width: '90px' }} /><col style={{ width: '72px' }} /><col style={{ width: '78px' }} />
                      {contractorExtras.has('timing') && <col style={{ width: '110px' }} />}
                      {contractorExtras.has('feast') && <col style={{ width: '78px' }} />}
                      {contractorExtras.has('mix') && <col style={{ width: '130px' }} />}
                      {contractorExtras.has('notes') && <col style={{ width: '160px' }} />}
                      <col style={{ width: '34px' }} />
                    </colgroup>
                    <thead><tr>
                      <th style={th}>Страва</th>
                      <th style={{ ...th, textAlign: 'center' }}>Ціна</th>
                      <th style={{ ...th, textAlign: 'right' }}>Сума</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Вихід</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Заг.вих.</th>
                      {contractorExtras.has('timing') && <th style={{ ...th, fontSize: '11px' }}>⏱</th>}
                      {contractorExtras.has('feast') && <th style={{ ...th, fontSize: '11px' }}>#</th>}
                      {contractorExtras.has('mix') && <th style={{ ...th, fontSize: '11px' }}>🔀</th>}
                      {contractorExtras.has('notes') && <th style={{ ...th, fontSize: '11px' }}>📝</th>}
                      <th style={{ ...th, width: '34px' }} />
                    </tr></thead>
                    <tbody>
                      {contractorRows.map((r) => {
                        const rowAmount = getMenuRowAmount(r.portions, r.unitPrice);
                        const rowOutput = (parseFloat(r.dishOutput) || 0) * (parseFloat(r.portions) || 0);
                        return (
                          <tr
                            key={r.key}
                            draggable
                            onDragStart={() => setDragContractorRowKey(r.key)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { if (dragContractorRowKey !== null) moveContractorRow(dragContractorRowKey, r.key); }}
                            onDragEnd={() => setDragContractorRowKey(null)}
                            style={{ borderBottom: '1px solid #dfe6e9', cursor: 'grab', opacity: dragContractorRowKey === r.key ? 0.55 : 1 }}
                          >
                            {r.rowKind === 'separator' ? (
                              <>
                                <td style={{ ...td, padding: '4px 8px' }}>
                                  <input type="text" value={r.separatorLabel} onChange={(e) => updateContractorRow(r.key, { separatorLabel: e.target.value })} style={{ ...inputStyle, padding: '6px 8px', fontSize: '13px', fontWeight: 600 }} placeholder="Роздільник (опційно, пустий = відступ у друці)" />
                                  <div className="order-edit-mobile-controls-row">
                                    <div className="order-edit-mobile-add-center">
                                      <button
                                        type="button"
                                        onClick={() => insertContractorRowAfter(r.key)}
                                        className="order-edit-mobile-mini-add"
                                        title="Додати рядок під цією позицією"
                                        aria-label="Додати рядок під цією позицією"
                                      >
                                        +
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => insertContractorSeparatorAfter(r.key)}
                                        className="order-edit-mobile-mini-separator"
                                        title="Додати роздільник під цією позицією"
                                        aria-label="Додати роздільник під цією позицією"
                                      >
                                        —
                                      </button>
                                    </div>
                                    <div className="order-edit-mobile-inline">
                                      <button
                                        type="button"
                                        onClick={() => removeContractorRow(r.key)}
                                        className="order-edit-mobile-delete-btn"
                                        title="Видалити позицію"
                                        aria-label="Видалити позицію"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>
                                {contractorExtras.has('timing') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {contractorExtras.has('feast') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {contractorExtras.has('mix') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                                {contractorExtras.has('notes') && <td style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>—</td>}
                              </>
                            ) : (
                              <>
                                <td style={{ ...td, padding: '6px 8px' }}>
                                  <ItemQuantityField
                                    items={itemQuantityDishOptions}
                                    value={r.dishId}
                                    onChange={(id) => updateContractorRow(r.key, getDishSelectionPatch(id, form.contractorCount))}
                                    quantity={r.portions}
                                    onQuantityChange={(value) => updateContractorRow(r.key, { portions: value })}
                                    label="Страва"
                                    quantityLabel="Порції"
                                    unit="порц."
                                    min={0}
                                    step={0.01}
                                    quantityRequired={false}
                                  />
                                  <div className="order-edit-mobile-controls-row">
                                    <div className="order-edit-mobile-add-center">
                                      <button
                                        type="button"
                                        onClick={() => insertContractorRowAfter(r.key)}
                                        className="order-edit-mobile-mini-add"
                                        title="Додати рядок під цією позицією"
                                        aria-label="Додати рядок під цією позицією"
                                      >
                                        +
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => insertContractorSeparatorAfter(r.key)}
                                        className="order-edit-mobile-mini-separator"
                                        title="Додати роздільник під цією позицією"
                                        aria-label="Додати роздільник під цією позицією"
                                      >
                                        —
                                      </button>
                                    </div>
                                    <div className="order-edit-mobile-inline">
                                      <button
                                        type="button"
                                        onClick={() => removeContractorRow(r.key)}
                                        className="order-edit-mobile-delete-btn"
                                        title="Видалити позицію"
                                        aria-label="Видалити позицію"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ ...td, padding: '4px' }}><input type="number" min="0" step="0.01" value={r.unitPrice} onChange={(e) => updateContractorRow(r.key, { unitPrice: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '13px', textAlign: 'right' }} /></td>
                                <td style={{ ...td, padding: '6px 8px', fontWeight: 700, color: 'var(--crm-success)', textAlign: 'right', whiteSpace: 'nowrap' }}>{rowAmount.toFixed(2)}</td>
                                <td style={{ ...td, padding: '6px 4px', textAlign: 'right', fontSize: '12px' }}>{(parseFloat(r.dishOutput) || 0).toFixed(2)}</td>
                                <td style={{ ...td, padding: '6px 4px', color: 'var(--crm-text)', fontWeight: 600, textAlign: 'right', fontSize: '12px' }}>{rowOutput.toFixed(2)}</td>
                                {contractorExtras.has('timing') && <td style={{ ...td, padding: '4px' }}><input type="text" value={r.servingTiming} onChange={(e) => updateContractorRow(r.key, { servingTiming: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="18:30" /></td>}
                                {contractorExtras.has('feast') && <td style={{ ...td, padding: '4px' }}><input type="text" value={r.feastTableNumber} onChange={(e) => updateContractorRow(r.key, { feastTableNumber: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="№" /></td>}
                                {contractorExtras.has('mix') && <td style={{ ...td, padding: '4px' }}><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={r.isMixedServe} onChange={(e) => updateContractorRow(r.key, { isMixedServe: e.target.checked, mixedGroup: e.target.checked ? r.mixedGroup : '' })} /><input type="text" value={r.mixedGroup} onChange={(e) => updateContractorRow(r.key, { mixedGroup: e.target.value })} disabled={!r.isMixedServe} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px', flex: 1 }} placeholder="Група" /></div></td>}
                                {contractorExtras.has('notes') && <td style={{ ...td, padding: '4px' }}><input type="text" value={r.dishNotes} onChange={(e) => updateContractorRow(r.key, { dishNotes: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="Нотатка..." /></td>}
                              </>
                            )}
                            <td style={{ ...td, padding: '4px', textAlign: 'center' }}><button type="button" onClick={() => removeContractorRow(r.key)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', width: '26px', height: '26px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>×</button></td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td colSpan={6 + contractorExtras.size} style={{ ...td, padding: '10px 8px', textAlign: 'center', background: 'var(--crm-surface-alt)' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={addContractorRow}
                              style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                              + Додати рядок в кінець списку
                            </button>
                            <button
                              type="button"
                              onClick={addContractorSeparatorRow}
                              style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                              + Додати роздільник
                            </button>
                          </div>
                        </td>
                      </tr>
                      {contractorRows.length === 0 && <tr><td colSpan={6 + contractorExtras.size} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Натисніть "+ Рядок" щоб додати меню для підрядників</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {customSections.map((section) => (
                <div key={section.key} className="order-edit-section" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', overflow: 'hidden' }}>
                  <div style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <input value={section.name} onChange={(e) => updateCustomSection(section.key, { name: e.target.value })} placeholder="Назва вкладки" style={{ ...inputStyle, borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', width: '180px' }} />
                      <input type="number" min="0" step="1" value={section.participantCount} onChange={(e) => updateCustomSection(section.key, { participantCount: e.target.value })} placeholder="К-сть" style={{ ...inputStyle, borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', width: '80px' }} />
                      <input type="text" value={section.serveTiming} onChange={(e) => updateCustomSection(section.key, { serveTiming: e.target.value })} placeholder="⏱ Таймінг" style={{ ...inputStyle, borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', width: '110px' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button type="button" onClick={() => addCustomSectionRow(section.key)} style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 12px', cursor: 'pointer', fontSize: '13px' }}>+ Рядок</button>
                      <button type="button" onClick={() => applyServingsCountToCustomSection(section.key, section.rows)} style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 12px', cursor: 'pointer', fontSize: '13px' }}>⚡ Автозаповнити порції</button>
                      <button type="button" onClick={() => removeCustomSection(section.key)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 12px', cursor: 'pointer', fontSize: '13px' }}>🗑</button>
                    </div>
                  </div>
                  <table className="order-edit-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col /><col style={{ width: '78px' }} /><col style={{ width: '90px' }} /><col style={{ width: '72px' }} /><col style={{ width: '78px' }} /><col style={{ width: '34px' }} />
                    </colgroup>
                    <thead><tr>
                      <th style={th}>Страва</th>
                      <th style={{ ...th, textAlign: 'center' }}>Ціна</th>
                      <th style={{ ...th, textAlign: 'right' }}>Сума</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Вихід</th>
                      <th style={{ ...th, textAlign: 'right', fontSize: '11px' }}>Заг.вих.</th>
                      <th style={{ ...th, width: '34px' }} />
                    </tr></thead>
                    <tbody>
                      {section.rows.map((row) => {
                        const rowAmount = getCustomRowAmount(row);
                        const rowOutput = getCustomRowOutput(row);
                        return (
                          <tr
                            key={row.key}
                            draggable
                            onDragStart={() => setDragCustomRow({ sectionKey: section.key, rowKey: row.key })}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                              if (dragCustomRow && dragCustomRow.sectionKey === section.key) {
                                moveCustomSectionRow(section.key, dragCustomRow.rowKey, row.key);
                              }
                            }}
                            onDragEnd={() => setDragCustomRow(null)}
                            style={{ borderBottom: '1px solid #dfe6e9', cursor: 'grab', opacity: dragCustomRow?.sectionKey === section.key && dragCustomRow.rowKey === row.key ? 0.55 : 1 }}
                          >
                            <td style={{ ...td, padding: '6px 8px' }}>
                              <ItemQuantityField
                                items={itemQuantityDishOptions}
                                value={row.dishId}
                                onChange={(id) => updateCustomSectionRow(section.key, row.key, getDishSelectionPatch(id, section.participantCount))}
                                quantity={row.portions}
                                onQuantityChange={(value) => updateCustomSectionRow(section.key, row.key, { portions: value })}
                                label="Страва"
                                quantityLabel="Порції"
                                unit="порц."
                                min={0}
                                step={0.01}
                                quantityRequired={false}
                              />
                              <div className="order-edit-mobile-controls-row">
                                <div className="order-edit-mobile-add-center">
                                  <button
                                    type="button"
                                    onClick={() => insertCustomSectionRowAfter(section.key, row.key)}
                                    className="order-edit-mobile-mini-add"
                                    title="Додати рядок під цією позицією"
                                    aria-label="Додати рядок під цією позицією"
                                  >
                                    +
                                  </button>
                                </div>
                                <div className="order-edit-mobile-inline">
                                  <button
                                    type="button"
                                    onClick={() => removeCustomSectionRow(section.key, row.key)}
                                    className="order-edit-mobile-delete-btn"
                                    title="Видалити позицію"
                                    aria-label="Видалити позицію"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td style={{ ...td, padding: '4px' }}><input type="number" min="0" step="0.01" value={row.unitPrice} onChange={(e) => updateCustomSectionRow(section.key, row.key, { unitPrice: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '13px', textAlign: 'right' }} /></td>
                            <td style={{ ...td, padding: '6px 8px', fontWeight: 700, color: 'var(--crm-success)', textAlign: 'right', whiteSpace: 'nowrap' }}>{rowAmount.toFixed(2)}</td>
                            <td style={{ ...td, padding: '6px 4px', textAlign: 'right', fontSize: '12px' }}>{(parseFloat(row.dishOutput) || 0).toFixed(2)}</td>
                            <td style={{ ...td, padding: '6px 4px', color: 'var(--crm-text)', fontWeight: 600, textAlign: 'right', fontSize: '12px' }}>{rowOutput.toFixed(2)}</td>
                            <td style={{ ...td, padding: '4px', textAlign: 'center' }}><button type="button" onClick={() => removeCustomSectionRow(section.key, row.key)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', width: '26px', height: '26px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>×</button></td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td colSpan={6} style={{ ...td, padding: '10px 8px', textAlign: 'center', background: 'var(--crm-surface-alt)' }}>
                          <button
                            type="button"
                            onClick={() => addCustomSectionRow(section.key)}
                            style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                          >
                            + Додати рядок в кінець списку
                          </button>
                        </td>
                      </tr>
                      {section.rows.length === 0 && <tr><td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Натисніть "+ Рядок" щоб додати страви</td></tr>}
                    </tbody>
                  </table>
                </div>
              ))}

              {showAdditionalServices && (
                <div className="order-edit-section" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', overflow: 'hidden' }}>
                  <div style={{ background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '14px' }}>Додаткові послуги</strong>
                      {!isWorker && (
                        <button
                          type="button"
                          onClick={() => navigate('/management/additional-services')}
                          style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                        >
                          Керувати послугами
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {([['notes','📝 Нотатки']] as [string,string][]).map(([k,label]) => (
                        <button key={k} type="button" onClick={() => toggleServiceExtra(k)} style={{ background: serviceExtras.has(k) ? 'var(--crm-primary)' : 'var(--crm-surface)', color: serviceExtras.has(k) ? 'var(--crm-primary-contrast)' : 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '3px 8px', cursor: 'pointer', fontSize: '12px', fontWeight: serviceExtras.has(k) ? 700 : 400 }}>{label}</button>
                      ))}
                      <button type="button" onClick={addAdditionalServiceRow} style={{ background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}>+ Рядок</button>
                    </div>
                  </div>
                  {additionalServiceOptions.length === 0 && (
                    <div style={{ padding: '12px 16px', background: 'var(--crm-warning-bg)', borderBottom: '1px solid var(--crm-warning)', color: 'var(--crm-warning)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <span>У базі ще немає жодної додаткової послуги. Спочатку створіть їх у довіднику послуг.</span>
                      {!isWorker && (
                        <button
                          type="button"
                          onClick={() => navigate('/management/additional-services')}
                          style={{ background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                        >
                          Відкрити довідник послуг
                        </button>
                      )}
                    </div>
                  )}
                  <table className="order-edit-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col /><col style={{ width: '90px' }} /><col style={{ width: '110px' }} /><col style={{ width: '90px' }} />
                      {serviceExtras.has('notes') && <col style={{ width: '180px' }} />}
                      <col style={{ width: '34px' }} />
                    </colgroup>
                    <thead><tr>
                      <th style={th}>Послуга</th>
                      <th style={{ ...th, textAlign: 'center' }}>Ціна продажу</th>
                      <th style={{ ...th, textAlign: 'center' }}>Собівартість од.</th>
                      <th style={{ ...th, textAlign: 'right' }}>Сума</th>
                      {serviceExtras.has('notes') && <th style={{ ...th, fontSize: '11px' }}>📝 Нотатки</th>}
                      <th style={{ ...th, width: '34px' }} />
                    </tr></thead>
                    <tbody>
                      {additionalServiceRows.map((r) => {
                        const rowAmount = (parseFloat(r.quantity) || 0) * (parseFloat(r.unitPrice) || 0);
                        return (
                          <tr key={r.key} style={{ borderBottom: '1px solid #dfe6e9' }}>
                            <td style={{ ...td, padding: '6px 8px' }}>
                              <ItemQuantityField
                                items={itemQuantityServiceOptions}
                                value={r.additionalServiceId}
                                onChange={(id) => {
                                  const service = additionalServiceOptions.find((item) => item.id === Number(id));
                                  updateAdditionalServiceRow(r.key, { additionalServiceId: id, unitPrice: service ? String(service.price) : '', unitCost: service ? String(service.costPrice) : '' });
                                }}
                                quantity={r.quantity}
                                onQuantityChange={(value) => updateAdditionalServiceRow(r.key, { quantity: value })}
                                label="Послуга"
                                quantityLabel="Кількість"
                                min={0}
                                step={0.01}
                                quantityRequired={false}
                                placeholder="🔍 Пошук послуги..."
                              />
                              <div className="order-edit-mobile-controls-row">
                                <div className="order-edit-mobile-add-center">
                                  <button
                                    type="button"
                                    onClick={() => insertAdditionalServiceRowAfter(r.key)}
                                    className="order-edit-mobile-mini-add"
                                    title="Додати рядок під цією позицією"
                                    aria-label="Додати рядок під цією позицією"
                                  >
                                    +
                                  </button>
                                </div>
                                <div className="order-edit-mobile-inline">
                                  <button
                                    type="button"
                                    onClick={() => removeAdditionalServiceRow(r.key)}
                                    className="order-edit-mobile-delete-btn"
                                    title="Видалити позицію"
                                    aria-label="Видалити позицію"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td style={{ ...td, padding: '4px' }}><input type="number" min="0" step="0.01" value={r.unitPrice} onChange={(e) => updateAdditionalServiceRow(r.key, { unitPrice: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '13px', textAlign: 'right' }} /></td>
                            <td style={{ ...td, padding: '4px' }}><input type="number" min="0" step="0.01" value={r.unitCost} onChange={(e) => updateAdditionalServiceRow(r.key, { unitCost: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '13px', textAlign: 'right' }} aria-label="Собівартість одиниці додаткової послуги" /></td>
                            <td style={{ ...td, padding: '6px 8px', fontWeight: 700, color: 'var(--crm-success)', textAlign: 'right', whiteSpace: 'nowrap' }}>{rowAmount.toFixed(2)}</td>
                            {serviceExtras.has('notes') && <td style={{ ...td, padding: '4px' }}><input type="text" value={r.serviceNotes} onChange={(e) => updateAdditionalServiceRow(r.key, { serviceNotes: e.target.value })} style={{ ...inputStyle, padding: '4px 5px', fontSize: '12px' }} placeholder="Нотатка..." /></td>}
                            <td style={{ ...td, padding: '4px', textAlign: 'center' }}><button type="button" onClick={() => removeAdditionalServiceRow(r.key)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', width: '26px', height: '26px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>×</button></td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td colSpan={5 + (serviceExtras.has('notes') ? 1 : 0)} style={{ ...td, padding: '10px 8px', textAlign: 'center', background: 'var(--crm-surface-alt)' }}>
                          <button
                            type="button"
                            onClick={addAdditionalServiceRow}
                            style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                          >
                            + Додати рядок в кінець списку
                          </button>
                        </td>
                      </tr>
                      {additionalServiceRows.length === 0 && <tr><td colSpan={5 + (serviceExtras.has('notes') ? 1 : 0)} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Натисніть "+ Рядок" щоб додати послуги</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="event-order-summary" style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '16px' }}>
                <strong style={{ color: 'var(--crm-text)', fontSize: '14px' }}>Підсумок замовлення</strong>
                <div className="event-order-form-grid event-order-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div style={labelStyle}>Сума страв<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{dishesSubtotal.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Сума дітей<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{childrenSubtotal.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Сума підрядників<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{contractorsSubtotal.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Сума вкладок<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{customSectionsSubtotal.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Сума послуг<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{additionalServicesSubtotal.toFixed(2)} грн</div></div>
                  <label style={labelStyle}>Обслуговування основного (%)<input type="number" min="0" step="0.1" style={inputStyle} value={form.servicePercent} onChange={(e) => setForm({ ...form, servicePercent: e.target.value })} /></label>
                </div>
                <div className="event-order-form-grid event-order-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div style={labelStyle}>Проміжна сума<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{subtotal.toFixed(2)} грн</div></div>
                  <label style={labelStyle}>Обслуговування дітей (%)<input type="number" min="0" step="0.1" style={inputStyle} value={form.childServicePercent} onChange={(e) => setForm({ ...form, childServicePercent: e.target.value })} /></label>
                  <label style={labelStyle}>Обслуговування підрядників (%)<input type="number" min="0" step="0.1" style={inputStyle} value={form.contractorServicePercent} onChange={(e) => setForm({ ...form, contractorServicePercent: e.target.value })} /></label>
                  <label style={labelStyle}>Знижка (%)<input type="number" min="0" step="0.1" style={inputStyle} value={form.discountPercent} onChange={(e) => setForm({ ...form, discountPercent: e.target.value })} /></label>
                </div>
                <div className="event-order-form-grid event-order-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div style={labelStyle}>Обслуг. основного<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{mainServiceAmount.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Обслуг. дітей<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{childrenServiceAmount.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Обслуг. підрядників<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{contractorsServiceAmount.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Сума обслуговування<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{serviceAmount.toFixed(2)} грн</div></div>
                </div>
                <div className="event-order-form-grid event-order-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div style={labelStyle}>Сума з обслуговуванням<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{(subtotal + serviceAmount).toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Сума знижки<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>- {discountAmount.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Вихід основного меню<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{mainMenuOutput.toFixed(3)}</div></div>
                  <div style={labelStyle}>Вихід дітей<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{childrenOutput.toFixed(3)}</div></div>
                  <div style={labelStyle}>Вихід підрядників<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{contractorsOutput.toFixed(3)}</div></div>
                  <div style={labelStyle}>Загальний вихід<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{totalOutput.toFixed(3)}</div></div>
                </div>
                <div className="event-order-form-grid event-order-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div style={labelStyle}>Їжі на гостя<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{foodPerGuest.toFixed(3)}</div></div>
                  <div style={labelStyle}>Їжі на дитину<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{foodPerChild.toFixed(3)}</div></div>
                  <div style={labelStyle}>Їжі на підрядника<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{foodPerContractor.toFixed(3)}</div></div>
                  <div style={labelStyle}>Ціна з людини<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }} title="Тільки основне меню + обслуговування ÷ гості">{perGuestPrice.toFixed(2)} грн</div></div>
                  <div style={labelStyle}>Разом до сплати<div style={{ ...inputStyle, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', fontWeight: 700, fontSize: '18px' }}>{total.toFixed(2)} грн</div></div>
                </div>
                <div className="event-order-form-grid event-order-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <label style={labelStyle}>Аванс<input type="number" min="0" step="0.01" style={inputStyle} value={form.advanceAmount} onChange={(e) => setForm({ ...form, advanceAmount: e.target.value })} /></label>
                  <div style={labelStyle}>Ефективне обслуговування<div style={{ ...inputStyle, background: 'var(--crm-surface-alt)', fontWeight: 700 }}>{effectiveServicePercent.toFixed(2)}%</div></div>
                  <div style={labelStyle}>Залишок після авансу<div style={{ ...inputStyle, background: balanceDue > 0 ? 'var(--crm-warning-bg)' : 'var(--crm-success-bg)', color: balanceDue > 0 ? 'var(--crm-warning)' : 'var(--crm-success)', border: `1px solid ${balanceDue > 0 ? 'var(--crm-warning)' : 'var(--crm-success)'}`, fontWeight: 700 }}>{balanceDue.toFixed(2)} грн</div></div>
                </div>
                <div className="event-order-form-grid event-order-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '12px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--crm-border)' }}>
                  <label style={labelStyle}>Собівартість обслуговування / роботи<input type="number" min="0" step="0.01" style={inputStyle} value={form.serviceLaborCost} onChange={(e) => setForm({ ...form, serviceLaborCost: e.target.value })} /></label>
                  <label style={labelStyle}>Інші витрати<input type="number" min="0" step="0.01" style={inputStyle} value={form.otherCost} onChange={(e) => setForm({ ...form, otherCost: e.target.value })} /></label>
                  <label style={{ ...labelStyle, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '9px', paddingTop: '20px' }}>
                    <input type="checkbox" checked={form.costDataConfirmed} onChange={(e) => setForm({ ...form, costDataConfirmed: e.target.checked })} style={{ width: 18, height: 18 }} />
                    <span><strong>Витрати перевірено</strong><br /><small>Підтверджую, що переглянуто витрати на роботу, додаткові послуги та інші категорії. Оцінка складських списань і надалі використовує поточну собівартість.</small></span>
                  </label>
                </div>
              </div>

              <label style={labelStyle}>Додаткові відомості про подію<textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={form.additionalEventInfo} onChange={(e) => setForm({ ...form, additionalEventInfo: e.target.value })} /></label>
              <label style={labelStyle}>Примітки<textarea style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>

              {error && <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: '10px', borderRadius: 'var(--crm-radius-sm)' }}>{error}</div>}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={handlePrintModal} style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: '1px solid var(--crm-accent)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 18px', cursor: 'pointer', fontWeight: 500 }}>🖨 Друкувати калькуляцію</button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--crm-text)' }}>Колонки:</span>
                    {([['output', 'Вихід'], ['totalOutput', 'Заг.вихід']] as [keyof typeof printColumns, string][]).map(([key, label]) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={printColumns[key]} onChange={() => togglePrintCol(key)} style={{ width: '13px', height: '13px', cursor: 'pointer' }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={() => closeModal()} style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 20px', cursor: 'pointer' }}>Скасувати</button>
                  <button type="button" onClick={handleSave} disabled={saving} style={{ background: saving ? 'var(--crm-surface-alt)' : 'var(--crm-success-bg)', color: saving ? 'var(--crm-text-muted)' : 'var(--crm-success)', border: `1px solid ${saving ? 'var(--crm-border)' : 'var(--crm-success)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '10px 24px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>{saving ? 'Збереження...' : editingId ? 'Зберегти' : 'Створити'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {routingOrder && productionRouting && (
        <ProductionRoutingEditor
          key={routingOrder.id}
          order={routingOrder}
          routing={productionRouting}
          kitchens={productionKitchens}
          canEdit={canEditProductionRouting}
          onClose={() => {
            ++routingRequestSequence.current;
            setRoutingOrder(null);
            setProductionRouting(null);
          }}
        />
      )}
      {routingOrder && routingLoading && <p role="status">Завантаження маршрутизації…</p>}
      {routingOrder && routingError && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{routingError}</p>}
      {routingOrder && !productionRouting && !routingLoading && <button type="button" onClick={() => {
        ++routingRequestSequence.current;
        setRoutingOrder(null);
      }}>Закрити маршрутизацію</button>}

      {prepModalOpen && prepOrder && (
        <div className="event-order-modal event-order-modal--full event-order-prep-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1001, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}>
          <div className="event-order-prep-modal__card" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', width: '100%', maxWidth: '1320px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '24px' }}>
            <div className="event-order-prep-modal__header" style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', padding: '14px 20px', borderRadius: 'var(--crm-radius-md) var(--crm-radius-md) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px' }}>Бланк підготовки до виконання робіт</h3>
                <div style={{ fontSize: '12px', opacity: 0.95 }}>Замовлення #{prepOrder.id} • {prepOrder.customerName || '—'} • {prepOrder.eventName || '—'}</div>
              </div>
              <button type="button" onClick={() => setPrepModalOpen(false)} aria-label="Закрити бланк підготовки" title="Закрити" style={{ ...modalCloseButtonStyle, color: 'var(--crm-primary-contrast)', fontSize: '22px' }}>×</button>
            </div>
            <div className="event-order-prep-modal__content" style={{ padding: '16px 20px' }}>
              <div className="event-order-prep-modal__filters" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '12px' }}>
                <label style={labelStyle}>Категорія
                  <SearchSelect
                    items={prepCategorySearchItems}
                    value={prepCategoryFilter}
                    onChange={setPrepCategoryFilter}
                    placeholder="🔍 Усі категорії..."
                  />
                </label>
                <label style={labelStyle}>Страва
                  <SearchSelect
                    items={prepDishSearchItems}
                    value={prepDishFilter}
                    onChange={setPrepDishFilter}
                    placeholder="🔍 Усі страви..."
                  />
                </label>
                <label style={labelStyle}>Сортування
                  <select value={prepSortBy} onChange={(e) => setPrepSortBy(e.target.value as typeof prepSortBy)} style={{ ...inputStyle, minWidth: '240px' }}>
                    <option value="day-asc">День А-Я</option>
                    <option value="day-desc">День Я-А</option>
                    <option value="category-asc">Категорія А-Я</option>
                    <option value="category-desc">Категорія Я-А</option>
                    <option value="dish-asc">Страва А-Я</option>
                    <option value="dish-desc">Страва Я-А</option>
                    <option value="portions-asc">Порції ↑</option>
                    <option value="portions-desc">Порції ↓</option>
                  </select>
                </label>
                <button type="button" onClick={handlePreparationExcelExport} style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '9px 14px', cursor: 'pointer', fontWeight: 600 }}>Експорт Excel</button>
                <button type="button" onClick={handlePreparationPdfExport} style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', border: '1px solid var(--crm-primary)', borderRadius: 'var(--crm-radius-sm)', padding: '9px 14px', cursor: 'pointer', fontWeight: 600 }}>Експорт PDF</button>
              </div>

              <div style={{ marginBottom: '10px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>
                Показано {prepFilteredRows.length} позицій. Для ручних корекцій змінюйте колонку "Скориговані порції".
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)', boxShadow: 'var(--crm-shadow-sm)' }}>
                  <thead>
                    <tr>
                      <th style={th}>День / таймінг</th>
                      <th style={th}>Розділ</th>
                      <th style={th}>Категорія</th>
                      <th style={th}>Страва</th>
                      <th style={{ ...th, textAlign: 'right' }}>Порції в меню</th>
                      <th style={{ ...th, textAlign: 'right' }}>Скориговані порції</th>
                      <th style={th}>Н/Ф на 1 порцію</th>
                      <th style={th}>Н/Ф на скориговані порції</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prepFilteredRows.map((row) => (
                      <tr key={row.key} style={{ borderBottom: '1px solid var(--crm-table-row-border)' }}>
                        <td style={td}>{row.day}</td>
                        <td style={td}>{row.section}</td>
                        <td style={td}>{row.category}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{formatDishName({ name: row.dishName, output: dishOptionMap.get(row.dishId)?.output, servingsCount: dishOptionMap.get(row.dishId)?.servingsCount }, printSettings)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{row.menuPortions.toFixed(2)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <input
                            type="number"
                            step="0.01"
                            value={row.adjustedPortions}
                            onChange={(e) => handlePreparationPortionsChange(row.key, e.target.value)}
                            style={{ ...inputStyle, width: '120px', marginLeft: 'auto', textAlign: 'right' }}
                          />
                        </td>
                        <td style={td}>
                          {row.semiItems.length === 0
                            ? '—'
                            : row.semiItems.map((semi) => (
                                <div key={`${row.key}-${semi.name}`} style={{ marginBottom: '4px' }}>
                                  {semi.name}: <strong>{semi.qtyPerPortion.toFixed(4)}</strong>
                                </div>
                              ))}
                        </td>
                        <td style={td}>
                          {row.semiItems.length === 0
                            ? '—'
                            : row.semiItems.map((semi) => (
                                <div key={`${row.key}-total-${semi.name}`} style={{ marginBottom: '4px' }}>
                                  {semi.name}: <strong>{(semi.qtyPerPortion * row.adjustedPortions).toFixed(4)}</strong>
                                </div>
                              ))}
                        </td>
                      </tr>
                    ))}
                    {prepFilteredRows.length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ ...td, textAlign: 'center', padding: '24px', color: 'var(--crm-text-muted)' }}>
                          Немає даних за обраними фільтрами.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventOrdersPage;
